import type {
  AdAccountStatus,
  AdPlatform,
  AnalyticsActiveSession,
  AnalyticsAudience,
  AnalyticsClickPoint,
  AnalyticsEventDeliverable,
  AnalyticsEventRow,
  AnalyticsPathClickCount,
  AnalyticsPathHeatmap,
  AnalyticsProject,
  AnalyticsScrollFunnel,
  AnalyticsSetupDraft,
  AnalyticsTopFacet,
  AnalyticsVisitsByDay,
  AnyCampaignDraft,
  BrandAsset,
  BrandAssetKind,
  BrandBrainAssets,
  BrandBrainContent,
  BrandBrainCustomer,
  BrandBrainHistory,
  BrandBrainIdentity,
  BrandBrainPreferences,
  BrandBrainPricing,
  BrandBrainProducts,
  BrandBrainTeam,
  BrandBrainTestimonials,
  BrandBrainVoice,
  BrandBrainVoiceExamples,
  BrandDesignSystem,
  BrandFunnel,
  BrandGuidelines,
  CampaignDraft,
  CampaignKind,
  CampaignListItem,
  CampaignMetrics,
  CampaignPlan,
  CampaignPlanningLog,
  CampaignProgress,
  CampaignSnapshot,
  CampaignStatus,
  CampaignVerdict,
  ChatContextDescriptor,
  CompetitiveLandscape,
  CreativeAsset,
  CreativeAssetFormat,
  Deliverable,
  FeedCardPayload,
  GoogleSearchAdDeliverable,
  InstagramPostDeliverable,
  IntakeMessage,
  MagChatMessage,
  MagFollowup,
  MetaPaidAdDeliverable,
  NotificationChannel,
  PaidAdHookDeliverable,
  PlanKey,
  PlanLimitExceededPayload,
  RefinementMessage,
  RunStrategistOutcome,
  TakeoverActionExecution,
  TakeoverActionItem,
  TakeoverRead,
} from '@magig/schemas';
import { env } from './env.js';

export type { CampaignVerdict, RunStrategistOutcome } from '@magig/schemas';
export { isAutoApplyEligible, isCampaignPlan } from '@magig/schemas';
// Re-export shared types so route files can import them from a single
// place alongside the fetch helpers — keeps imports simple even though
// these originate in @magig/schemas.
export type {
  AdAccountStatus,
  AdPlatform,
  AnalyticsActiveSession,
  AnalyticsAudience,
  AnalyticsClickPoint,
  AnalyticsEventDeliverable,
  AnalyticsEventRow,
  AnalyticsPathClickCount,
  AnalyticsPathHeatmap,
  AnalyticsProject,
  AnalyticsScrollFunnel,
  AnalyticsSetupDraft,
  AnalyticsTopFacet,
  AnalyticsVisitsByDay,
  AnyCampaignDraft,
  BrandAsset,
  BrandAssetKind,
  BrandGuidelines,
  CampaignDraft,
  CampaignKind,
  CampaignListItem,
  CampaignMetrics,
  CampaignPlan,
  CampaignPlanningLog,
  CampaignProgress,
  CampaignSnapshot,
  CampaignStatus,
  CreativeAsset,
  CreativeAssetFormat,
  Deliverable,
  GoogleSearchAdDeliverable,
  InstagramPostDeliverable,
  IntakeMessage,
  MagChatMessage,
  MagFollowup,
  MetaPaidAdDeliverable,
  PaidAdHookDeliverable,
  PlanKey,
  RefinementMessage,
  TakeoverActionExecution,
  TakeoverActionItem,
  TakeoverRead,
};

/**
 * Authenticated user as returned by GET /api/me. Mirrors what Better Auth
 * stores on the user table — we keep this type local for now; once we add
 * @magig/schemas user types they'll move there.
 */
export interface MeResponse {
  id: string;
  email: string;
  name: string;
  image: string | null;
  emailVerified: boolean;
  /** Workspace role on the user's active org. Drives nav/UI gating —
   *  owner-only surfaces (billing, transfer ownership) hide for the
   *  other roles. Defaults to 'member' if the row is missing for any
   *  reason; UI degrades to least-privilege. */
  workspaceRole: 'owner' | 'admin' | 'member';
  organizationId: string | null;
  /** Workspace display name — shown in the nav, invite emails, team
   *  page header. Null only if /api/me couldn't resolve the org. */
  workspaceName: string | null;
  /** [i18n] Resolved INTERFACE language — what the founder reads. Already
   *  resolved server-side through users.locale -> Accept-Language -> 'en',
   *  so the client never re-derives it. Not the market language: what Mag
   *  advertises in lives on brand_markets.language. */
  locale: string;
  /** Which step of that chain answered. 'user' means the founder chose
   *  explicitly; 'header'/'default' mean we guessed, and the settings
   *  control renders those differently from a deliberate choice. */
  localeSource: 'user' | 'header' | 'default';
  /** [designer-role] True when the user only holds brand_members
   *  rows with role='designer' (no manager/editor/viewer + workspace
   *  role is 'member'). Drives the /home → /designer redirect + the
   *  simpler top-nav. Optional in the type so an api ahead of the
   *  dashboard still decodes; treat absent as false. */
  designerOnly?: boolean;
}

/** [i18n] Save (or clear) the founder's interface language.
 *
 *  null is not "unset the field and hope" — it is the explicit "follow my
 *  browser" choice, which hands resolution back to Accept-Language. The
 *  response re-resolves rather than echoing the input, so a cleared choice
 *  comes back as the locale the founder will actually GET. */
export async function updateMyLocaleApi(
  locale: string | null,
): Promise<{ locale: string; localeSource: 'user' | 'header' | 'default' }> {
  const res = await fetch(`${env.API_URL}/api/me/locale`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ locale }),
  });
  if (!res.ok) {
    let detail = '';
    try {
      detail = JSON.stringify(await res.json());
    } catch {
      // fall through
    }
    throw new Error(`PATCH /api/me/locale ${res.status} ${detail}`);
  }
  return (await res.json()) as {
    locale: string;
    localeSource: 'user' | 'header' | 'default';
  };
}

export interface SignupConversionClaim {
  claimed: boolean;
  method?: string | null;
  invited?: boolean;
}

/** Ask the server whether this founder's account was just created and still
 *  owes an `account_created` conversion. Returns claimed:true at most once
 *  per account — the server clears the marker in the same statement that
 *  reads it.
 *
 *  Deliberately swallows failures and reports "nothing to claim": this runs
 *  on every authenticated page load, and a 500 here must never surface to a
 *  founder or break a render. The cost of a lost claim is one unreported
 *  conversion, which is strictly better than a broken dashboard.
 */
export async function claimSignupConversionApi(): Promise<SignupConversionClaim> {
  try {
    const res = await fetch(`${env.API_URL}/api/me/signup-conversion/claim`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return { claimed: false };
    return (await res.json()) as SignupConversionClaim;
  } catch {
    return { claimed: false };
  }
}

export async function updateWorkspaceNameApi(name: string): Promise<{ id: string; name: string }> {
  const res = await fetch(`${env.API_URL}/api/workspace`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) {
    let detail = '';
    try {
      detail = JSON.stringify(await res.json());
    } catch {
      // fall through
    }
    throw new Error(`PATCH /api/workspace ${res.status} ${detail}`);
  }
  const body = (await res.json()) as { workspace: { id: string; name: string } };
  return body.workspace;
}

/** Hard-delete the caller's workspace + every user that was a member
 *  (when those users aren't members of any other org). Owner-only on
 *  the server. After resolution the client should signOut + redirect
 *  to /login — the server already wiped the user row so any further
 *  api call from this session will 401. */
export async function deleteWorkspaceApi(): Promise<void> {
  const res = await fetch(`${env.API_URL}/api/workspace`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    let detail = '';
    try {
      detail = JSON.stringify(await res.json());
    } catch {
      // fall through
    }
    throw new Error(`DELETE /api/workspace ${res.status} ${detail}`);
  }
}

export interface ProfilePatch {
  name?: string;
  /** Explicit null clears the avatar (revert to initials). */
  image?: string | null;
}

export async function updateProfileApi(
  patch: ProfilePatch,
): Promise<{ id: string; name: string; image: string | null }> {
  const res = await fetch(`${env.API_URL}/api/profile`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    let detail = '';
    try {
      detail = JSON.stringify(await res.json());
    } catch {
      // fall through
    }
    throw new Error(`PATCH /api/profile ${res.status} ${detail}`);
  }
  const body = (await res.json()) as {
    user: { id: string; name: string; image: string | null };
  };
  return body.user;
}

export async function uploadAvatarApi(
  file: File,
): Promise<{ user: { id: string; image: string | null }; url: string }> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${env.API_URL}/api/profile/avatar`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!res.ok) {
    let detail = '';
    try {
      detail = JSON.stringify(await res.json());
    } catch {
      // fall through
    }
    throw new Error(`POST /api/profile/avatar ${res.status} ${detail}`);
  }
  return (await res.json()) as { user: { id: string; image: string | null }; url: string };
}

export async function fetchMe(): Promise<MeResponse | null> {
  const res = await fetch(`${env.API_URL}/api/me`, { credentials: 'include' });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`GET /api/me ${res.status}`);
  return (await res.json()) as MeResponse;
}

// ───── Feedback ───────────────────────────────────────────────────────────
// spec d645e60c. The founder writes a topic + a message; the client attaches
// where they were, because the server cannot see their route and a bug
// report without it costs a support round-trip.

export type FeedbackTopic = 'bug' | 'feature_request' | 'question' | 'other';

export interface FeedbackContextPayload {
  href?: string;
  brandSlug?: string;
  locale?: string;
  viewport?: string;
  userAgent?: string;
}

export class FeedbackRateLimitedError extends Error {
  constructor() {
    super('rate_limited');
    this.name = 'FeedbackRateLimitedError';
  }
}

/** Returns whether the support mail actually left. The submission is stored
 *  either way, so `mailed: false` is not a failure the founder must retry. */
export async function submitFeedback(body: {
  topic: FeedbackTopic;
  message: string;
  context: FeedbackContextPayload;
}): Promise<{ ok: true; mailed: boolean }> {
  const res = await fetch(`${env.API_URL}/api/feedback`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  // Distinct error type: the cap is the one failure with a useful thing to
  // say to the founder ("you've sent a few already"), rather than a generic
  // apology.
  if (res.status === 429) throw new FeedbackRateLimitedError();
  if (!res.ok) throw new Error(`POST /api/feedback ${res.status}`);
  return (await res.json()) as { ok: true; mailed: boolean };
}

// ───── Brand Brain ────────────────────────────────────────────────────────
// Spec §3.1. Fetched once on /home + /brief load and re-saved one section at
// a time. The API does the merge so callers only send what they changed.

export interface BrandBrainResponse extends BrandBrainContent {
  id: string;
  organizationId: string;
  version: number;
  /** Mag's design system — colors, fonts, logo (M1 s155). Null on
   *  legacy brand_brain rows that pre-date the visual scrape. */
  guidelines: BrandGuidelines | null;
  /** [12.2L-172] Competitive landscape — populated by the
   *  competition-analyst agent. Null on brains that pre-date the
   *  column or haven't had a pass yet. */
  competitiveLandscape: CompetitiveLandscape | null;
  /** [multi-market] Every market's landscape, keyed by ISO country code.
   *  `competitiveLandscape` above is the PRIMARY market's mirror, kept for
   *  single-country readers.
   *
   *  The api has serialized this since multi-market shipped; the client type
   *  simply never declared it, so the workbench could only ever render one
   *  market of however many a brand sells in — and said nothing about the
   *  rest. Null on brains predating multi-market. */
  competitiveLandscapeByMarket: Record<string, CompetitiveLandscape> | null;
  /** [spec 494bc165] The competitors who are the same in EVERY market — global
   *  software a buyer in Uruguay and a buyer in Canada both shortlist.
   *
   *  Deliberately not a key in the by-market map above: a global competitor is a
   *  fact about the brand, not about a market, and rendering it as a market pill
   *  would have mag describing a worldwide SaaS as competition "in Chile". Null
   *  for every market-bound brand, which is most of them. */
  competitiveLandscapeGlobal: CompetitiveLandscape | null;
  createdAt: string;
  updatedAt: string;
}

export interface BrandBrainEnvelope {
  brandBrain: BrandBrainResponse | null;
  organizationId: string;
}

export type BrandBrainPatch = {
  identity?: BrandBrainIdentity;
  voice?: BrandBrainVoice;
  customer?: BrandBrainCustomer;
  assets?: BrandBrainAssets;
  history?: BrandBrainHistory;
  preferences?: BrandBrainPreferences;
  /** Phase 4.31 (s221) — structured products & services library.
   *  Replace-in-full semantics: the api merges the section verbatim. */
  products?: BrandBrainProducts;
  /** [spec cf58e5eb] Pricing model + plan tiers. Same replace-in-full
   *  semantics as products. The api has accepted this patch key since
   *  s284 (brand-brain.ts); only the dashboard type was missing it, which
   *  is why nothing here could edit plans. */
  pricing?: BrandBrainPricing;
  /** Phase 4.41 (s231) — structured team / people roster. Same
   *  replace-in-full semantics as products. */
  team?: BrandBrainTeam;
  /** §3.1b Phase 7a (s233) — structured testimonials library. */
  testimonials?: BrandBrainTestimonials;
  /** §3.1b Phase 7b (s233a) — structured voice examples library. */
  voiceExamples?: BrandBrainVoiceExamples;
  /** §3.1c Phase 1 (s236) — Brand Design System. */
  designSystem?: BrandDesignSystem;
  /** [Layer 1] Founder triage on the competition landscape — discard
   *  misread competitors, accept / skip recommendations. Replace-in-
   *  full like products/team. */
  competitiveLandscape?: CompetitiveLandscape;
};

/** D1 Phase 3b — append ?brand=<id> when set so the API scopes the
 *  request to the active brand. Null/undefined → falls through to the
 *  org's default brand (matches pre-Phase-3 behavior for single-brand
 *  orgs). 'all' is a sentinel the multi-brand rollup endpoints accept. */
function brandQuery(brandId: string | null | undefined, prefix: '?' | '&' = '?'): string {
  if (!brandId) return '';
  return `${prefix}brand=${encodeURIComponent(brandId)}`;
}

/** Read the browser's IANA timezone for endpoints that bucket by day
 *  ("today" on the analytics chart, eventsToday on the home pulse).
 *  Without this the server defaults to UTC, which rolls the day forward
 *  at 9pm for founders in UYT. Empty string on environments where Intl
 *  isn't available — the server falls back to UTC. */
function browserTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    return '';
  }
}

function tzQuery(prefix: '?' | '&'): string {
  const tz = browserTz();
  if (!tz) return '';
  return `${prefix}tz=${encodeURIComponent(tz)}`;
}

export async function fetchBrandBrain(brandId?: string | null): Promise<BrandBrainEnvelope | null> {
  const res = await fetch(`${env.API_URL}/api/brand-brain${brandQuery(brandId)}`, {
    credentials: 'include',
  });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`GET /api/brand-brain ${res.status}`);
  return (await res.json()) as BrandBrainEnvelope;
}

/** Mag's brand-guidelines payload — the design system she uses when
 *  she generates creative. Sibling to BrandBrainPatch; lives at its
 *  own endpoints so the visual scrape can flow into it without
 *  touching identity / voice / customer. (M1 s155) */
export type BrandGuidelinesPatch = {
  colors?: Partial<BrandGuidelines['colors']>;
  typography?: Partial<BrandGuidelines['typography']>;
  logoUrl?: string | null;
  logoDarkUrl?: string | null;
  visualMotifs?: string[];
  voiceSamples?: string[];
  illustrationStyle?: string | null;
  /** Founder edits to the vision-pass output. Pass the whole next
   *  shape; the api replaces wholesale (no field-level merge — the
   *  vision system is treated as one coherent block). */
  visualSystem?: BrandGuidelines['visualSystem'];
  /** Phase 2.9 — founder edits to brandStyle (buttonShape, cardRadius,
   *  bulletStyle). Pass null to clear; absent leaves the merged value
   *  untouched. */
  brandStyle?: BrandGuidelines['brandStyle'];
  /** Phase 2.9 — founder edits to componentSpec (rich per-primitive
   *  design knobs). Same merge precedence as brandStyle. */
  componentSpec?: BrandGuidelines['componentSpec'];
};

export interface GuidelinesEnvelope {
  guidelines: BrandGuidelines;
  brandId: string;
}

export interface ScrapeGuidelinesEnvelope extends GuidelinesEnvelope {
  scrapedUrl: string;
  durationMs: number;
  /** How many screenshots the chromium pass captured. 0 means the
   *  Playwright tier didn't run or crashed — check the api logs for
   *  the [visual-scrape] line. */
  screenshotCount: number;
  /** True when the multimodal vision pass produced a visualSystem.
   *  False when it was skipped (no screenshots) or threw. */
  visionRan: boolean;
}

export async function saveBrandGuidelines(
  patch: BrandGuidelinesPatch,
  brandId?: string | null,
): Promise<GuidelinesEnvelope> {
  const res = await fetch(`${env.API_URL}/api/brand-brain/guidelines${brandQuery(brandId)}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`PUT /api/brand-brain/guidelines ${res.status} ${detail}`);
  }
  return (await res.json()) as GuidelinesEnvelope;
}

// ───── Scraped pages (M1 s159.4 + s159.5) ─────────────────────────────
// Verbatim landing-site text that mag reads when she drafts creative.
// One entry per page; crawl is opt-in (founder controls how deep mag
// reads). Schema matches @magig/schemas/ScrapedPage.
export interface ScrapedPage {
  url: string;
  title: string;
  description: string;
  text: string;
  scrapedAt: string;
  /** R2-hosted screenshot of the page, captured asynchronously by
   *  the scraped-page-thumbnail queue. Null until the job completes;
   *  the UI renders a paper placeholder in that window. */
  thumbnailUrl?: string | null;
}

export interface ScrapedPagesEnvelope {
  scrapedPages: ScrapedPage[];
  brandId: string;
}

export interface CrawlScrapedPagesResult extends ScrapedPagesEnvelope {
  /** Server now runs the crawl async (s162.19). True for every call
   *  the new server returns; older "synchronous" responses still
   *  carry `added` for compatibility. */
  started?: boolean;
  added?: number;
  skipped?: Array<{ url: string; reason: string }>;
}

export async function fetchScrapedPages(brandId?: string | null): Promise<ScrapedPagesEnvelope> {
  const res = await fetch(`${env.API_URL}/api/brand-brain/scraped-pages${brandQuery(brandId)}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`GET /api/brand-brain/scraped-pages ${res.status}`);
  return (await res.json()) as ScrapedPagesEnvelope;
}

export async function crawlScrapedPages(
  brandId?: string | null,
  options?: { maxPages?: number; url?: string },
): Promise<CrawlScrapedPagesResult> {
  const res = await fetch(
    `${env.API_URL}/api/brand-brain/scraped-pages/crawl${brandQuery(brandId)}`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options ?? {}),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/brand-brain/scraped-pages/crawl ${res.status} ${detail}`);
  }
  return (await res.json()) as CrawlScrapedPagesResult;
}

/** Re-run the whole extraction against the brand's site.
 *
 *  'refresh' re-reads the same business: mag merges, your edits survive, and
 *  anything the site no longer mentions is marked stale rather than dropped.
 *  'reingest' tells her it is a DIFFERENT business — she snapshots what she
 *  knows, forgets it, and starts from the new site.
 *
 *  Returns immediately with a runId; the work happens in the background and
 *  the extraction-progress rows tick as it goes. */
export async function reingestBrandBrain(
  brandId: string,
  mode: 'refresh' | 'reingest',
): Promise<{ runId: string; brandId: string; mode: string }> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/brain/reingest`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mode }),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/brands/${brandId}/brain/reingest ${res.status} ${detail}`);
  }
  return (await res.json()) as { runId: string; brandId: string; mode: string };
}

export async function removeScrapedPage(brandId: string, url: string): Promise<void> {
  const params = new URLSearchParams({ brand: brandId, url });
  const res = await fetch(`${env.API_URL}/api/brand-brain/scraped-pages?${params.toString()}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`DELETE /api/brand-brain/scraped-pages ${res.status} ${detail}`);
  }
}

/** Re-enqueue the chromium screenshot for one scraped page. Used by
 *  the "retry capture" affordance when the original capture didn't
 *  land. Idempotent on the server side — same jobId dedupes. */
export async function recaptureScrapedPageThumbnail(brandId: string, url: string): Promise<void> {
  const params = new URLSearchParams({ brand: brandId, url });
  const res = await fetch(
    `${env.API_URL}/api/brand-brain/scraped-pages/recapture-thumbnail?${params.toString()}`,
    {
      method: 'POST',
      credentials: 'include',
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      `POST /api/brand-brain/scraped-pages/recapture-thumbnail ${res.status} ${detail}`,
    );
  }
}

/** Re-read the brand's marketing site and merge the inferred values
 *  into the saved guidelines. URL falls back to whatever the previous
 *  scrape recorded — pass `{ url }` to override. */
export async function scrapeBrandGuidelines(
  brandId?: string | null,
  url?: string,
): Promise<ScrapeGuidelinesEnvelope> {
  const res = await fetch(
    `${env.API_URL}/api/brand-brain/guidelines/scrape${brandQuery(brandId)}`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(url ? { url } : {}),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/brand-brain/guidelines/scrape ${res.status} ${detail}`);
  }
  return (await res.json()) as ScrapeGuidelinesEnvelope;
}

// ───── Brand assets (M1 s156) ─────────────────────────────────────────────
// Founder-uploaded raw material — photography, illustrations, logos, etc.
// The library mag picks from when she designs creative.

export interface BrandAssetsEnvelope {
  assets: BrandAsset[];
  brandId: string;
}

export interface UploadBrandAssetsResult {
  assets: BrandAsset[];
  failed: Array<{ filename: string; reason: string }>;
  brandId: string;
}

export async function fetchBrandAssets(
  brandId: string,
  filters?: { kind?: BrandAssetKind; tags?: string[] },
): Promise<BrandAssetsEnvelope> {
  const params = new URLSearchParams({ brand: brandId });
  if (filters?.kind) params.set('kind', filters.kind);
  if (filters?.tags && filters.tags.length > 0) params.set('tag', filters.tags.join(','));
  const res = await fetch(`${env.API_URL}/api/brand-brain/assets?${params.toString()}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`GET /api/brand-brain/assets ${res.status}`);
  return (await res.json()) as BrandAssetsEnvelope;
}

/** Bulk upload via multipart/form-data. `files` is the founder's drag-
 *  drop selection; `kind` applies to every file in the request (one
 *  upload session = one kind for simplicity); `tags` applies to every
 *  file too. Returns successes + failures so the UI can show per-file
 *  error messages without aborting the whole batch. */
export async function uploadBrandAssets(
  brandId: string,
  files: File[],
  /** Optional manual override. When omitted, the API auto-classifies
   *  each file's kind + tags with a vision model (s216-54h.90). */
  kind?: BrandAssetKind,
  tags: string[] = [],
): Promise<UploadBrandAssetsResult> {
  const fd = new FormData();
  for (const file of files) fd.append('file', file, file.name);
  if (kind) fd.append('kind', kind);
  if (tags.length > 0) fd.append('tags', tags.join(','));
  const res = await fetch(
    `${env.API_URL}/api/brand-brain/assets?brand=${encodeURIComponent(brandId)}`,
    {
      method: 'POST',
      credentials: 'include',
      body: fd,
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/brand-brain/assets ${res.status} ${detail}`);
  }
  return (await res.json()) as UploadBrandAssetsResult;
}

export async function deleteBrandAsset(assetId: string): Promise<void> {
  const res = await fetch(`${env.API_URL}/api/brand-brain/assets/${encodeURIComponent(assetId)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`DELETE /api/brand-brain/assets/${assetId} ${res.status} ${detail}`);
  }
}

// ───── Marketing assets (M1 — creative pool) ──────────────────────────────
// Distinct from brand_assets (above). marketing_assets are campaign creative
// inputs — product shots, lifestyle, ugc — that the strategist will draw
// from when designing Meta proposals. The chat ingest classifier routes
// new media here when it reads as "ad creative" rather than identity.

export interface MarketingAsset {
  id: string;
  brandId: string;
  /** [12.2L-163] M13.1 — raw = founder source material; rendered =
   *  mag-designed PNG. The dashboard splits the creative-pool route
   *  into two sections on this discriminator. */
  kind: 'raw' | 'rendered';
  /** [12.2L-163] M13.1 — kind='rendered' rows reference the
   *  creative_assets row that produced them. Null on kind='raw'. */
  sourceCreativeAssetId: string | null;
  /** [12.2L-165] M14.1 — when a rendered row represents an organic
   *  IG post being promoted (promote_post Meta channel kind), the
   *  IG media id. Null on raw uploads + fresh bridge renders. */
  sourceIgMediaId: string | null;
  assetUrl: string;
  thumbnailUrl: string | null;
  mediaType: 'image' | 'video';
  format: 'square' | 'portrait' | 'landscape' | 'story' | 'unknown';
  durationMs: number | null;
  filename: string;
  mimeType: string;
  bytes: number;
  occasion: string | null;
  productId: string | null;
  tags: string[];
  visionClassification: {
    subject?: string;
    style?: string;
    mood?: string | null;
    dominantColors?: string[];
    quality?: 'usable' | 'reject';
    rejectReason?: string | null;
  } | null;
  uploadSource: 'dashboard' | 'slack' | 'telegram' | 'mag';
  uploadedByUserId: string | null;
  uploadedAt: string;
  archivedAt: string | null;
  expiresAt: string | null;
}

export interface MarketingAssetsEnvelope {
  assets: MarketingAsset[];
  brandId: string;
}

export interface UploadMarketingAssetsResult {
  assets: MarketingAsset[];
  failed: Array<{ filename: string; reason: string }>;
  brandId: string;
}

export async function fetchMarketingAssets(
  brandId: string,
  filters?: { tags?: string[]; includeArchived?: boolean; includeExpired?: boolean },
): Promise<MarketingAssetsEnvelope> {
  const params = new URLSearchParams({ brand: brandId });
  if (filters?.tags && filters.tags.length > 0) params.set('tag', filters.tags.join(','));
  if (filters?.includeArchived) params.set('includeArchived', 'true');
  if (filters?.includeExpired) params.set('includeExpired', 'true');
  const res = await fetch(`${env.API_URL}/api/marketing-assets?${params.toString()}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`GET /api/marketing-assets ${res.status}`);
  return (await res.json()) as MarketingAssetsEnvelope;
}

export async function uploadMarketingAssets(
  brandId: string,
  files: File[],
  meta: { occasion?: string | null; tags?: string[]; productId?: string | null } = {},
): Promise<UploadMarketingAssetsResult> {
  const fd = new FormData();
  for (const file of files) fd.append('file', file, file.name);
  if (meta.occasion) fd.append('occasion', meta.occasion);
  if (meta.productId) fd.append('productId', meta.productId);
  if (meta.tags && meta.tags.length > 0) fd.append('tags', meta.tags.join(','));
  const res = await fetch(
    `${env.API_URL}/api/marketing-assets?brand=${encodeURIComponent(brandId)}`,
    { method: 'POST', credentials: 'include', body: fd },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/marketing-assets ${res.status} ${detail}`);
  }
  return (await res.json()) as UploadMarketingAssetsResult;
}

export async function patchMarketingAsset(
  assetId: string,
  brandId: string,
  patch: {
    occasion?: string | null;
    productId?: string | null;
    tags?: string[];
    expiresAt?: string | null;
  },
): Promise<MarketingAsset> {
  const res = await fetch(
    `${env.API_URL}/api/marketing-assets/${encodeURIComponent(assetId)}?brand=${encodeURIComponent(brandId)}`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`PATCH /api/marketing-assets/${assetId} ${res.status} ${detail}`);
  }
  const json = (await res.json()) as { asset: MarketingAsset };
  return json.asset;
}

export async function archiveMarketingAsset(
  assetId: string,
  brandId: string,
): Promise<MarketingAsset> {
  const res = await fetch(
    `${env.API_URL}/api/marketing-assets/${encodeURIComponent(assetId)}/archive?brand=${encodeURIComponent(brandId)}`,
    { method: 'POST', credentials: 'include' },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST archive ${res.status} ${detail}`);
  }
  return ((await res.json()) as { asset: MarketingAsset }).asset;
}

export async function unarchiveMarketingAsset(
  assetId: string,
  brandId: string,
): Promise<MarketingAsset> {
  const res = await fetch(
    `${env.API_URL}/api/marketing-assets/${encodeURIComponent(assetId)}/unarchive?brand=${encodeURIComponent(brandId)}`,
    { method: 'POST', credentials: 'include' },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST unarchive ${res.status} ${detail}`);
  }
  return ((await res.json()) as { asset: MarketingAsset }).asset;
}

export async function deleteMarketingAsset(assetId: string, brandId: string): Promise<void> {
  const res = await fetch(
    `${env.API_URL}/api/marketing-assets/${encodeURIComponent(assetId)}?brand=${encodeURIComponent(brandId)}`,
    { method: 'DELETE', credentials: 'include' },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`DELETE /api/marketing-assets/${assetId} ${res.status} ${detail}`);
  }
}

/** [12.2L-165] M13.2 — founder re-roll. Server takes ~15-25s to
 *  re-render against the original brief; the dashboard shows the
 *  InlineLoader during the call. Returns the freshly-inserted
 *  rendered row (the original stays). */
export async function regenerateMarketingAsset(
  assetId: string,
  brandId: string,
): Promise<MarketingAsset> {
  const res = await fetch(
    `${env.API_URL}/api/marketing-assets/${encodeURIComponent(assetId)}/regenerate?brand=${encodeURIComponent(brandId)}`,
    { method: 'POST', credentials: 'include' },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST regenerate ${res.status} ${detail}`);
  }
  return ((await res.json()) as { asset: MarketingAsset }).asset;
}

// ───── Creative pipeline (M1 s159) ────────────────────────────────────────
// Founder posts a brief, mag picks a template, writes the fill, and
// enqueues a render. The dashboard polls /api/creative-assets to
// watch the gallery flip from pending → rendering → ready.

export interface RunCreativeInput {
  brief: string;
  formats?: CreativeAssetFormat[];
  campaignId?: string;
  /** s233.27 — when set, bypasses the template-router agent and uses
   *  this templateKey directly. Surfaces in the new-creative modal's
   *  ↘ template selector ("auto" omits this field; picking a specific
   *  template populates it). Server validates it against the
   *  eligibility filter and returns 422 if the template doesn't fit
   *  the requested format. */
  templateOverride?: string;
  /** s233.42 — when switching templates from the drawer, the source
   *  asset's existing fal-generated background gets carried over to
   *  avoid burning a fresh image-gen call. Pipeline pastes this URL
   *  into the new fill's backgroundImageUrl when the new fill writes
   *  a backgroundBrief. */
  carryoverBackground?: { url: string; brief?: string };
}

export type RunCreativeResult =
  | {
      kind: 'fill';
      creativeAssetIds: string[];
      templateKey: string;
      fill: unknown;
      costUsd: number;
      queued: boolean;
    }
  | {
      kind: 'clarification';
      /** Mag asked for missing context instead of fabricating. UI
       *  surfaces this as a follow-up composer; founder answers and
       *  the brief gets re-sent with the answer appended. */
      question: string;
    };

export interface CreativeAssetsEnvelope {
  creativeAssets: CreativeAsset[];
  brandId: string;
}

export async function runCreativePipeline(
  brandId: string,
  input: RunCreativeInput,
): Promise<RunCreativeResult> {
  const res = await fetch(`${env.API_URL}/api/creative?brand=${encodeURIComponent(brandId)}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/creative ${res.status} ${detail}`);
  }
  return (await res.json()) as RunCreativeResult;
}

export async function fetchCreativeAssets(
  brandId: string,
  /** Optional campaign filter — scopes the gallery to one campaign.
   *  Used by the campaign detail page's creative section. */
  campaignId?: string,
): Promise<CreativeAssetsEnvelope> {
  const params = new URLSearchParams({ brand: brandId });
  if (campaignId) params.set('campaign', campaignId);
  const res = await fetch(`${env.API_URL}/api/creative-assets?${params.toString()}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`GET /api/creative-assets ${res.status}`);
  return (await res.json()) as CreativeAssetsEnvelope;
}

export async function regenerateCreativeAsset(assetId: string): Promise<void> {
  const res = await fetch(
    `${env.API_URL}/api/creative-assets/${encodeURIComponent(assetId)}/render`,
    {
      method: 'POST',
      credentials: 'include',
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/creative-assets/${assetId}/render ${res.status} ${detail}`);
  }
}

export type IterateCreativeResult =
  | {
      kind: 'fill';
      /** Row ids touched by the iteration. Length-1 for single-image
       *  drafts (the iterated row); the whole carousel group for
       *  carousels (every slide gets regenerated together). */
      creativeAssetIds: string[];
      templateKey: string;
      fill: unknown;
      costUsd: number;
      queued: boolean;
    }
  | { kind: 'clarification'; question: string };

/** Re-draft a creative with steering. REPLACES the existing row's
 *  fill in place — same id, same R2 key, new PNG. The drawer keeps
 *  the same tile and just polls for the new render. */
export async function iterateCreativeAsset(
  assetId: string,
  steering: string,
): Promise<IterateCreativeResult> {
  const res = await fetch(
    `${env.API_URL}/api/creative-assets/${encodeURIComponent(assetId)}/iterate`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ steering }),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/creative-assets/${assetId}/iterate ${res.status} ${detail}`);
  }
  return (await res.json()) as IterateCreativeResult;
}

export async function deleteCreativeAsset(assetId: string): Promise<void> {
  const res = await fetch(`${env.API_URL}/api/creative-assets/${encodeURIComponent(assetId)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`DELETE /api/creative-assets/${assetId} ${res.status} ${detail}`);
  }
}

/** Phase 4.31 (s221) — trigger a fresh products extraction against
 *  the brain's scraped pages. Returns the updated envelope so the
 *  caller can refresh in one round trip. */
export async function reExtractBrandProducts(
  brandId?: string | null,
): Promise<{ ok: true; productsCount: number; brandBrain: BrandBrainResponse | null }> {
  const res = await fetch(`${env.API_URL}/api/brand-brain/products/extract${brandQuery(brandId)}`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/brand-brain/products/extract ${res.status} ${detail}`);
  }
  return (await res.json()) as {
    ok: true;
    productsCount: number;
    brandBrain: BrandBrainResponse | null;
  };
}

/** [spec cf58e5eb] Trigger a fresh PRICING extraction. Same contract as the
 *  products extract, against a different endpoint — the products button does
 *  not touch plans, which is why re-extracting products looked like it did
 *  nothing to a founder waiting for their tiers to appear. */
export async function reExtractBrandPricing(
  brandId?: string | null,
): Promise<{ ok: true; plansCount: number; brandBrain: BrandBrainResponse | null }> {
  const res = await fetch(`${env.API_URL}/api/brand-brain/pricing/extract${brandQuery(brandId)}`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/brand-brain/pricing/extract ${res.status} ${detail}`);
  }
  return (await res.json()) as {
    ok: true;
    plansCount: number;
    brandBrain: BrandBrainResponse | null;
  };
}

/** Phase 4.41 (s231) — trigger a fresh team / people extraction. Same
 *  contract as the products extract; re-fetches the team page HTML
 *  and rebuilds the roster (preserving founder edits). */
export async function reExtractBrandTeam(
  brandId?: string | null,
): Promise<{ ok: true; teamCount: number; brandBrain: BrandBrainResponse | null }> {
  const res = await fetch(`${env.API_URL}/api/brand-brain/team/extract${brandQuery(brandId)}`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/brand-brain/team/extract ${res.status} ${detail}`);
  }
  return (await res.json()) as {
    ok: true;
    teamCount: number;
    brandBrain: BrandBrainResponse | null;
  };
}

/** §3.1c Phase 4 (s239) — trigger a fresh DS extraction. Reads the
 *  brand's marketing URL via Playwright + DOM computed styles +
 *  merges into brain.designSystem. */
export async function reExtractBrandDesignSystem(brandId?: string | null): Promise<{
  ok: true;
  durationMs: number;
  motifMatchedCount: number;
  motifNewlyStaleCount: number;
  brandBrain: BrandBrainResponse | null;
}> {
  const res = await fetch(
    `${env.API_URL}/api/brand-brain/design-system/extract${brandQuery(brandId)}`,
    { method: 'POST', credentials: 'include' },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/brand-brain/design-system/extract ${res.status} ${detail}`);
  }
  return (await res.json()) as {
    ok: true;
    durationMs: number;
    motifMatchedCount: number;
    motifNewlyStaleCount: number;
    brandBrain: BrandBrainResponse | null;
  };
}

/** [12.2L-172] Trigger the competition analyst against the brand.
 *  Synchronous — the agent takes ~30-60s, the caller is on a dedicated
 *  waiting surface so the wait is OK. Returns the outcome envelope
 *  + the refreshed brain (with competitiveLandscape filled in on
 *  success). Used by the onboarding step-4 surface. */
export type CompetitionAnalyzeOutcome =
  | { outcome: 'card_created'; feedCardId: string; brainVersion: number; costUsd: number }
  | { outcome: 'no_brand' }
  | { outcome: 'no_brain' }
  | { outcome: 'no_identity' }
  | { outcome: 'cooldown_active'; daysUntilEligible: number }
  | { outcome: 'still_fresh'; daysUntilStale: number }
  | { outcome: 'agent_crashed'; reason: string };

export interface CompetitionAnalyzeArgs {
  /** Founder-named competitor URLs (one per line in the UI). */
  namedCompetitorUrls?: string[];
  /** Free-text hint about how the founder sees the market. */
  founderHints?: string;
}

/** Fire the competition analyst. The endpoint runs the agent in the
 *  BACKGROUND (a real run is ~2min, longer than the edge's request
 *  timeout) and returns immediately with `started: true`; it does NOT
 *  wait for or return the result. The caller stays on the waiting
 *  surface and polls the brand brain — `competitiveLandscape` lands when
 *  the run finishes. A 4xx (bad body / brand not found) still rejects
 *  synchronously. */
export async function requestCompetitionAnalysis(
  brandId?: string | null,
  args: CompetitionAnalyzeArgs = {},
): Promise<{ ok: true; started: boolean }> {
  const res = await fetch(
    `${env.API_URL}/api/brand-brain/competition/analyze${brandQuery(brandId)}`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(args),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/brand-brain/competition/analyze ${res.status} ${detail}`);
  }
  return (await res.json()) as { ok: true; started: boolean };
}

/** [Layer 2] Coaching draft outcomes — one per code path in
 *  apps/api/src/competition-coaching-draft.ts. The dashboard branches
 *  the drawer UI on this. */
export type CoachingDraftOutcome =
  | { outcome: 'draft_ready'; recId: string; retried: boolean; costUsd: number }
  | { outcome: 'no_brand' }
  | { outcome: 'no_brain' }
  | { outcome: 'no_landscape' }
  | { outcome: 'rec_not_found'; recId: string }
  | { outcome: 'rec_not_accepted'; recId: string }
  | { outcome: 'already_fresh'; recId: string }
  | { outcome: 'rec_changed_during_run'; recId: string }
  | { outcome: 'agent_crashed'; recId: string; reason: string };

export interface RequestCoachingDraftArgs {
  regenerate?: boolean;
  refineNote?: string;
}

/** Fires the coaching agent for the targeted rec. Atomic on the
 *  server — parallel calls don't race. */
export async function requestCoachingDraft(
  brandId: string | null | undefined,
  recId: string,
  args: RequestCoachingDraftArgs = {},
): Promise<{
  ok: true;
  outcome: CoachingDraftOutcome;
  brandBrain: BrandBrainResponse | null;
}> {
  const res = await fetch(
    `${env.API_URL}/api/brand-brain/competition/recommendations/${encodeURIComponent(recId)}/draft${brandQuery(brandId)}`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(args),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /draft ${res.status} ${detail}`);
  }
  return (await res.json()) as {
    ok: true;
    outcome: CoachingDraftOutcome;
    brandBrain: BrandBrainResponse | null;
  };
}

/** Archive a coaching draft (founder dismisses it). Persists so the
 *  next quarterly pass doesn't resurface the same rec. */
export async function archiveCoachingDraft(
  brandId: string | null | undefined,
  recId: string,
): Promise<{
  ok: true;
  outcome: { outcome: 'archived' | 'no_landscape' | 'rec_not_found' | 'no_draft' };
  brandBrain: BrandBrainResponse | null;
}> {
  const res = await fetch(
    `${env.API_URL}/api/brand-brain/competition/recommendations/${encodeURIComponent(recId)}/draft/archive${brandQuery(brandId)}`,
    { method: 'POST', credentials: 'include' },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /draft/archive ${res.status} ${detail}`);
  }
  return (await res.json()) as {
    ok: true;
    outcome: { outcome: 'archived' | 'no_landscape' | 'rec_not_found' | 'no_draft' };
    brandBrain: BrandBrainResponse | null;
  };
}

/** §3.1b Phase 7b (s233a) — trigger a fresh voice-examples extraction. */
export async function reExtractBrandVoiceExamples(
  brandId?: string | null,
): Promise<{ ok: true; voiceExamplesCount: number; brandBrain: BrandBrainResponse | null }> {
  const res = await fetch(
    `${env.API_URL}/api/brand-brain/voice-examples/extract${brandQuery(brandId)}`,
    {
      method: 'POST',
      credentials: 'include',
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/brand-brain/voice-examples/extract ${res.status} ${detail}`);
  }
  return (await res.json()) as {
    ok: true;
    voiceExamplesCount: number;
    brandBrain: BrandBrainResponse | null;
  };
}

/** §3.1b Phase 7a (s233) — trigger a fresh testimonials extraction
 *  against the brain's scraped pages. Same contract as products + team. */
export async function reExtractBrandTestimonials(
  brandId?: string | null,
): Promise<{ ok: true; testimonialsCount: number; brandBrain: BrandBrainResponse | null }> {
  const res = await fetch(
    `${env.API_URL}/api/brand-brain/testimonials/extract${brandQuery(brandId)}`,
    {
      method: 'POST',
      credentials: 'include',
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/brand-brain/testimonials/extract ${res.status} ${detail}`);
  }
  return (await res.json()) as {
    ok: true;
    testimonialsCount: number;
    brandBrain: BrandBrainResponse | null;
  };
}

export async function saveBrandBrain(
  patch: BrandBrainPatch,
  brandId?: string | null,
): Promise<BrandBrainEnvelope> {
  // Bound the request — the brain PUT is a simple DB upsert; anything
  // over 20s is the API/network hanging, not real work. Without the
  // timeout the founder's "saving…" spinner stuck forever.
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 20_000);
  let res: Response;
  try {
    res = await fetch(`${env.API_URL}/api/brand-brain${brandQuery(brandId)}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
      signal: controller.signal,
    });
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      throw new Error('save timed out — the api took too long. try again.');
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`PUT /api/brand-brain ${res.status} ${detail}`);
  }
  return (await res.json()) as BrandBrainEnvelope;
}

// ───── Feed ───────────────────────────────────────────────────────────────
// Spec §11. The endpoint lazily generates Mag's first cards (welcome +
// concept_proposal) on first load after the brief is filled in, so
// fetchFeed may take 5–20s the very first time.

/** Derived from @magig/schemas, NOT hand-written.
 *
 *  It was a 45-literal union maintained by remembering, and it had already
 *  drifted: the db enum carried 46 kinds and this one 45, missing
 *  `connection_reauth_required`. Nothing complained, because the eyebrow
 *  maps keyed on this type are `Record<FeedCard['kind'], string>` —
 *  exhaustive by construction, and exhaustive over the stale set. The API
 *  compiled because it never reads this; the dashboard compiled because
 *  this did not know a kind was missing from it. Three such cards were live
 *  in production, rendering with a blank eyebrow.
 *
 *  Deriving installs the seam those two facts were missing: a kind that
 *  exists server-side and not in a dashboard map is now a compile error
 *  naming the kind, at the site of the omission. Spec a58652a6. */
export type FeedCardKind = import('@magig/schemas').FeedCardKind;
export type CampaignAssetCoverage = import('@magig/schemas').CampaignAssetCoverage;
export type FeedCardStatus = 'open' | 'acked' | 'dismissed';

export interface FeedCard {
  id: string;
  /** D1 Phase 2 — which brand under the org fired this card. Used by
   *  /home to render the brand chip when the org has more than one. */
  brandId: string;
  kind: FeedCardKind;
  status: FeedCardStatus;
  title: string;
  body: string;
  payload: FeedCardPayload | null;
  createdAt: string;
  ackedAt: string | null;
  dismissedAt: string | null;
  /** s216-54h.51 — null = founder hasn't opened the drawer for this
   *  card yet. Auto-set when FeedCardDrawer mounts via POST
   *  /api/feed/:id/mark-read. Drives the unread-dot indicator on the
   *  home card row. */
  readAt: string | null;
  decisionNote: string | null;
  /** s216/s217 — creative_drop only. Map of conceptId → per-concept
   *  action: 'approved'/'objected' from the founder, or
   *  'auto_launched' from the A1 enforcer (deadline elapsed, no
   *  objection). Absent on other card kinds; absent (or empty)
   *  when no action exists yet. */
  dropConceptActions?: Record<
    string,
    {
      conceptId: string;
      action: 'approved' | 'objected' | 'auto_launched';
      actedAt: string;
    }
  >;
  /** Per-finding verdicts on a seo_audit / ai_visibility card, keyed by the
   *  finding's index in that card's findings array. Absent on other kinds,
   *  `{}` when nothing has been decided yet. Stamped at read time so a
   *  reopened drawer shows the founder's own earlier marks. */
  findingVerdicts?: Record<number, 'implemented' | 'dismissed'>;
}

/** Record a verdict on ONE finding of an audit card. The card stays open
 *  until every finding has an answer; the response says how far along that
 *  is so the drawer can reflect it without a refetch. */
export async function setFindingVerdict(
  kind: 'seo_audit' | 'ai_visibility',
  cardId: string,
  findingIndex: number,
  status: 'implemented' | 'dismissed',
): Promise<{ card: FeedCard; decidedCount: number; totalCount: number }> {
  // The path segment differs per kind because the two audits keep separate
  // histories server-side — see finding-verdicts.ts.
  const path = kind === 'seo_audit' ? 'seo-audit' : 'ai-visibility';
  const url = `${env.API_URL}/api/feed/${path}/${encodeURIComponent(cardId)}/finding/${findingIndex}/action`;
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  if (!res.ok) await throwFromResponse(res, `POST ${url} ${res.status}`);
  return (await res.json()) as { card: FeedCard; decidedCount: number; totalCount: number };
}

export interface Brand {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  /** Phase 4.28a — R2-hosted favicon. The BrandSwitcher pill in the
   *  top nav prefers this over logoUrl because favicons are already
   *  shaped for a small circle. Null on legacy brands that haven't
   *  been re-scraped since the field landed. */
  faviconUrl: string | null;
  /** Founder's marketing-site URL — captured at onboarding, editable
   *  in settings. Powers the "re-read my site" button in
   *  BrandVisualsSection and gets fed into Mag-generated copy that
   *  cites the brand's domain. Null on legacy brands that pre-date
   *  M1 s157. */
  marketingUrl: string | null;
  /** (mag-built-marketing-sites) Globally-unique handle the founder
   *  reserved for their Magig-hosted site — the page lives at
   *  `<siteHandle>.magig.site`. Null until reserved (the interview-first
   *  flow reserves it at finalize; URL-onboarded brands reserve it from
   *  the /site surface). */
  siteHandle: string | null;
  createdAt: string;
  /** D1 Phase 3c — null = live, ISO string = archived. */
  archivedAt: string | null;
  /** Only populated by GET /api/brands. True once the founder has
   *  saved a non-placeholder identity (company_name + category). The
   *  /brands UI uses it to expose a quick "delete" on never-onboarded
   *  rows. Other endpoints (create/update/delete) omit it. */
  briefReady?: boolean;
  /** Spec §0a.2 — mag's monthly autonomous-spend cap, USD cents.
   *  Null = no cap. Editable in /settings. */
  monthlyAutonomousSpendBudgetCents?: number | null;
  /** Founder's declared monthly ad budget, USD cents. Used to anchor
   *  mag's 20% autonomous default. Null when not yet declared. */
  monthlyAdBudgetCents?: number | null;
  /** Spec §0b.5 — always-on lane envelope. The slice of the ad
   *  budget reserved for perpetual channel work (no campaign id).
   *  Defaults to 50% of monthlyAdBudgetCents on first brand
   *  creation. Null = treat as no separate cap. */
  alwaysOnPaidBudgetCents?: number | null;
  /** [12.2L-124] AI strategist target country, ISO 3166-1 alpha-2
   *  (uppercased). Drives Google Keyword Planner geo for proposals.
   *  When null, strategist falls back to a brain-tz heuristic; when
   *  even that fails, the strategist refuses to run for the brand
   *  until the founder picks a country. */
  targetCountry?: string | null;
  /** [12.2L-124] AI strategist target language, ISO 639-1
   *  (lowercased). Same fallback chain as targetCountry. */
  targetLanguage?: string | null;
  /** Migration 0144 — stamped the first time the onboarding
   *  checklist hit all-settled. Source of truth so the dashboard
   *  can show the full home immediately after an OAuth-redirect
   *  reload that completes the last step, without re-running
   *  the per-step queries' transition check. Null on brands
   *  whose founder hasn't finished onboarding yet (or that pre-date
   *  the migration — those stamp on next load). */
  onboardedAt?: string | null;
  /** Migration 0144 — stamped after the dashboard fires the
   *  onboarding celebration moment (confetti + "wired up" badge).
   *  Gates the celebration to once per brand TOTAL across all
   *  devices + sessions. */
  onboardingCelebratedAt?: string | null;
}

export async function fetchBrands(): Promise<Brand[]> {
  const res = await fetch(`${env.API_URL}/api/brands`, { credentials: 'include' });
  if (res.status === 401) return [];
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/brands ${res.status} ${detail}`);
  }
  const json = (await res.json()) as { brands: Brand[] };
  return json.brands;
}

// s263 — dev-only template render endpoint. Renders through the
// HTML/CSS template pipeline (Playwright capture of the existing
// /render/creative/:id route). Lets the founder preview a hand-
// authored fill against any template in the registry without going
// through the LLM fill agent.

export interface DevTemplateListItem {
  key: string;
  label: string;
  description: string;
  bestFor: string;
  formats: string[];
  deprecated: boolean;
}

export async function fetchDevTemplates(): Promise<DevTemplateListItem[]> {
  const res = await fetch(`${env.API_URL}/api/__dev/templates/list`, {
    credentials: 'include',
  });
  if (!res.ok) return [];
  const json = (await res.json().catch(() => null)) as
    | { ok: true; templates: DevTemplateListItem[] }
    | { ok: false }
    | null;
  if (!json?.ok) return [];
  return json.templates;
}

export interface DevTemplateRenderResult {
  ok: true;
  url: string;
  creativeAssetId: string;
  templateKey: string;
  format: string;
  width: number;
  height: number;
  durationMs: number;
  brand: { id: string; slug: string; name: string };
}

export interface DevTemplateRenderError {
  ok: false;
  error: string;
  detail?: string;
  issues?: { path: string; message: string }[];
}

export async function devTemplateRender(input: {
  templateKey: string;
  templateFill: unknown;
  brandId?: string;
  format?: string;
}): Promise<DevTemplateRenderResult | DevTemplateRenderError> {
  const res = await fetch(`${env.API_URL}/api/__dev/templates/render`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  const body = (await res.json().catch(() => null)) as
    | DevTemplateRenderResult
    | DevTemplateRenderError
    | null;
  if (!body) {
    return { ok: false, error: 'unparseable_response', detail: `status ${res.status}` };
  }
  return body;
}

// s119 — typed error for plan-limit 402s. The api returns a structured
// payload (see PlanLimitExceededPayload in @magig/schemas) so the
// dashboard can render the right upgrade prompt. Call sites pattern-match
// on `err instanceof PlanLimitError` to differentiate "you hit a tier
// ceiling" from generic create failures.
export class PlanLimitError extends Error {
  override readonly name = 'PlanLimitError';
  readonly payload: PlanLimitExceededPayload;
  constructor(payload: PlanLimitExceededPayload) {
    super(payload.message);
    this.payload = payload;
  }
}

async function maybeThrowPlanLimit(res: Response): Promise<void> {
  if (res.status !== 402) return;
  const body = (await res.json().catch(() => null)) as PlanLimitExceededPayload | null;
  if (body && body.error === 'plan_limit_exceeded') {
    throw new PlanLimitError(body);
  }
}

export async function createBrandApi(input: { name: string }): Promise<Brand> {
  const res = await fetch(`${env.API_URL}/api/brands`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  await maybeThrowPlanLimit(res);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /api/brands ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { brand: Brand };
  return json.brand;
}

// ─── Interview-first brand creation (mag-built-marketing-sites) ────────

export interface InterviewMessage {
  role: 'mag' | 'founder';
  content: string;
}

/** One interview turn — send the transcript so far, get Mag's next line +
 *  whether she's done. */
export async function interviewTurn(
  brandId: string,
  messages: InterviewMessage[],
): Promise<{ done: boolean; reply: string }> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/interview/turn`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages }),
  });
  if (!res.ok) throw new Error(`POST interview/turn ${res.status}`);
  return (await res.json()) as { done: boolean; reply: string };
}

/** Finalize — research + synthesize the brain, get a suggested handle. */
export async function interviewFinalize(
  brandId: string,
  messages: InterviewMessage[],
): Promise<{ ok: boolean; siteHandleSuggestion: string; productCount: number; planCount: number }> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/interview/finalize`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages }),
  });
  if (!res.ok) throw new Error(`POST interview/finalize ${res.status}`);
  return (await res.json()) as {
    ok: boolean;
    siteHandleSuggestion: string;
    productCount: number;
    planCount: number;
  };
}

/** Live availability for a <handle>.magig.site subdomain. */
export async function checkSiteHandle(
  handle: string,
  from?: string,
): Promise<{
  handle: string;
  ok: boolean;
  available: boolean;
  error: string | null;
  suggestion: string;
}> {
  const params = new URLSearchParams({ handle });
  if (from) params.set('from', from);
  const res = await fetch(`${env.API_URL}/api/brands/site-handle/check?${params.toString()}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`GET site-handle/check ${res.status}`);
  return (await res.json()) as {
    handle: string;
    ok: boolean;
    available: boolean;
    error: string | null;
    suggestion: string;
  };
}

/** Generate (or regenerate) the brand's marketing site from its brain. */
/** Kick off an ASYNC generate. Returns immediately (202) with status
 *  'generating' — the worker builds in the background; poll getSiteApi. */
export async function generateSiteApi(brandId: string): Promise<{ ok: boolean; status: string }> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/site/generate`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    throw new Error(body.detail ?? body.error ?? `POST site/generate ${res.status}`);
  }
  return (await res.json()) as { ok: boolean; status: string };
}

/** The site's current status (null when none yet) + version. The dashboard
 *  polls this during an async generate: 'generating' → still building;
 *  'generated'/'edited'/'published' → ready to preview; 'failed' → retry. */
export async function getSiteApi(
  brandId: string,
): Promise<{ status: string | null; version: number | null }> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/site`, { credentials: 'include' });
  if (!res.ok) throw new Error(`GET site ${res.status}`);
  const json = (await res.json()) as { site: { status?: string } | null; version: number | null };
  return { status: json.site?.status ?? null, version: json.version };
}

/** Fetch the rendered site HTML (authenticated) for an iframe srcdoc.
 *  Returns null when no site has been generated yet. */
export async function fetchSitePreview(brandId: string): Promise<string | null> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/site/preview`, {
    credentials: 'include',
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`GET site/preview ${res.status}`);
  return res.text();
}

/** Reserve a globally-unique handle on a brand. */
export async function reserveSiteHandle(brandId: string, handle: string): Promise<Brand> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/site-handle`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ handle }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { reason?: string };
    throw new Error(body.reason ? `handle ${body.reason}` : `POST site-handle ${res.status}`);
  }
  return ((await res.json()) as { brand: Brand }).brand;
}

export async function updateBrandApi(
  brandId: string,
  input: {
    name?: string;
    logoUrl?: string | null;
    marketingUrl?: string | null;
    archived?: boolean;
    monthlyAutonomousSpendBudgetCents?: number | null;
    monthlyAdBudgetCents?: number | null;
    alwaysOnPaidBudgetCents?: number | null;
    targetCountry?: string | null;
    targetLanguage?: string | null;
  },
): Promise<Brand> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `PATCH /api/brands/${brandId} ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { brand: Brand };
  return json.brand;
}

// [multi-market] A brand's target markets — the strategist researches
// keywords once per market and competition is scoped per market.
export interface BrandMarket {
  country: string;
  language: string;
  isPrimary: boolean;
  sortOrder: number;
}

export async function fetchBrandMarkets(brandId: string): Promise<BrandMarket[]> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/markets`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`GET /api/brands/${brandId}/markets ${res.status}`);
  const json = (await res.json()) as { markets: BrandMarket[] };
  return json.markets;
}

export async function saveBrandMarkets(
  brandId: string,
  markets: { country: string; language?: string; isPrimary?: boolean }[],
): Promise<BrandMarket[]> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/markets`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ markets }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /api/brands/${brandId}/markets ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { markets: BrandMarket[] };
  return json.markets;
}

export async function deleteBrandApi(brandId: string): Promise<{ brand: Brand }> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `DELETE /api/brands/${brandId} ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as { brand: Brand };
}

/** Idempotently stamp the brand as onboarded — fire once when the
 *  founder's checklist transitions to all-settled. Server's COALESCE
 *  keeps the first stamp on retry / multi-tab races. Returns the
 *  refreshed brand so the caller can update the brands cache. */
export async function markBrandOnboarded(brandId: string): Promise<Brand> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/onboarding/complete`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /api/brands/${brandId}/onboarding/complete ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { brand: Brand };
  return json.brand;
}

/** Idempotently stamp the brand as having seen the onboarding
 *  celebration moment. Fire from the celebration's onMount so the
 *  next load (any device, any session) doesn't replay it. */
export async function markBrandOnboardingCelebrated(brandId: string): Promise<Brand> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/onboarding/celebrated`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /api/brands/${brandId}/onboarding/celebrated ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { brand: Brand };
  return json.brand;
}

export async function uploadBrandLogo(brandId: string, file: File): Promise<Brand> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/logo`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail =
      typeof body === 'object' && body && ('detail' in body || 'error' in body)
        ? String(
            (body as { detail?: unknown; error?: unknown }).detail ??
              (body as { error: unknown }).error,
          )
        : `POST /api/brands/${brandId}/logo ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { brand: Brand };
  return json.brand;
}

export interface FeedEnvelope {
  cards: FeedCard[];
  organizationId: string;
  /** Null when the request was the multi-brand rollup ('all'); set
   *  when the API resolved a specific active brand. */
  brandId: string | null;
  /** True iff the active brand has at least one campaign briefed. The
   *  dashboard uses this to gate pre-campaign UI (e.g. hide the "ask
   *  mag for a concept" composer until the founder has briefed a
   *  campaign — Mag has no strategic context to riff on otherwise).
   *  Omitted for the multi-brand rollup. */
  hasCampaigns?: boolean;
}

export async function fetchFeed(brandId?: string | null): Promise<FeedEnvelope | null> {
  const res = await fetch(`${env.API_URL}/api/feed${brandQuery(brandId)}`, {
    credentials: 'include',
  });
  if (res.status === 401) return null;
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: `GET /api/feed ${res.status}` }));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `GET /api/feed ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as FeedEnvelope;
}

// ───── Onboarding: URL scrape (§12.2L live surface) ────────────────────────
// The endpoint kicks the orchestrator fire-and-forget and returns runId +
// brandId in <500ms. UI navigates to the live page and polls
// /api/brand-brain/extraction-progress every 1.5s to render the work
// ticking step by step.

export interface ScrapeUrlResult {
  runId: string;
  brandId: string;
  organizationId: string;
}

/** [2026-06-08] Onboarding market autodetect. Country comes from
 *  server-side MaxMind (request IP); timezone comes from the
 *  browser's resolved IANA zone, passed up so the api can echo it
 *  back without trying to guess. Both fields are nullable when
 *  detection fails. */
export interface MarketDetectResult {
  country: string | null;
  timezone: string | null;
  source: 'maxmind+tz' | 'maxmind-only' | 'tz-only' | 'unknown';
}

export async function fetchMarketDetect(): Promise<MarketDetectResult> {
  // The browser is the source of truth for tz. Skip the call if it
  // can't tell us — the server still returns a country guess.
  let tz = '';
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    // Some old browsers throw — leave tz empty and let the server
    // return country-only.
  }
  const qs = tz ? `?tz=${encodeURIComponent(tz)}` : '';
  const res = await fetch(`${env.API_URL}/api/onboarding/market-detect${qs}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`GET /api/onboarding/market-detect ${res.status}`);
  }
  return (await res.json()) as MarketDetectResult;
}

export async function scrapeUrlIntoBrief(
  url: string,
  brandId?: string | null,
): Promise<ScrapeUrlResult> {
  const res = await fetch(`${env.API_URL}/api/onboarding/scrape-url${brandQuery(brandId)}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null as Record<string, unknown> | null);
    // Server may carry the human-readable reason on `message` (preferred,
    // mag-voice) or `detail` (legacy). Fall back to a generic line tied
    // to the status code so the toast is never empty.
    const message =
      (body && typeof body === 'object' && 'message' in body && typeof body.message === 'string'
        ? body.message
        : null) ??
      (body && typeof body === 'object' && 'detail' in body && typeof body.detail === 'string'
        ? body.detail
        : null) ??
      `POST /api/onboarding/scrape-url ${res.status}`;
    throw new Error(message);
  }
  return (await res.json()) as ScrapeUrlResult;
}

// ───── Onboarding: live extraction-progress polling ───────────────────────

export type ExtractionStep =
  | 'fetch_html'
  | 'extract_identity'
  | 'extract_voice'
  | 'extract_customer'
  | 'write_brain'
  | 'persist_landing_page'
  | 'enqueue_landing_thumbnail'
  | 'stamp_marketing_url'
  | 'visual_screenshot'
  | 'visual_read_guidelines'
  | 'visual_store_logo'
  | 'visual_store_favicon'
  | 'visual_upsert_guidelines'
  | 'visual_classify_landing_images'
  | 'crawl_subpages'
  | 'crawl_persist_pages'
  | 'crawl_classify_subpage_images'
  | 'extract_products'
  | 'extract_team'
  | 'extract_testimonials'
  | 'extract_voice_examples'
  | 'extract_design_system'
  | 'extract_motifs'
  | 'stamp_target_market';

export type ExtractionStepStatus = 'pending' | 'running' | 'done' | 'failed' | 'skipped';
export type ExtractionStepGroup = 'fetch' | 'visual' | 'crawl' | 'extract';

export interface ExtractionStepRow {
  step: ExtractionStep;
  groupKind: ExtractionStepGroup;
  orderIdx: number;
  status: ExtractionStepStatus;
  receipt: string | null;
  metadata: unknown;
  startedAt: string | null;
  completedAt: string | null;
}

export interface ExtractionProgressResponse {
  runId: string | null;
  steps: ExtractionStepRow[];
}

export async function fetchExtractionProgress(
  brandId: string,
): Promise<ExtractionProgressResponse> {
  const res = await fetch(`${env.API_URL}/api/brand-brain/extraction-progress?brand=${brandId}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/brand-brain/extraction-progress ${res.status} ${detail}`);
  }
  return (await res.json()) as ExtractionProgressResponse;
}

export interface AskForConceptInput {
  /** Optional founder steering. Pass undefined for "just give me another". */
  steering?: string;
}

export async function askForConcept(
  input: AskForConceptInput = {},
  brandId?: string | null,
): Promise<FeedCard> {
  const res = await fetch(`${env.API_URL}/api/feed/concepts${brandQuery(brandId)}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input.steering ? { steering: input.steering } : {}),
  });
  if (!res.ok) {
    const body = await res
      .json()
      .catch(() => ({ detail: `POST /api/feed/concepts ${res.status}` }));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /api/feed/concepts ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { card: FeedCard };
  return json.card;
}

/** Ask mag a general question via the home composer (s141). Returns
 *  the new mag_chat feed card so the caller can prepend it
 *  optimistically while the home feed refetches. */
/** [s264] Typed error thrown when the api returns 503 runaway_paused.
 *  Replaces the s174 BudgetExceededError — per the meter-with-overage
 *  spec, cost itself never blocks; only the catastrophic runaway-paused
 *  state does (a level no real customer hits). When this fires, ops
 *  has already been notified server-side; the founder just needs to
 *  see the state isn't silent. */
export class RunawayPausedError extends Error {
  override readonly name = 'RunawayPausedError';
  constructor(readonly pausedAt: string) {
    super(
      "mag's paused on your account — looks like something is firing more than it should. the magig team has been notified; expect to hear from us within an hour.",
    );
  }
}

/** [s264] Mag-token balance hit zero — mag's generative side is paused
 *  until the founder tops up. Dashboard chat surfaces a top-up nag
 *  instead of a generic error. `balance` may be negative (the call
 *  that pushed past zero already billed). */
export class InsufficientTokensError extends Error {
  override readonly name = 'InsufficientTokensError';
  constructor(
    readonly balance: number,
    readonly topUpHref: string,
  ) {
    super(
      "i'm out of mag tokens — top up at the dashboard and i'll pick this back up. nothing scheduled is paused; just my generative side is on hold.",
    );
  }
}

/** Parse a non-2xx api response and throw the right typed error.
 *  Pulls runaway_paused + out_of_tokens out specifically; everything
 *  else becomes a generic Error with the api detail. */
async function throwFromResponse(res: Response, fallback: string): Promise<never> {
  // biome-ignore lint/suspicious/noExplicitAny: api error shapes vary by endpoint
  const body = (await res.json().catch(() => ({}))) as any;
  if (res.status === 503 && body && body.error === 'runaway_paused') {
    throw new RunawayPausedError(String(body.pausedAt ?? ''));
  }
  if (res.status === 402 && body && body.error === 'out_of_tokens') {
    throw new InsufficientTokensError(
      Number(body.balance ?? 0),
      String(body.topUpHref ?? '/settings/billing'),
    );
  }
  const msg =
    typeof body === 'object' && body && 'detail' in body
      ? String(body.detail)
      : typeof body === 'object' && body && 'error' in body
        ? String(body.error)
        : fallback;
  throw new Error(msg);
}

export async function askMagChat(
  question: string,
  brandId?: string | null,
  attachedAssetIds?: string[],
): Promise<FeedCard> {
  const body: Record<string, unknown> = { question };
  if (attachedAssetIds && attachedAssetIds.length > 0) {
    body['attachedAssetIds'] = attachedAssetIds;
  }
  const res = await fetch(`${env.API_URL}/api/feed/chat${brandQuery(brandId)}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) await throwFromResponse(res, `POST /api/feed/chat ${res.status}`);
  const json = (await res.json()) as { card: FeedCard };
  return json.card;
}

/** Right-rail pulse shape (s147). Combines tracker live-activity +
 *  the brand's LLM spend this calendar month / previous month. */
export interface BrandPulse {
  hasTracker: boolean;
  liveVisitors: number;
  eventsToday: number;
  costThisMonthUsd: number;
  costLastMonthUsd: number | null;
}

export interface BrandPulseEnvelope {
  pulse: BrandPulse;
  brandId: string;
}

export async function fetchBrandPulse(brandId?: string | null): Promise<BrandPulseEnvelope | null> {
  const brand = brandQuery(brandId);
  const tz = tzQuery(brand ? '&' : '?');
  const res = await fetch(`${env.API_URL}/api/home/pulse${brand}${tz}`, {
    credentials: 'include',
  });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`GET /api/home/pulse ${res.status}`);
  return (await res.json()) as BrandPulseEnvelope;
}

// ───── Brand calendar (agency-coordination C4) ────────────────────────

export type CalendarChipKindDTO = 'organic_slot' | 'paid_launch';

export interface CalendarChipDTO {
  kind: CalendarChipKindDTO;
  campaignId: string;
  campaignName: string;
  campaignKind: string;
  label: string;
  hour?: number;
  detail?: string;
  status?: string;
  channel?: 'meta_organic' | 'meta_paid' | 'google_paid';
  format?: string;
  anchorId?: string;
}

export interface CalendarDayDTO {
  date: string;
  chips: CalendarChipDTO[];
}

export interface BrandCalendarDTO {
  weekStart: string;
  weekEnd: string;
  brandTz: string;
  monthlyIntent: {
    planMonth: string;
    summary: string;
    launches: Array<{ title: string; whenIsh: string }>;
    events: Array<{ title: string; whenIsh: string }>;
  } | null;
  days: CalendarDayDTO[];
}

export async function fetchBrandCalendar(brandId: string): Promise<BrandCalendarDTO | null> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/calendar`, {
    credentials: 'include',
  });
  if (res.status === 401) return null;
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`GET /api/brands/${brandId}/calendar ${res.status}`);
  const json = (await res.json()) as { calendar: BrandCalendarDTO };
  return json.calendar;
}

/** On-site conversion funnel for a brand, optionally scoped to one
 *  campaign. Returns null on 401/404 so a missing brand renders nothing
 *  rather than throwing into a card body. */
export async function fetchBrandFunnel(
  brandId: string,
  campaignId?: string | null,
): Promise<BrandFunnel | null> {
  const qs = campaignId ? `?campaignId=${encodeURIComponent(campaignId)}` : '';
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/funnel${qs}`, {
    credentials: 'include',
  });
  if (res.status === 401) return null;
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`GET /api/brands/${brandId}/funnel ${res.status}`);
  const json = (await res.json()) as { funnel: BrandFunnel | null };
  return json.funnel;
}

/** Append a follow-up turn to an existing mag_chat card (s143). The
 *  server appends both the founder's question and mag's reply to the
 *  card's payload.messages array, updates card.body to the latest
 *  answer for the home preview, and returns the updated card. */
export async function sendMagChatTurn(
  cardId: string,
  question: string,
  attachedAssetIds?: string[],
): Promise<FeedCard> {
  const body: Record<string, unknown> = { question };
  if (attachedAssetIds && attachedAssetIds.length > 0) {
    body['attachedAssetIds'] = attachedAssetIds;
  }
  const res = await fetch(`${env.API_URL}/api/feed/chat/${cardId}/messages`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) await throwFromResponse(res, `POST /api/feed/chat/${cardId}/messages ${res.status}`);
  const json = (await res.json()) as { card: FeedCard };
  return json.card;
}

// ───── Activity ───────────────────────────────────────────────────────────

export interface ActivityByRole {
  role: string;
  calls: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCachedTokens: number;
  costUsd: string;
  lastAt: string | null;
  /** s184 — cached_tokens / input_tokens for this role. 0..1. */
  cacheHitRate: number;
}

export interface ActivityByBrand {
  brandId: string;
  brandName: string;
  calls: number;
  costUsd: string;
}

export interface MagToolCallStat {
  toolName: string;
  calls: number;
  errors: number;
  avgDurationMs: number;
  lastAt: string;
}

export interface MagToolCallSummary {
  windowDays: number;
  byTool: MagToolCallStat[];
  totals: { calls: number; errors: number };
}

export interface ActivitySummary {
  organizationId: string;
  windowDays: number;
  byRole: ActivityByRole[];
  /** D1 Phase 2 — per-brand split. Hidden by the UI when length <= 1. */
  byBrand: ActivityByBrand[];
  /** s186 — mag's analytics-tool usage in the window. Hidden by the
   *  UI when byTool is empty. */
  tools: MagToolCallSummary;
  totals: {
    calls: number;
    costUsd: string;
    /** s184 — overall cache hit rate across all roles in the window. */
    cacheHitRate: number;
  };
}

export interface TrustByKind {
  kind: 'concept_proposal' | 'creative_variants';
  kept: number;
  skipped: number;
  open: number;
  score: number | null;
}

export interface TrustSummary {
  organizationId: string;
  windowDays: number;
  byKind: TrustByKind[];
  overall: { kept: number; skipped: number; score: number | null };
}

export async function fetchTrust(days = 30): Promise<TrustSummary | null> {
  const res = await fetch(`${env.API_URL}/api/trust?days=${days}`, {
    credentials: 'include',
  });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`GET /api/trust ${res.status}`);
  return (await res.json()) as TrustSummary;
}

export async function fetchActivity(days = 30): Promise<ActivitySummary | null> {
  const res = await fetch(`${env.API_URL}/api/activity?days=${days}`, {
    credentials: 'include',
  });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`GET /api/activity ${res.status}`);
  return (await res.json()) as ActivitySummary;
}

// ───── Admin: LLM costs (s180) ─────────────────────────────────────────────
// Global aggregates across llm_calls — gated by MAGIG_ADMIN_EMAILS on the
// API side. Dashboard route does a parallel client-side gate so non-admins
// don't even see the link.

export interface AdminLLMCostsTotals {
  calls: number;
  costUsd: string;
  inputTokens: number;
  cachedTokens: number;
  outputTokens: number;
  cacheHitRate: number;
}

export interface AdminLLMCostsByRole {
  role: string;
  model: string;
  calls: number;
  costUsd: string;
  inputTokens: number;
  cachedTokens: number;
  outputTokens: number;
  cacheHitRate: number;
}

export interface AdminLLMCostsByOrg {
  organizationId: string;
  organizationName: string | null;
  calls: number;
  costUsd: string;
  cacheHitRate: number;
}

/** [s264] Per-agent breakdown. Surfaces the cost driver inside each
 *  role (e.g. "orchestrator costs $34, of which mag-recap is $12"). */
export interface AdminLLMCostsByAgent {
  agentName: string;
  role: string;
  calls: number;
  costUsd: string;
  cacheHitRate: number;
  avgInputTokens: number;
}

export interface AdminLLMCostsSpendPoint {
  date: string;
  costUsd: string;
  calls: number;
  cacheHitRate: number;
}

/** [s264] Per-org token economics — pairs with byOrg above so margin
 *  per customer is one screen of math. costUsd from byOrg = COGS;
 *  topupRevenueUsd from here + plan basePrice = revenue. */
export interface AdminTokenEconomicsByOrg {
  organizationId: string;
  organizationName: string | null;
  plan: string;
  balanceTokens: number;
  tokensGranted: number;
  tokensConsumed: number;
  topupRevenueUsd: string;
}

export interface AdminLLMCostsSummary {
  windowDays: number;
  totals: AdminLLMCostsTotals;
  byRole: AdminLLMCostsByRole[];
  byOrg: AdminLLMCostsByOrg[];
  byAgent: AdminLLMCostsByAgent[];
  tokenEconomicsByOrg: AdminTokenEconomicsByOrg[];
  spendTrend: AdminLLMCostsSpendPoint[];
}

export class AdminForbiddenError extends Error {
  override readonly name = 'AdminForbiddenError';
}

export interface AdminTrustDropRow {
  id: string;
  brandId: string;
  brandName: string;
  organizationId: string;
  decisionType: string;
  fromLevel: string;
  toLevel: string;
  reason: string;
  receipt: string | null;
  occurredAt: string;
}

export interface AdminRecoveryQueueRow {
  brandId: string;
  brandName: string;
  organizationId: string;
  decisionType: string;
  currentLevel: string;
  lastDropReason: string;
  lastDropAt: string;
  cleanStreakCount: number;
  alreadyRecovered: boolean;
}

export async function fetchAdminTrustDrops(days = 30): Promise<AdminTrustDropRow[]> {
  const res = await fetch(`${env.API_URL}/api/admin/trust/drops?days=${days}`, {
    credentials: 'include',
  });
  if (res.status === 401 || res.status === 403) {
    throw new AdminForbiddenError(`GET /api/admin/trust/drops ${res.status}`);
  }
  if (!res.ok) throw new Error(`GET /api/admin/trust/drops ${res.status}`);
  const body = (await res.json()) as { drops: AdminTrustDropRow[] };
  return body.drops;
}

export async function fetchAdminTrustRecoveryQueue(): Promise<AdminRecoveryQueueRow[]> {
  const res = await fetch(`${env.API_URL}/api/admin/trust/recovery-queue`, {
    credentials: 'include',
  });
  if (res.status === 401 || res.status === 403) {
    throw new AdminForbiddenError(`GET /api/admin/trust/recovery-queue ${res.status}`);
  }
  if (!res.ok) throw new Error(`GET /api/admin/trust/recovery-queue ${res.status}`);
  const body = (await res.json()) as { queue: AdminRecoveryQueueRow[] };
  return body.queue;
}

// [12.2L-127] Strategist cross-brand status. Heavier than the
// per-brand heartbeats endpoint — call from /admin/strategist.

export interface AdminStrategistRecentProposal {
  id: string;
  status: string;
  createdAt: string;
  decidedAt: string | null;
  costUsd: number;
  proposalName: string | null;
  outcomeScore: string | null;
  outcomeActualCpaUsd: number | null;
}

export interface AdminStrategistOutcomes {
  hit: number;
  outperformed: number;
  miss_high_cost: number;
  dead: number;
  inconclusive: number;
  totalScored: number;
}

export interface AdminStrategistLifecycle {
  open: number;
  accepted: number;
  dismissed: number;
  abandoned: number;
}

export interface AdminStrategistBidLookup {
  campaignsLaunched: number;
  keywordsMatched: number;
  keywordsTotal: number;
}

export interface AdminStrategistBrandRow {
  brandId: string;
  brandSlug: string;
  brandName: string;
  organizationId: string;
  cadence:
    | { decision: 'run'; reason: string; effectiveWindowDays: number }
    | { decision: 'skip'; reason: string; detail: string | null };
  lifetimeSpendUsd: number;
  totalProposals: number;
  outcomes: AdminStrategistOutcomes;
  lifecycle: AdminStrategistLifecycle;
  bidLookup: AdminStrategistBidLookup;
  recentProposals: AdminStrategistRecentProposal[];
}

export async function fetchAdminStrategistStatus(): Promise<AdminStrategistBrandRow[]> {
  const res = await fetch(`${env.API_URL}/api/admin/strategist/status`, {
    credentials: 'include',
  });
  if (res.status === 401 || res.status === 403) {
    throw new AdminForbiddenError(`GET /api/admin/strategist/status ${res.status}`);
  }
  if (!res.ok) throw new Error(`GET /api/admin/strategist/status ${res.status}`);
  const body = (await res.json()) as { brands: AdminStrategistBrandRow[] };
  return body.brands;
}

export async function fetchAdminLLMCosts(days: 1 | 7 | 30 = 7): Promise<AdminLLMCostsSummary> {
  const res = await fetch(`${env.API_URL}/api/admin/llm-costs?days=${days}`, {
    credentials: 'include',
  });
  if (res.status === 401 || res.status === 403) {
    throw new AdminForbiddenError(`GET /api/admin/llm-costs ${res.status}`);
  }
  if (!res.ok) throw new Error(`GET /api/admin/llm-costs ${res.status}`);
  return (await res.json()) as AdminLLMCostsSummary;
}

export async function generateVariantsImage(variantsCardId: string): Promise<FeedCard> {
  const res = await fetch(`${env.API_URL}/api/feed/variants/${variantsCardId}/image`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res
      .json()
      .catch(() => ({ detail: `POST /api/feed/variants/.../image ${res.status}` }));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /api/feed/variants/.../image ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { card: FeedCard };
  return json.card;
}

export async function generateVariants(conceptCardId: string): Promise<FeedCard> {
  const res = await fetch(`${env.API_URL}/api/feed/concepts/${conceptCardId}/variants`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res
      .json()
      .catch(() => ({ detail: `POST /api/feed/concepts/.../variants ${res.status}` }));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /api/feed/concepts/.../variants ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { card: FeedCard };
  return json.card;
}

export async function dismissFeedCard(id: string, note?: string): Promise<FeedCard> {
  const res = await fetch(`${env.API_URL}/api/feed/${id}/dismiss`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(note?.trim() ? { note: note.trim() } : {}),
  });
  if (!res.ok) {
    const body = await res
      .json()
      .catch(() => ({ detail: `POST /api/feed/${id}/dismiss ${res.status}` }));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /api/feed/${id}/dismiss ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { card: FeedCard };
  return json.card;
}

/** [spec 6bfa5da5] Stop a pending autonomous action inside its grace
 *  window. The endpoint is brand-scoped and re-checks the brand against
 *  the row, so a guessed action uuid cannot be vetoed from a brand the
 *  caller happens to have access to.
 *
 *  Distinguishes the outcomes the veto page has to tell apart: 'ok',
 *  'too_late' (the worker fired between the read and the click — not an
 *  error, just the honest answer), and 'not_found'. */
export type VetoActionOutcome = 'ok' | 'too_late' | 'not_found' | 'failed';

export async function vetoPendingAutonomousAction(
  brandId: string,
  actionId: string,
  reason?: string,
): Promise<VetoActionOutcome> {
  const url = `${env.API_URL}/api/brands/${encodeURIComponent(brandId)}/autonomous-actions/${encodeURIComponent(actionId)}/veto`;
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(reason?.trim() ? { reason: reason.trim() } : {}),
  });
  if (res.ok) return 'ok';
  const body = (await res.json().catch(() => ({}))) as { error?: unknown };
  const code = typeof body.error === 'string' ? body.error : '';
  if (code === 'too_late') return 'too_late';
  if (code === 'not_found' || code === 'brand_not_found') return 'not_found';
  return 'failed';
}

/** [spec 8a1b9ddf] Stop a pending action from a SIGNED LINK, with no
 *  session. The token is the credential; everything the veto acts on is
 *  inside it, so this sends no ids of its own.
 *
 *  `credentials` is deliberately omitted — this call must work for a
 *  browser that has never signed in, which is the whole point. */
export async function vetoPendingActionByToken(token: string): Promise<VetoActionOutcome> {
  const res = await fetch(`${env.API_URL}/api/veto/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
  if (res.ok) return 'ok';
  const body = (await res.json().catch(() => ({}))) as { error?: unknown };
  const code = typeof body.error === 'string' ? body.error : '';
  if (code === 'too_late') return 'too_late';
  if (code === 'not_found' || code === 'wrong_brand' || code === 'invalid_or_expired') {
    return 'not_found';
  }
  return 'failed';
}

/** s217.20 — record a founder verdict on a funnel-diagnosis card. 'implemented'
 *  acks the card; 'dismissed' dismisses it. Either way the leak + recommendation
 *  land in brand_funnel_actions so Mag's next diagnosis doesn't repeat itself. */
export async function setFunnelDiagnosisAction(
  cardId: string,
  status: 'implemented' | 'dismissed' | 'rejected',
  note?: string,
): Promise<FeedCard> {
  const url = `${env.API_URL}/api/feed/funnel-diagnosis/${encodeURIComponent(cardId)}/action`;
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(note?.trim() ? { status, note: note.trim() } : { status }),
  });
  if (!res.ok) await throwFromResponse(res, `POST ${url} ${res.status}`);
  const json = (await res.json()) as { card: FeedCard };
  return json.card;
}

/** s218 — record a founder verdict on a seo_audit card. 'implemented' acks the
 *  card; 'dismissed' dismisses it. Either way the audit summary + findings land
 *  in seo_audit_actions so the next weekly audit doesn't nag. */
export async function setSeoAuditAction(
  cardId: string,
  status: 'implemented' | 'dismissed',
  note?: string,
): Promise<FeedCard> {
  const url = `${env.API_URL}/api/feed/seo-audit/${encodeURIComponent(cardId)}/action`;
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(note?.trim() ? { status, note: note.trim() } : { status }),
  });
  if (!res.ok) await throwFromResponse(res, `POST ${url} ${res.status}`);
  const json = (await res.json()) as { card: FeedCard };
  return json.card;
}

/** Record a founder verdict on an ai_visibility card. 'implemented' acks the
 *  card; 'dismissed' dismisses it. Either way the summary + findings land in
 *  ai_visibility_actions so the next weekly run reads them as priorActions and
 *  doesn't re-raise a fix already shipped.
 *
 *  Separate endpoint from setSeoAuditAction on purpose — the two audits keep
 *  independent histories so a verdict on one never silences the other. */
export async function setAiVisibilityAction(
  cardId: string,
  status: 'implemented' | 'dismissed',
  note?: string,
): Promise<FeedCard> {
  const url = `${env.API_URL}/api/feed/ai-visibility/${encodeURIComponent(cardId)}/action`;
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(note?.trim() ? { status, note: note.trim() } : { status }),
  });
  if (!res.ok) await throwFromResponse(res, `POST ${url} ${res.status}`);
  const json = (await res.json()) as { card: FeedCard };
  return json.card;
}

/** s219 — latest persisted Lighthouse scores for the home "site health" rail
 *  widget. Survives the seo_audit card being acted on (the card payload
 *  vanishes; these scores live in brand_seo_health). */
export interface BrandSeoHealth {
  hasAudit: boolean;
  hasMarketingUrl: boolean;
  url: string | null;
  scores: {
    performance: number | null;
    seo: number | null;
    accessibility: number | null;
    bestPractices: number | null;
  } | null;
  lastCheckedAt: string | null;
  /** ms until a re-run is allowed (0 = available now). Drives the widget's
   *  pre-emptive "next check in Xm" disabled state. */
  rerunAvailableInMs: number;
  /** s219.3 — the latest audit's full output + evidence, so the widget can
   *  always open the analysis (even with no live feed card). null on pre-0186
   *  snapshots — the widget then falls back to the open card. */
  output: SeoAuditCardPayload['output'] | null;
  evidence: SeoAuditCardPayload['evidence'];
  costUsd: number;
}

/** The seo_audit feed-card payload shape — reused to type the persisted audit. */
type SeoAuditCardPayload = Extract<NonNullable<FeedCard['payload']>, { kind: 'seo_audit' }>;

export async function fetchBrandSeoHealth(brandId?: string | null): Promise<BrandSeoHealth | null> {
  if (!brandId) return null;
  const res = await fetch(`${env.API_URL}/api/brands/${encodeURIComponent(brandId)}/seo-health`, {
    credentials: 'include',
  });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`GET /api/brands/:id/seo-health ${res.status}`);
  return (await res.json()) as BrandSeoHealth;
}

export type RunSeoAuditOutcome =
  | { outcome: 'card_created'; feedCardId?: string; brandId: string }
  | { outcome: 'all_clear'; brandId: string }
  | { outcome: 'already_open'; brandId: string }
  | { outcome: 'no_url' }
  | { outcome: 'cooldown'; retryAfterMs: number };

/** s219 — force a fresh SEO audit now (the rail widget's "re-run" button).
 *  Refreshes the scores and spawns a findings card if material; behind a server
 *  cooldown (returns { outcome: 'cooldown' } when too soon). */
export async function runSeoAuditNow(brandId: string): Promise<RunSeoAuditOutcome> {
  const url = `${env.API_URL}/api/brands/${encodeURIComponent(brandId)}/seo-audit/run`;
  const res = await fetch(url, { method: 'POST', credentials: 'include' });
  if (!res.ok) await throwFromResponse(res, `POST ${url} ${res.status}`);
  return (await res.json()) as RunSeoAuditOutcome;
}

/** The persisted AI-visibility snapshot behind the widget's "ai search"
 *  section. Read instead of the feed card because the card is transient: an
 *  all_clear run creates none, and a founder verdict closes the one that
 *  exists — so for a healthy brand the card is exactly what is NOT there. */
export interface BrandAiVisibilityHealth {
  hasCheck: boolean;
  hasMarketingUrl: boolean;
  url: string | null;
  lastCheckedAt: string | null;
  /** ms until a re-run is allowed (0 = available now). Its own clock, separate
   *  from the SEO one — two checks, two metered calls. */
  rerunAvailableInMs: number;
  output: AiVisibilityCardPayload['output'] | null;
  evidence: AiVisibilityCardPayload['evidence'] | null;
  costUsd: number;
}

/** The ai_visibility feed-card payload shape — reused to type the snapshot, so
 *  the widget can hand it straight to AiVisibilityDetails. */
type AiVisibilityCardPayload = Extract<NonNullable<FeedCard['payload']>, { kind: 'ai_visibility' }>;

export async function fetchBrandAiVisibilityHealth(
  brandId?: string | null,
): Promise<BrandAiVisibilityHealth | null> {
  if (!brandId) return null;
  const res = await fetch(
    `${env.API_URL}/api/brands/${encodeURIComponent(brandId)}/ai-visibility-health`,
    { credentials: 'include' },
  );
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`GET /api/brands/:id/ai-visibility-health ${res.status}`);
  return (await res.json()) as BrandAiVisibilityHealth;
}

export type RunAiVisibilityOutcome =
  | { outcome: 'card_created'; feedCardId?: string; brandId: string }
  | { outcome: 'all_clear'; brandId: string }
  | { outcome: 'already_open'; brandId: string }
  | { outcome: 'no_url' }
  | { outcome: 'no_brand' }
  | { outcome: 'cooldown'; retryAfterMs: number };

/** Force a fresh AI-visibility check now. Refreshes the snapshot and spawns a
 *  findings card only if something material turned up; behind a server cooldown
 *  (returns { outcome: 'cooldown' } when too soon). */
export async function runAiVisibilityNow(brandId: string): Promise<RunAiVisibilityOutcome> {
  const url = `${env.API_URL}/api/brands/${encodeURIComponent(brandId)}/ai-visibility/run`;
  const res = await fetch(url, { method: 'POST', credentials: 'include' });
  if (!res.ok) await throwFromResponse(res, `POST ${url} ${res.status}`);
  return (await res.json()) as RunAiVisibilityOutcome;
}

/** Resolve a mag_followup with one of three founder actions (s161).
 *  - 'done' → status='done', completed_by='founder'
 *  - 'working' → resets last_nudged_at (buys another cadence cycle)
 *  - 'drop' → status='abandoned', abandoned_reason from optional note
 *  Each closes the matching mag_followup feed card so the home stays
 *  clean. The api responds with { ok: true } on success. */
export async function resolveFollowup(
  followupId: string,
  action: 'done' | 'working' | 'drop',
  note?: string,
): Promise<void> {
  const res = await fetch(`${env.API_URL}/api/followups/${followupId}/${action}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: action === 'drop' && note?.trim() ? JSON.stringify({ note: note.trim() }) : '{}',
  });
  if (!res.ok) {
    const body = await res
      .json()
      .catch(() => ({ detail: `POST /api/followups/${followupId}/${action} ${res.status}` }));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail?: unknown }).detail)
        : `POST /api/followups/${followupId}/${action} ${res.status}`;
    throw new Error(detail);
  }
}

// ───── Campaigns ──────────────────────────────────────────────────────────
// new-campaign handoff §8. Three endpoints: POST /draft (kicks off), GET
// /:id (poll), POST /:id/launch (simulated). The DRAFTING screen reads
// progress straight off the campaign payload — server is the source of
// truth.

/** Where this campaign row came from. Unification roadmap Phase 1 —
 *  'briefed' is mag-drafted from a founder brief, 'imported' is a
 *  takeover of an existing google/meta ads campaign. Behavior must
 *  NOT gate on this; it's a UI hint + analytics discriminator. */
export type CampaignOrigin = 'briefed' | 'imported';

export interface CampaignSummary {
  id: string;
  organizationId: string;
  /** Brand the campaign belongs to. Exposed so the creative pipeline
   *  (M1 s159.3) + the launch flow (s161) can act on the campaign
   *  without a separate brand lookup. */
  brandId: string;
  status: CampaignStatus;
  kind: CampaignKind;
  /** Phase 3 — provenance chip on the unified /campaigns list. */
  origin: CampaignOrigin;
  /** Ad platform if this campaign is bound to one. Always set for
   *  origin='imported'; set for origin='briefed' once it launches. */
  provider: AdPlatform | null;
  brief: string;
  name: string | null;
  createdAt: string;
  readyAt: string | null;
  launchedAt: string | null;
  shortPitch: string | null;
  /** True when status='ready' but draft is null — non-creative kinds
   *  (conversion_path, analytics_setup) end up here. UI labels them
   *  "captured · building next" instead of "ready · needs you". */
  captured: boolean;
  /** s155 — most recent insight, attached server-side for live
   *  analytics_setup campaigns so the home page can render "ongoing
   *  reads" without a second round-trip. Null when no scan has run yet
   *  or the kind isn't analytics_setup. */
  latestRead: {
    insightId: string;
    headline: string;
    generatedAt: string;
    material: boolean;
  } | null;
  /** s155 — next scheduled scan, surfaced on the list so the home page
   *  can render "next read in ~3h" alongside the latest. Null when the
   *  cadence is 'off' or the campaign isn't scanning. */
  nextScanAt: string | null;
  /** s156 — current cadence. Drives pause/resume affordances and the
   *  PAUSED pill in the campaigns list + detail subject card. Mirrors
   *  CampaignDetail.scanCadence (same enum). */
  scanCadence: CampaignScanCadence;
  /** s160 — mag's one-liner explaining the current nextScanAt. Surfaced
   *  verbatim as the "i'll check back…" heartbeat line on the campaign
   *  page + /home insight cards. Null on campaigns scanned before
   *  s160 shipped. */
  nextScanReason: string | null;
  /** [12.2L-96] — autopilot status badge driver + mag's-next-moves
   *  source on /home. True iff every decision in every channel for this
   *  campaign's kind+provider is overridden to its AUTOPILOT_ON_LEVELS
   *  value. Always present; false for kinds with no autopilot lanes. */
  autopilotOn: boolean;
  /** [12.2L-96] — capped preview of mag's planned actions. Null when
   *  autopilot is off or no takeover-read exists. */
  autopilotPreview: {
    headline: string;
    actions: Array<{
      title: string;
      blastRadius: 'low' | 'medium' | 'high';
      autoApplyEligible: boolean;
    }>;
  } | null;
  /** Real-platform paid delivery, derived server-side off the draft.
   *  `running === 0 && launched > 0` means mag (or the founder) paused
   *  everything — the campaign row still reads status='launched',
   *  because pausing happens on deliverables and never flips the
   *  campaign. Any surface that renders "live" off status alone is
   *  lying in exactly the way this field exists to stop.
   *
   *  NULL MEANS NOT APPLICABLE — analytics_setup, organic_meta, and
   *  anything launched only via 'mock'. Never read null as stopped. */
  paidDelivery: {
    launched: number;
    running: number;
    lastPausedAt: string | null;
  } | null;
  /** The SERVING verdict for this campaign — the same one the detail
   *  page leads with, least-serving across its ads (spec be02dc43).
   *
   *  The list used to badge LIVE off `status` while the detail page
   *  said LIMITED about the same campaign. `paidDelivery` above fixed
   *  this shape once, for pauses; this is the other half, for whether
   *  the platform is actually serving.
   *
   *  UNDEFINED means no reading — render exactly as before this
   *  existed, never as live. */
  verdictState?: 'pending' | 'not_serving' | 'serving_thin' | 'learning' | 'readable';
  /** The platform's own word on each launched deliverable, keyed by
   *  deliverable id. Hand-maintained twin of the api's field.
   *
   *  Carried ALONGSIDE verdictState, not instead of it (decision 909a74f):
   *  the list shows google's LIVE/LIMITED chip and the measured verdict
   *  together, and they can disagree. Absent when no deliverable had a
   *  reading — render exactly as before, never as live. */
  platformServingStatus?: Record<string, DeliverableServingStatus>;
  /** A founder decision is waiting on this launched campaign (today: at
   *  least one dead keyword). Hand-maintained twin of the api's field.
   *
   *  Absent means nothing to decide OR nothing measured — the two must not
   *  render differently. */
  needsDecision?: boolean;

  /** [spec 3a365634] Open asks mag has filed ABOUT this campaign — feed
   *  cards anchored to it and still unanswered. Hand-maintained twin of
   *  the api's field.
   *
   *  Distinct from needsDecision, which reads the campaign's own verdict
   *  signals: this one reads the feed, which is where the founder saw
   *  NEEDS YOU 2 while this list said 0.
   *
   *  Absent means nothing open, never 0 — do not render a count of zero. */
  attentionCount?: number;
}

/** Tracker config attached to analytics_setup campaigns on the detail
 *  fetch. The plaintext write key is never included (the api hashes it
 *  at generation and never persists the plaintext) — we expose only
 *  whether one exists so the review screen can either reference the
 *  founder's existing env var or surface "generate one" as a step. */
export interface AnalyticsTrackerContext {
  publicKey: string;
  allowedHosts: string[];
  hasWriteKey: boolean;
  ingestUrl: string;
}

/** One proactive insight Mag wrote for the campaign (s125). Returned
 *  by both the detail endpoint (latest 10) and the dedicated list
 *  endpoint. Stored on the campaign_insights table. */
export interface CampaignInsight {
  id: string;
  campaignId: string;
  generatedAt: string;
  headline: string;
  body: string;
  recommendation: string;
  costUsd: number;
  model: string;
  /** Per-event count tally Mag saw in the scan window (s127). Map of
   *  event name → count over the campaign's drafted event set. Null
   *  on rows persisted before s127 — the launched page degrades to
   *  no diff chips on those. */
  windowSnapshot: Record<string, number> | null;
  /** Per-insight chat thread (s159.7 → s172d). Founder + mag turns
   *  about this specific insight. Empty until the founder asks
   *  something. toolCalls is set on mag turns where she investigated
   *  via analytics tools. */
  thread: Array<{
    role: 'founder' | 'mag';
    body: string;
    createdAt: string;
    toolCalls?: ToolCallEntry[];
  }>;
  /** s172d — scan-agent investigation transcript. Each entry is one
   *  call mag made before composing the read. Empty when she
   *  answered from header counts alone. */
  toolCalls: ToolCallEntry[];
  /** s177 — when this scan was triggered off-cycle by the anomaly
   *  watcher, the hint that fired it. Null on regular cadence reads.
   *  The campaign page renders a "↘ anomaly: spike on
   *  wizard_started" eyebrow when set. */
  anomalyHint?: CampaignAnomalyHint | null;
}

/** Anomaly hint mirrored onto an insight when the scan was triggered
 *  off-cycle by the watcher (s177). Same shape as the schema-side
 *  campaignAnomalyHintSchema. */
export interface CampaignAnomalyHint {
  kind: 'spike' | 'drop' | 'shift' | 'funnel_break';
  /** s198 — optional now; analytics-event anomalies set these,
   *  paid-ad anomalies use the paid_ad block instead. */
  event_name?: string;
  current_count?: number;
  baseline_per_hour?: number;
  /** s198 — set when the anomaly was triggered by a paid-ad metrics
   *  movement (CTR drop, CPM spike, status flip). */
  paid_ad?: {
    deliverable_id: string;
    hook: string;
    metric: 'ctr' | 'cpm' | 'status';
    current_value: string;
    previous_value: string;
    multiplier: number;
  };
  observed_at: string;
  judge_reason: string;
}

/** One tool call mag made during an investigation (s172d). Mirrors
 *  the api's toolCallEntrySchema shape. */
export interface ToolCallEntry {
  id: string;
  name: string;
  arguments: Record<string, unknown>;
  result: string;
  isError: boolean;
  durationMs: number;
}

/** How often Mag scans this campaign on her own (s126). Wire shape
 *  matches the api's campaignScanCadenceSchema. */
export type CampaignScanCadence = 'off' | 'manual' | 'daily' | 'weekly' | 'auto';

/** One deliverable's last known serving state on its platform (spec
 *  da3515af). Hand-maintained twin of the api's
 *  DeliverableServingStatus. */
export interface DeliverableServingStatus {
  primaryStatus: string;
  reasons: string[];
  fetchedAt: string;
}

/** Spec 3c98725c — one reason a campaign cannot perform, and what it
 *  would take to fix it. Hand-maintained twin of the api's
 *  CampaignHealthFinding. */
export interface CampaignHealthFinding {
  kind: 'underbid_keyword';
  keyword: string;
  /** What the pause action addresses. */
  criterionId: string;
  currentBidUsd: number;
  firstPageCpcUsd: number;
  /** What the fix BUYS, not just what it costs. Null when the budget is
   *  unknown. Raising a bid without this number is advice that quietly
   *  trades clicks for position. */
  clicksPerDayAtTargetBid: number | null;
  dailyBudgetUsd: number;
}

/** Spec 97d4a11b — a keyword google has not served for weeks and has
 *  never priced. Hand-maintained twin of the api's DeadKeywordFinding.
 *
 *  Separate from CampaignHealthFinding, which is about a PRICE and
 *  carries bids and clicks-per-day. This is about there being no
 *  auction at any price, and its action is pause. */
export interface DeadKeywordFinding {
  kind: 'dead_keyword';
  keyword: string;
  criterionId: string;
  rarelyServedSince: string;
  daysRarelyServed: number;
}

export interface CampaignHealthReport {
  findings: CampaignHealthFinding[];
  /** Absent when the report was built without duration evidence —
   *  which is NOT the same as none found, and must not render as an
   *  all-clear. */
  deadKeywords?: DeadKeywordFinding[];
  /** The campaign was not examined at all — a mock launch, or paused.
   *  Distinct from an empty findings list, which means we looked and
   *  found nothing wrong. */
  skipped: boolean;
  skippedReason?: string;
}

/** Spec d3f0e475 — one keyword and what Google says about its ability to
 *  serve. Hand-maintained twin of the api's KeywordSnapshot, narrowed to
 *  what the panel renders.
 *
 *  ABSENCE IS THE SIGNAL on the estimates. Google OMITS a position estimate
 *  for a keyword it will not serve, so `firstPageCpcUsd` being undefined is
 *  not missing data — it is Google declining to price something it has no
 *  intention of showing. Rendering it as 0 or a bare dash would read as a
 *  cheap keyword, which is the opposite of what it means. */
export interface CampaignHealthKeyword {
  text: string;
  /** Google's own word: 'eligible', 'rarely_served', and so on. Shown as a
   *  FACT, never as a recommendation — see the T-1 note on the panel. */
  systemServingStatus?: string;
  status?: string;
  matchType?: string;
  criterionId?: string;
  effectiveCpcBidUsd?: number;
  firstPageCpcUsd?: number;
  topOfPageCpcUsd?: number;
}

/** The verdict AND the evidence behind it.
 *
 *  Findings alone let the panel say "every keyword here is priced to
 *  compete", which can be TRUE while most of the campaign's keywords are
 *  rarely served — priced-to-compete and served are different claims, and
 *  only the first is checked. The roster is what lets a founder see which
 *  keywords the sentence is about. */
export interface CampaignHealthEntry {
  report: CampaignHealthReport;
  /** Capped at 200 server-side, and the true total is NOT persisted — so
   *  anything counting these must phrase it against what was read. */
  keywords: CampaignHealthKeyword[];
}

export interface CampaignDetail extends CampaignSummary {
  /** Whether each launched paid deliverable is CAPABLE of performing,
   *  keyed by deliverable id. Detail endpoint only.
   *
   *  A deliverable ABSENT from this map has no stored evidence yet,
   *  which is not the same as a healthy one. */
  campaignHealth?: Record<string, CampaignHealthEntry>;
  /** Spec a41ace97 — the serving verdict per deliverable: one sentence plus
   *  the readings behind it. Distinct from campaignHealth, which is about
   *  bids. */
  campaignVerdicts?: Record<string, CampaignVerdict>;
  /** [spec ads/google-campaign-assets] What each launched deliverable is
   *  missing in sitelinks, snippets, callouts and images.
   *
   *  Keyed by deliverable, not by campaign: a deliverable is 1:1 with a
   *  google campaign and the stored snapshot covers only one of them, so a
   *  row-level number would be rendered against deliverables nobody looked
   *  at. `measured: false` is exactly that case and must render as this page
   *  rendered before the field existed. */
  assetCoverage?: Record<string, CampaignAssetCoverage>;
  /** Whether each launched paid deliverable is actually SERVING, keyed
   *  by deliverable id. Detail endpoint only.
   *
   *  A deliverable ABSENT from this map means no platform reading
   *  exists for it, which must render exactly as it did before this
   *  field existed — absent is "we didn't look", not "serving". */
  platformServingStatus?: Record<string, DeliverableServingStatus>;
  draft: AnyCampaignDraft | null;
  progress: CampaignProgress | null;
  launchChoice: string | null;
  failureReason: string | null;
  intakeMessages: IntakeMessage[];
  /** Plan-mode session 1 — multi-agent panel trail. Null on legacy
   *  one-shot drafts; populated when the campaign was drafted via
   *  /api/campaigns/draft-with-panel. */
  planningLog: CampaignPlanningLog | null;
  /** Plan-mode session 2 — refinement chat. Empty until the founder
   *  reacts to the panel draft via /api/campaigns/:id/refine. */
  refinementMessages: RefinementMessage[];
  kind: CampaignKind;
  /** Populated for kind='analytics_setup' on the detail fetch. Null/
   *  undefined for other kinds and for endpoints that don't attach it
   *  (intake POST, launch). */
  analyticsTracker?: AnalyticsTrackerContext | null;
  /** Recent proactive insights for analytics_setup launched campaigns,
   *  newest first. Empty array when scans haven't run yet; absent for
   *  other kinds + for endpoints that don't attach insights. */
  recentInsights?: CampaignInsight[];
  /** First-ever insight on this campaign — separate from recentInsights
   *  so it survives the latest-10 cap on long-running campaigns. Used
   *  by the launched page to anchor "since launch" diff chips
   *  (s127). Null when no scans have run yet. */
  baselineInsight?: CampaignInsight | null;
  /** Scan cadence (s126). Always present on the wire — defaults to
   *  'manual' for campaigns drafted before the cadence picker
   *  shipped. */
  scanCadence: CampaignScanCadence;
  /** When the next automatic scan is due. Null when cadence is
   *  manual/off (the scheduler doesn't enqueue those). */
  nextScanAt: string | null;
  /** s160 — mag's one-liner explaining the current nextScanAt. */
  nextScanReason: string | null;
  /** s163 — open mag_followups on this campaign, newest first. Lets
   *  the campaign page render an inline "what mag is chasing you on"
   *  panel with the same done/working/drop actions /home cards have.
   *  Absent on summary endpoints (list); populated only on detail. */
  openFollowups?: MagFollowup[];
  /** s164 — recently-closed followups (done + abandoned), newest first,
   *  capped server-side. Powers the history section on the campaign
   *  page. */
  recentClosedFollowups?: MagFollowup[];
  /** s171 — actual LLM cost mag has spent on this campaign so far
   *  (scans + followup extractions). Rendered on the campaign page
   *  next to the estimated monthly cost. */
  actualCostUsd?: number;
  /** Estimated monthly cost at the current cadence. 0 for off/manual,
   *  a real number for daily/weekly once at least one scan has run.
   *  Null when no scan history exists yet — the dashboard shows
   *  "first scan is free of estimate" until this is non-null. */
  estMonthlyScanCostUsd: number | null;
}

export interface CampaignListEnvelope {
  campaigns: CampaignSummary[];
  organizationId: string;
}

export async function fetchCampaigns(
  brandId?: string | null,
): Promise<CampaignListEnvelope | null> {
  // Unification roadmap Phase 3 — request the unified list (briefed
  // + imported) via origin=all. Older api builds that don't know
  // this query param just ignore it and return briefed-only, which
  // still renders correctly (dashboard treats every row as briefed
  // when origin is missing).
  const brandPart = brandQuery(brandId);
  const sep = brandPart === '' ? '?' : '&';
  const res = await fetch(`${env.API_URL}/api/campaigns${brandPart}${sep}origin=all`, {
    credentials: 'include',
  });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`GET /api/campaigns ${res.status}`);
  return (await res.json()) as CampaignListEnvelope;
}

/** s166 — all open followups for one brand, newest first. Powers the
 *  cross-campaign /followups page so the founder sees every open
 *  commitment in one view. */
export async function fetchBrandOpenFollowups(brandId: string): Promise<MagFollowup[]> {
  const res = await fetch(`${env.API_URL}/api/followups?brand=${encodeURIComponent(brandId)}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`GET /api/followups ${res.status}`);
  const json = (await res.json()) as { followups: MagFollowup[] };
  return json.followups;
}

export async function fetchCampaign(id: string): Promise<CampaignDetail | null> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${id}`, { credentials: 'include' });
  if (res.status === 401) return null;
  if (res.status === 404) return null;
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/campaigns/${id} ${res.status} ${detail}`);
  }
  const json = (await res.json()) as { campaign: CampaignDetail };
  return json.campaign;
}

// Campaign rollup (rollup feature phase 1 session 3). Read-only cache
// snapshot used by the campaign-detail page's rollup block.
export interface CampaignRollupPerDeliverable {
  deliverableId: string;
  kind: string;
  label: string;
  spendUsd: number;
  conversions: number;
  cpaUsd: number;
  impressions: number;
  /** Raw clicks. Optional — rows written before this field existed have
   *  none, and readers coalesce to 0 rather than reconstructing it from
   *  ctrPercent * impressions, which rounds. */
  clicks?: number;
  ctrPercent: number;
  lastMetricAt: string | null;
}
export interface CampaignRollupWindow {
  windowDays: number;
  computedAt: string;
  totalSpendUsd: number;
  totalConversions: number;
  metaSpendUsd: number;
  metaConversions: number;
  googleSpendUsd: number;
  googleConversions: number;
  /** Leading indicators. On a small search campaign these move DAYS
   *  before spend or conversions do, which is the whole reason they are
   *  here — a rollup carrying only trailing metrics reads as "nothing is
   *  happening" while the ad is serving. */
  totalImpressions: number;
  totalClicks: number;
  metaImpressions: number;
  metaClicks: number;
  googleImpressions: number;
  googleClicks: number;
  perDeliverable: CampaignRollupPerDeliverable[];
  /** Spec f510708e s1 — what the impressions and clicks MEAN, computed
   *  server-side so this card and mag's chat give one explanation of one
   *  campaign. `line` is null when the tiles already say it (a launched
   *  deliverable that has never reported). Optional because an older api
   *  build does not send it. */
  funnelRead?: {
    kind: 'not_reported' | 'too_early' | 'no_clicks' | 'clicking';
    line: string | null;
    impressions: number;
    clicks: number;
  };
  /** Spec 060bde1d s2 — the lever, priced. Null when there is nothing
   *  honest to propose: several deliverables carry a share, the campaign
   *  is not starved, or the loss is ad rank, which budget cannot fix. */
  budgetProposal?: {
    newDailyBudgetUsd: number;
    currentDailyBudgetUsd: number;
    deliverableId: string;
    line: string;
  } | null;
  /** Spec 5614cfab slice 2 — the other half of the lever, for campaigns
   *  losing auctions on rank rather than on budget. Mutually exclusive with
   *  budgetProposal by construction: at most one of the two is ever non-null.
   *  Optional because an older api build does not send it. */
  rewriteProposal?: {
    deliverableId: string;
    line: string;
  } | null;
}
export interface CampaignAttributionPolicy {
  model: 'last_click' | 'time_decay' | 'position_based' | 'linear';
  /** ISO timestamp Mag last evaluated the data shape (separate from
   *  decidedAt — Mag might have re-evaluated and held). Null when
   *  Mag has never run for this brand. */
  lastEvaluatedAt: string | null;
  /** ISO timestamp the current model was chosen / applied. Null for
   *  brands using the implicit default. */
  decidedAt: string | null;
  /** Mag's mag-voice explanation of the active model. Null for
   *  brands using the implicit default. */
  agentRationale: string | null;
  /** True when no brand_attribution_policy row exists yet — the
   *  rollup ran under the implicit last_click default. */
  isDefault: boolean;
}

/** [spec one-campaign-page-one-answer] One ranked answer for the campaign
 *  page, resolved server-side over the funnel lever, campaign health's
 *  findings and the serving state together.
 *
 *  It exists because three surfaces used to answer "what should i do?" with
 *  no knowledge of each other: MAG RECOMMENDS said "nothing for you to
 *  decide right now" while eight raise-bid buttons sat below it on a
 *  $5/day campaign whose cheapest keyword needed $1.29. */
export interface CampaignAdvice {
  top: {
    kind:
      | 'budget_cannot_test'
      | 'not_serving'
      | 'funnel_lever'
      | 'bidding_promotable'
      | 'priced_bids'
      // The three EMPTY shapes. Which one is used depends on who the page is
      // talking to: under autopilot mag acts, so "nothing for YOU to decide"
      // is true and useless (spec 4758bfde).
      | 'autopilot_next'
      | 'autopilot_waiting'
      | 'nothing';
    line: string;
    count?: number;
    /** `autopilot_next` only — when mag acts. Until then the founder can
     *  still stop it, which is why this is shown rather than just notified. */
    executeAt?: string;
  };
  /** Candidates the top answer outranked, each with the reason it lost.
   *  Rendered as EVIDENCE, never dropped — a suppressed finding is still
   *  true, and hiding it would trade a contradiction for a blind spot. */
  suppressed: Array<{
    candidate: { kind: string; line: string; count?: number };
    reason: string;
  }>;
}

export interface CampaignRollup {
  campaignId: string;
  campaignName: string | null;
  windows: CampaignRollupWindow[];
  /** Absent on an older API than the one that computes it. */
  advice?: CampaignAdvice;
  /** Phase 2.5 — brand's active attribution policy. Always present;
   *  isDefault=true means we're showing the default last_click. */
  attributionPolicy: CampaignAttributionPolicy;
  v1Disclosures: string[];
}

export async function fetchCampaignRollup(id: string): Promise<CampaignRollup | null> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${id}/rollup`, { credentials: 'include' });
  if (res.status === 401 || res.status === 404) return null;
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/campaigns/${id}/rollup ${res.status} ${detail}`);
  }
  return (await res.json()) as CampaignRollup;
}

export async function draftCampaign(
  brief: string,
  brandId?: string | null,
): Promise<CampaignDetail> {
  const res = await fetch(`${env.API_URL}/api/campaigns/draft${brandQuery(brandId)}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ brief }),
  });
  if (!res.ok) {
    const body = await res
      .json()
      .catch(() => ({ detail: `POST /api/campaigns/draft ${res.status}` }));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /api/campaigns/draft ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { campaign: CampaignDetail };
  return json.campaign;
}

/** Plan-mode (session 1) — kicks off a full panel draft: drafter →
 *  3 critic personas in parallel → synthesizer. Synchronous from the
 *  client's POV (~90s) so when the promise resolves, the campaign
 *  has draft + planning_log populated and status='ready'. */
export async function draftCampaignWithPanel(
  brief: string,
  brandId?: string | null,
): Promise<CampaignDetail> {
  const res = await fetch(`${env.API_URL}/api/campaigns/draft-with-panel${brandQuery(brandId)}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ brief }),
  });
  if (!res.ok) {
    const body = await res
      .json()
      .catch(() => ({ detail: `POST /api/campaigns/draft-with-panel ${res.status}` }));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /api/campaigns/draft-with-panel ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { campaign: CampaignDetail };
  return json.campaign;
}

/** Plan-mode (session 2) — appends one founder turn + runs the
 *  refiner. Returns the full updated campaign so the route can
 *  re-render without a follow-up fetch. */
export async function refineCampaign(
  id: string,
  body: string,
): Promise<{ campaign: CampaignDetail; lastTurnCostUsd: number }> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${id}/refine`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body }),
  });
  if (!res.ok) {
    const json = await res
      .json()
      .catch(() => ({ detail: `POST /api/campaigns/${id}/refine ${res.status}` }));
    const detail =
      typeof json === 'object' && json && 'detail' in json
        ? String((json as { detail: unknown }).detail)
        : `POST /api/campaigns/${id}/refine ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as { campaign: CampaignDetail; lastTurnCostUsd: number };
}

export async function deleteCampaign(id: string): Promise<void> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const json = await res
      .json()
      .catch(() => ({ detail: `DELETE /api/campaigns/${id} ${res.status}` }));
    const detail =
      typeof json === 'object' && json && 'detail' in json
        ? String((json as { detail: unknown }).detail)
        : `DELETE /api/campaigns/${id} ${res.status}`;
    throw new Error(detail);
  }
}

/** [s379] Streaming twin of postIntakeTurn. Mag's reply arrives as
 *  text_chunk deltas while she writes it; the resolved CampaignDetail
 *  is the same row the JSON endpoint returns.
 *
 *  Callers must tolerate ZERO deltas: a provider without streaming
 *  falls back to a buffered call server-side, and the founder simply
 *  sees the old thinking-dots until the reply lands. */
export async function postIntakeTurnStreaming(
  id: string,
  body: string,
  onEvent: (event: ChatStreamEvent) => void,
): Promise<CampaignDetail> {
  return runChatStream(
    `${env.API_URL}/api/campaigns/${encodeURIComponent(id)}/intake/stream`,
    { body },
    'campaign',
    (payload) => (payload as { campaign: CampaignDetail }).campaign,
    onEvent,
  );
}

/** [spec eebec8e1] Cancel a running panel draft. The campaign goes back to
 *  status='intake' — the brief chat, with the thread intact — so the founder
 *  can redirect mag instead of watching a plan they already know is wrong.
 *
 *  The stage the panel is in when this lands still finishes and still bills;
 *  the cancel is cooperative, not an abort. Copy shown to the founder must
 *  say so. */
export async function cancelCampaignDraft(id: string): Promise<CampaignDetail> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${id}/cancel-draft`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    // `unknown`, not the implicit `any` res.json() hands back — the narrowing
    // two lines down is the only thing that should decide this shape.
    const json: unknown = await res
      .json()
      .catch(() => ({ detail: `POST /api/campaigns/${id}/cancel-draft ${res.status}` }));
    const detail =
      typeof json === 'object' && json && 'detail' in json
        ? String((json as { detail: unknown }).detail)
        : `POST /api/campaigns/${id}/cancel-draft ${res.status}`;
    throw new Error(detail);
  }
  const out = (await res.json()) as { campaign: CampaignDetail };
  return out.campaign;
}

export async function postIntakeTurn(id: string, body: string): Promise<CampaignDetail> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${id}/intake`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body }),
  });
  if (!res.ok) {
    const json = await res
      .json()
      .catch(() => ({ detail: `POST /api/campaigns/${id}/intake ${res.status}` }));
    const detail =
      typeof json === 'object' && json && 'detail' in json
        ? String((json as { detail: unknown }).detail)
        : `POST /api/campaigns/${id}/intake ${res.status}`;
    throw new Error(detail);
  }
  const out = (await res.json()) as { campaign: CampaignDetail };
  return out.campaign;
}

export async function launchCampaign(id: string, choice: string): Promise<CampaignDetail> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${id}/launch`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ choice }),
  });
  if (!res.ok) {
    const body = await res
      .json()
      .catch(() => ({ detail: `POST /api/campaigns/${id}/launch ${res.status}` }));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /api/campaigns/${id}/launch ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { campaign: CampaignDetail };
  return json.campaign;
}

/** Publish one deliverable on a launched campaign (s183 — mock
 *  publish only; s184 swaps in real Meta Graph API calls). Returns
 *  the updated campaign so callers can rebind from the response
 *  without a follow-up GET. */
export async function publishDeliverable(
  campaignId: string,
  deliverableId: string,
): Promise<CampaignDetail> {
  const res = await fetch(
    `${env.API_URL}/api/campaigns/${campaignId}/deliverables/${encodeURIComponent(deliverableId)}/publish`,
    { method: 'POST', credentials: 'include' },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: `publish ${res.status}` }));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `publish ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { campaign: CampaignDetail };
  return json.campaign;
}

/** Trigger the meta-paid-strategy agent on a creative_ads campaign.
 *  Stores 3-5 angle proposals on campaign.draft.paidStrategy.
 *  Re-running overwrites. ~$0.12/run. (autopilot phase A5) */
export interface MetaPaidStrategyResultDTO {
  campaignId: string;
  angleCount: number;
  costUsd: number;
  durationMs: number;
}

export async function triggerMetaPaidStrategy(
  campaignId: string,
): Promise<MetaPaidStrategyResultDTO> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${campaignId}/paid-strategy`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = body.detail ?? body.error ?? `paid strategy ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as MetaPaidStrategyResultDTO;
}

/** Trigger the google-paid-strategy agent. Sibling of
 *  triggerMetaPaidStrategy. Result lands on
 *  campaign.draft.googlePaidStrategy. (autopilot phase A6) */
export async function triggerGooglePaidStrategy(
  campaignId: string,
): Promise<MetaPaidStrategyResultDTO> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${campaignId}/google-strategy`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = body.detail ?? body.error ?? `google strategy ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as MetaPaidStrategyResultDTO;
}

/** cap2 — trigger per-day rolling paid schedule proposer for a
 *  channel on a creative_ads campaign. Lands on
 *  draft.paidSchedule (meta_paid) or draft.googlePaidSchedule
 *  (google_paid). Daily applier picks up today's row on the
 *  next 6h tick. Re-running overwrites. ~$0.10/run. */
export interface PaidScheduleResultDTO {
  campaignId: string;
  channel: 'meta_paid' | 'google_paid';
  daysGenerated: number;
  costUsd: number;
  durationMs: number;
}

export async function triggerPaidScheduleDraft(
  campaignId: string,
  channel: 'meta_paid' | 'google_paid',
): Promise<PaidScheduleResultDTO> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${campaignId}/paid-schedule`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ channel }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = body.detail ?? body.error ?? `paid schedule ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as PaidScheduleResultDTO;
}

/** Promote an organic boost candidate to a paid ad (agency C3).
 *  targetCampaignId set = append to existing creative_ads campaign.
 *  Null = create a new boost campaign with newCampaignName. */
export interface BoostPromoteResultDTO {
  campaignId: string;
  deliverableId: string;
  createdNewCampaign: boolean;
}

export async function promoteBoostCandidate(
  cardId: string,
  body: { targetCampaignId: string | null; newCampaignName?: string },
): Promise<BoostPromoteResultDTO> {
  const res = await fetch(
    `${env.API_URL}/api/feed/meta-engagement-digest/${encodeURIComponent(cardId)}/boost`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = errBody.detail ?? errBody.error ?? `boost promote ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as BoostPromoteResultDTO;
}

// ───── Cross-channel budget proposal (autopilot A7) ──────────────────

/** Outcome row returned from apply — one per adjustment. */
export interface CrossChannelBudgetApplyOutcomeDTO {
  campaignId: string;
  deliverableId: string;
  outcome: 'ok' | 'skipped_concurrent_edit' | 'skipped_not_launched' | 'failed';
  detail?: string | undefined;
}

export interface CrossChannelBudgetApplyResultDTO {
  outcome: 'applied' | 'hold_nothing_to_apply';
  cardId?: string;
  applyOutcomes?: CrossChannelBudgetApplyOutcomeDTO[];
}

export async function applyCrossChannelBudgetProposal(
  cardId: string,
): Promise<CrossChannelBudgetApplyResultDTO> {
  const res = await fetch(
    `${env.API_URL}/api/feed/cross-channel-budget-proposal/${encodeURIComponent(cardId)}/apply`,
    {
      method: 'POST',
      credentials: 'include',
    },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = body.detail ?? body.error ?? `cross-channel-budget apply ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as CrossChannelBudgetApplyResultDTO;
}

export async function dismissCrossChannelBudgetProposal(cardId: string): Promise<void> {
  const res = await fetch(
    `${env.API_URL}/api/feed/cross-channel-budget-proposal/${encodeURIComponent(cardId)}/dismiss`,
    {
      method: 'POST',
      credentials: 'include',
    },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = body.detail ?? body.error ?? `cross-channel-budget dismiss ${res.status}`;
    throw new Error(detail);
  }
}

// ───── Attribution policy proposal (rollup phase 2.5 session 6) ──────

export interface AttributionPolicyApplyResultDTO {
  outcome: 'applied';
  cardId: string;
  newModel: 'last_click' | 'time_decay';
  previousModel: 'last_click' | 'time_decay';
}

export async function applyAttributionPolicyProposal(
  cardId: string,
): Promise<AttributionPolicyApplyResultDTO> {
  const res = await fetch(
    `${env.API_URL}/api/feed/attribution-policy-proposal/${encodeURIComponent(cardId)}/apply`,
    {
      method: 'POST',
      credentials: 'include',
    },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = body.detail ?? body.error ?? `attribution-policy apply ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as AttributionPolicyApplyResultDTO;
}

export async function dismissAttributionPolicyProposal(cardId: string): Promise<void> {
  const res = await fetch(
    `${env.API_URL}/api/feed/attribution-policy-proposal/${encodeURIComponent(cardId)}/dismiss`,
    {
      method: 'POST',
      credentials: 'include',
    },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = body.detail ?? body.error ?? `attribution-policy dismiss ${res.status}`;
    throw new Error(detail);
  }
}

// ───── Paid strategy refresh proposal (A5/A6-rotate) ─────────────────

export interface PaidStrategyRefreshAcceptResultDTO {
  outcome: 'accepted';
  cardId: string;
  channel: 'meta_paid' | 'google_paid';
  newStrategyGeneratedAt: string;
  costUsd: number;
}

export async function acceptPaidStrategyRefresh(
  cardId: string,
): Promise<PaidStrategyRefreshAcceptResultDTO> {
  const res = await fetch(
    `${env.API_URL}/api/feed/paid-strategy-refresh-proposal/${encodeURIComponent(cardId)}/accept`,
    { method: 'POST', credentials: 'include' },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = body.detail ?? body.error ?? `paid-strategy-refresh accept ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as PaidStrategyRefreshAcceptResultDTO;
}

export async function dismissPaidStrategyRefresh(cardId: string): Promise<void> {
  const res = await fetch(
    `${env.API_URL}/api/feed/paid-strategy-refresh-proposal/${encodeURIComponent(cardId)}/dismiss`,
    { method: 'POST', credentials: 'include' },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = body.detail ?? body.error ?? `paid-strategy-refresh dismiss ${res.status}`;
    throw new Error(detail);
  }
}

/** Per-campaign action-log entry (phase A8; actor axis from spec
 *  1e20a044). Read-only feed of every action taken on this campaign by
 *  EITHER party — it began as an autonomous-only log and the route name
 *  still says autopilot. payload shape varies by actionKind. */
export interface AutopilotAuditEntryDTO {
  id: string;
  decisionType: string;
  actionKind: string;
  payload: Record<string, unknown>;
  succeeded: boolean;
  createdAt: string;
  /** 'mag' | 'founder'. Widened to string because the api owns the
   *  vocabulary and an unknown value must render, not crash. */
  actor: string;
  /** Who to name for a founder action — 'you' when the viewer is the
   *  actor, their name when it was a teammate, null when mag acted.
   *  Resolved server-side: only the api knows who is reading. */
  actorLabel: string | null;
  /** The feed card whose approval authorised this action. Null means
   *  standing authority, not missing provenance. */
  sourceFeedCardId: string | null;
}

// [spec preflight-the-plan] What the founder is about to buy, priced BEFORE
// they approve it: per keyword, whether anybody searches for it in each of the
// brand's markets, and whether the stated budget can reach a readable sample.
//
// NO FIELD HERE IS A CLICK PRICE. Theory 5ea7207 measured Keyword Planner's
// suggested bid at 8-24x the auction's own first-page estimate, so cost arrives
// as a coarse `tier` beside google's categorical `competition` value, never as
// dollars. The only money figure is `breakEvenCpcUsd`, which is arithmetic on
// the budget and the calendar.
export type PreflightVolumeBucket =
  | 'zero'
  | 'ten'
  | 'hundred'
  | 'thousand'
  | 'ten_thousand'
  | 'hundred_thousand'
  | 'million';
export type PreflightCompetition = 'low' | 'medium' | 'high' | 'unknown';
export type PreflightCostTier = 'cents' | 'dollars' | 'expensive' | 'unknown';
export type PreflightShape = 'workable' | 'thin' | 'cannot_test' | 'unknown';

export interface PreflightKeywordDTO {
  keyword: string;
  /** null means google priced nothing for this term — NOT that demand is zero. */
  volumeBucket: PreflightVolumeBucket | null;
  volumeMidpoint: number | null;
  competition: PreflightCompetition | null;
  /** null when volume is unknown; never false-by-default. */
  clearsFloor: boolean | null;
  tier: PreflightCostTier;
}

export interface PreflightMarketDTO {
  country: string;
  language: string;
  volumeFloor: number;
  /** 'ok', or the typed reason the research came back empty. A market we could
   *  not check must never render as a market with no demand. */
  outcome: string;
  keywords: PreflightKeywordDTO[];
  withVolume: number;
  checked: number;
  total: number;
  truncated: boolean;
}

export interface PreflightVerdictDTO {
  shape: PreflightShape;
  /** Why a thin verdict is thin: 'demand' (the words are barely searched) or
   *  'budget' (there is demand, in auctions this budget cannot enter). The two
   *  need different sentences, so the surface branches on it. */
  thinReason: 'demand' | 'budget' | null;
  dailyBudgetUsd: number;
  targetClicks: number;
  targetDays: number;
  /** The only money figure here, and it is arithmetic: what a click must cost
   *  for this budget to buy targetClicks of them in targetDays. */
  breakEvenCpcUsd: number;
  /** The strongest SINGLE market, never a sum across markets — a campaign
   *  serves one auction at a time, and summing six sparse markets once made
   *  this panel say "this can run" about a campaign that could not. */
  bestMarket: { country: string; withVolume: number; checked: number } | null;
  expensiveWithVolume: number;
  checkedMarkets: number;
  unknownReasons: Array<{ country: string; outcome: string }>;
}

export interface CampaignPreflightDTO {
  deliverableId: string;
  dailyBudgetUsd: number;
  markets: PreflightMarketDTO[];
  verdict: PreflightVerdictDTO;
}

// [spec preflight-the-plan S3] WHERE A LAUNCHED CAMPAIGN ACTUALLY RUNS, read
// off its own LOCATION criteria rather than inferred from the brand's markets —
// the two differ, and inferring is what mag's rollup tool warns against.
//
// THREE STATES, AND THEY MUST NOT COLLAPSE:
//   countries: ['US']  — targeted, fine
//   countries: []      — NO location criterion, which on google means WORLDWIDE
//   countries: null    — we could not look (failed: true)
// An empty array rendered as "unknown" hides the expensive case; a failed read
// rendered as worldwide invents an alarm.
export interface CampaignGeoDTO {
  deliverableId: string;
  countries: string[] | null;
  failed: boolean;
}

// [spec preflight-the-plan S4] What people actually typed, last 30 days, by
// spend. The read the product never had: fiscalo's paused campaign spent $40.47
// on 34 clicks from people looking for the tax authority's own portal, and
// nothing could see it.
//
// `terms: null` means we could not look; an empty ARRAY means google reported
// nothing, which on a campaign that has served is itself a finding.
export interface SearchTermDTO {
  text: string;
  adGroupName: string | null;
  impressions: number;
  clicks: number;
  costUsd: number;
  conversions: number;
}

export interface CampaignSearchTermsDTO {
  deliverableId: string;
  terms: SearchTermDTO[] | null;
  failed: boolean;
}

export async function getCampaignSearchTerms(
  campaignId: string,
): Promise<CampaignSearchTermsDTO[]> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${campaignId}/search-terms`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    throw new Error(body.detail ?? body.error ?? `search terms ${res.status}`);
  }
  const json = (await res.json()) as { deliverables: CampaignSearchTermsDTO[] };
  return json.deliverables;
}

export async function getCampaignGeo(campaignId: string): Promise<CampaignGeoDTO[]> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${campaignId}/geo`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    throw new Error(body.detail ?? body.error ?? `campaign geo ${res.status}`);
  }
  const json = (await res.json()) as { deliverables: CampaignGeoDTO[] };
  return json.deliverables;
}

export async function getCampaignPreflight(campaignId: string): Promise<CampaignPreflightDTO[]> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${campaignId}/preflight`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = body.detail ?? body.error ?? `preflight ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { deliverables: CampaignPreflightDTO[] };
  return json.deliverables;
}

export async function fetchCampaignAutopilotAudit(
  campaignId: string,
): Promise<AutopilotAuditEntryDTO[]> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${campaignId}/autopilot-audit`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = body.detail ?? body.error ?? `autopilot audit ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { entries: AutopilotAuditEntryDTO[] };
  return json.entries;
}

/** [12.2L-100] Brand-wide receipt feed. Joins each audit row with its
 *  campaign so the home card can render "what mag shipped" across
 *  every autopilot campaign in one round-trip. */
export interface BrandAutopilotEntryDTO {
  id: string;
  campaignId: string;
  campaignName: string | null;
  campaignOrigin: CampaignOrigin;
  actionKind: string;
  payload: Record<string, unknown>;
  succeeded: boolean;
  createdAt: string;
}

/** [12.2L-101] Wire shape of the brand autopilot feed. Includes the
 *  next scheduler tick so the home card can render a "next pass in
 *  ~Xh" line without a second round-trip. */
export interface BrandAutopilotRecent {
  entries: BrandAutopilotEntryDTO[];
  /** ISO timestamp of the next brand-coach tick (= when autopilot
   *  will next walk every brand and act). Null when the scheduler is
   *  disabled or stopped. */
  nextTickAt: string | null;
}

export async function fetchBrandAutopilotRecent(
  brandId: string,
  limit = 10,
): Promise<BrandAutopilotRecent> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/autopilot-recent?limit=${limit}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = body.detail ?? body.error ?? `autopilot recent ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as BrandAutopilotRecent;
}

// ─── Home briefing ─────────────────────────────────────────────────
// The 1:1-shaped /home surface backed by mag_briefing_cards. One
// fetch per brand returns three story-card sections + counts.

export interface BriefingCardDTO {
  id: string;
  channel: 'google_paid' | 'meta_paid' | 'tiktok_paid' | 'meta_organic' | 'cross_channel';
  kind: 'action_outcome' | 'planned' | 'needs_eye';
  verb: string;
  targetLabel: string;
  rationale: string;
  outcomeState: 'too_early' | 'positive' | 'negative' | 'neutral' | 'superseded' | null;
  outcomeText: string | null;
  outcomeSettledAt: string | null;
  actedAt: string;
  /** Deep-link target for needs_eye cards (null for other kinds). */
  importedCampaignId: string | null;
}

export interface BriefingSpendDTO {
  metaUsd: number;
  googleUsd: number;
  totalUsd: number;
  hasData: boolean;
}

/** 54a/N — trailing-30d performance rollup, one shape per platform.
 *  Null entries mean "no data" (fresh brand, no spend yet, no posts
 *  shipped, etc.) — the dashboard renders an honest empty state. */
export interface HomePerformanceMetaPaid {
  spendUsd: number;
  impressions: number;
  clicks: number;
  conversions: number;
  cpaUsd: number | null;
}

export interface HomePerformanceGooglePaid extends HomePerformanceMetaPaid {}

export interface HomePerformanceInstagram {
  postsLast30d: number;
  totalEngagement: number;
  topPost: { slotId: string; hook: string; engagement: number } | null;
}

export interface HomePerformanceDTO {
  brandId: string;
  instagram: HomePerformanceInstagram | null;
  metaAds: HomePerformanceMetaPaid | null;
  googleAds: HomePerformanceGooglePaid | null;
}

export async function fetchHomePerformance(brandId: string): Promise<HomePerformanceDTO> {
  const res = await fetch(`${env.API_URL}/api/home/performance?brandId=${brandId}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/home/performance ${res.status} ${detail}`);
  }
  return (await res.json()) as HomePerformanceDTO;
}

export interface HomeBriefingDTO {
  brandId: string;
  /** Null when the brand has no paid spend in the trailing 30d window
   *  — the renderer hides the rollup line entirely in that case. */
  spend: BriefingSpendDTO | null;
  briefing: BriefingCardDTO[];
  planned: BriefingCardDTO[];
  needsEye: BriefingCardDTO[];
  needsEyeMoreCount: number;
  moreThisWeekCount: number;
}

export async function fetchHomeBriefing(brandId: string): Promise<HomeBriefingDTO> {
  const res = await fetch(`${env.API_URL}/api/home/briefing?brandId=${brandId}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET briefing ${res.status} ${detail}`);
  }
  return (await res.json()) as HomeBriefingDTO;
}

/** Approve an autopilot-drafted google_search_ad deliverable.
 *  Clears autopilot_drafted_at so A4 google autolaunch picks it up
 *  on its next tick. (autopilot phase A6-finish) */
export async function approveAutodraft(
  campaignId: string,
  deliverableId: string,
): Promise<CampaignDetail | null> {
  const res = await fetch(
    `${env.API_URL}/api/campaigns/${campaignId}/deliverables/${encodeURIComponent(deliverableId)}/approve-autodraft`,
    { method: 'POST', credentials: 'include' },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = body.detail ?? body.error ?? `approve-autodraft ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { campaign: CampaignDetail | null };
  return json.campaign;
}

/** Resume an autopilot-paused paid deliverable. Founder-driven —
 *  clears the auto_paused_at audit + resumes the campaign on
 *  Meta/Google's side. (autopilot phase A3b-finish) */
export async function unpauseDeliverable(
  campaignId: string,
  deliverableId: string,
): Promise<CampaignDetail | null> {
  const res = await fetch(
    `${env.API_URL}/api/campaigns/${campaignId}/deliverables/${encodeURIComponent(deliverableId)}/unpause`,
    { method: 'POST', credentials: 'include' },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = body.detail ?? body.error ?? `unpause ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { campaign: CampaignDetail | null };
  return json.campaign;
}

export interface CampaignPauseOutcome {
  campaign: CampaignDetail | null;
  pausedCount: number;
  /** Non-empty means the campaign was NOT marked paused and mag was NOT
   *  switched off, because these ads are still serving. */
  stillRunning: string[];
  campaignPaused: boolean;
}

/** Pause a whole campaign: every running ad, then mag herself.
 *
 *  A partial result comes back 200, not as an error — the ads that stopped
 *  really stopped. Read `campaignPaused` rather than assuming success means a
 *  clean sweep. */
export async function pauseCampaign(id: string, reason?: string): Promise<CampaignPauseOutcome> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${id}/pause`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(reason ? { reason } : {}),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    throw new Error(body.detail ?? body.error ?? `pause campaign ${res.status}`);
  }
  return (await res.json()) as CampaignPauseOutcome;
}

export interface CampaignResumeOutcome {
  campaign: CampaignDetail | null;
  resumedCount: number;
  stillPaused: string[];
}

/** Put a paused campaign back: resume the ads, restore mag's cadence. */
export async function resumeCampaign(id: string): Promise<CampaignResumeOutcome> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${id}/resume`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    throw new Error(body.detail ?? body.error ?? `resume campaign ${res.status}`);
  }
  return (await res.json()) as CampaignResumeOutcome;
}

/** Founder-initiated pause of a launched paid deliverable.
 *
 *  `platformDispatched` is the field that matters: a brand with no live
 *  platform connection still gets its local state stamped, and the
 *  caller has to say so rather than claim the ad stopped on google. */
export async function pauseDeliverable(
  campaignId: string,
  deliverableId: string,
  reason?: string,
): Promise<{ campaign: CampaignDetail | null; platformDispatched: boolean }> {
  const res = await fetch(
    `${env.API_URL}/api/campaigns/${campaignId}/deliverables/${encodeURIComponent(deliverableId)}/pause`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(reason ? { reason } : {}),
    },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = body.detail ?? body.error ?? `pause ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as {
    campaign: CampaignDetail | null;
    platformDispatched: boolean;
  };
  return { campaign: json.campaign, platformDispatched: json.platformDispatched };
}

/** s197 — one snapshot in the metrics history series. Returned by
 *  GET /campaigns/:id/deliverables/:did/metrics-history. The campaign
 *  detail page renders these as a small trend chart under each
 *  launched paid-ad tile. */
export interface MetaPaidAdMetricsHistoryPoint {
  id: string;
  fetchedAt: string;
  fetchedVia: 'mock' | 'meta';
  impressions: number;
  clicks: number;
  reach: number;
  spendUsd: number;
  ctrPercent: number;
  cpmUsd: number;
  status: string;
}

export async function fetchMetaPaidAdMetricsHistory(
  campaignId: string,
  deliverableId: string,
): Promise<MetaPaidAdMetricsHistoryPoint[]> {
  const res = await fetch(
    `${env.API_URL}/api/campaigns/${campaignId}/deliverables/${encodeURIComponent(deliverableId)}/metrics-history`,
    { credentials: 'include' },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: `history ${res.status}` }));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `history ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { history: MetaPaidAdMetricsHistoryPoint[] };
  return json.history;
}

/** Ask the platform, right now, whether a launched google_search_ad is
 *  actually serving — the button behind "check now".
 *
 *  Same route as the meta refresh; the api branches on the deliverable's
 *  kind. Returns the campaign with a FRESH platformServingStatus, so the
 *  caller can render the new pill without a second fetch. */
/** Apply one fix the campaign-health panel offered (spec 3c98725c).
 *
 *  Returns the campaign with FRESH health, because the api re-reads
 *  from google after the write — so the panel re-renders from what is
 *  true rather than from what we hoped the click did. */
/** Spec 060bde1d s2 — the founder's click on the funnel card's lever.
 *
 *  A sibling of applyCampaignHealthAction, and deliberately its own
 *  endpoint: on the server it writes through the same function mag's
 *  adjust_campaign_budget tool uses, so the button and the conversation
 *  cannot diverge. */
export async function applyFunnelBudgetRaise(
  campaignId: string,
  deliverableId: string,
  newDailyBudgetUsd: number,
): Promise<{ outcome: string }> {
  const res = await fetch(
    `${env.API_URL}/api/campaigns/${campaignId}/deliverables/${encodeURIComponent(deliverableId)}/funnel-action`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'raise_daily_budget', newDailyBudgetUsd }),
    },
  );
  if (!res.ok) {
    const detail = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(detail?.error ?? 'funnel action failed');
  }
  return (await res.json()) as { outcome: string };
}

export async function applyCampaignHealthAction(
  campaignId: string,
  deliverableId: string,
  action:
    | { action: 'raise_bid'; cpcBidUsd: number }
    | { action: 'pause_keyword'; criterionId: string },
): Promise<CampaignDetail> {
  const res = await fetch(
    `${env.API_URL}/api/campaigns/${campaignId}/deliverables/${encodeURIComponent(deliverableId)}/health-action`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(action),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: `action ${res.status}` }));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `action ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { campaign: CampaignDetail };
  return json.campaign;
}

export async function refreshGoogleSearchAdStatus(
  campaignId: string,
  deliverableId: string,
): Promise<CampaignDetail> {
  return refreshMetaPaidAdMetrics(campaignId, deliverableId);
}

/** Refresh metrics for a launched meta_paid_ad deliverable (s194).
 *  Hits Meta's Insights API (or the mock client when env-gated off)
 *  and persists the snapshot on the deliverable. The metrics live
 *  on the returned campaign's deliverable. */
export async function refreshMetaPaidAdMetrics(
  campaignId: string,
  deliverableId: string,
): Promise<CampaignDetail> {
  const res = await fetch(
    `${env.API_URL}/api/campaigns/${campaignId}/deliverables/${encodeURIComponent(deliverableId)}/refresh-metrics`,
    { method: 'POST', credentials: 'include' },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: `refresh ${res.status}` }));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `refresh ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { campaign: CampaignDetail };
  return json.campaign;
}

// ─── Per-brand Meta connection (s191) ───────────────────────────

export type MetaScopeBundle = 'meta_organic' | 'meta_paid';

export interface BrandMetaConnectionDTO {
  brandId: string;
  instagramBusinessId: string;
  connectedPageName: string;
  status: 'active' | 'expired' | 'disconnected';
  pageTokenExpiresAt: string;
  connectedAt: string;
  /** s192 — true when the connection has a stored user token (which
   *  is what /me/adaccounts needs). Old connections from s191 won't
   *  have one; the dashboard offers a "reconnect to enable ad
   *  accounts" prompt in that case. */
  hasUserToken: boolean;
  /** s192 — the founder's picked ad account. Null until they run
   *  the picker. */
  metaAdAccountId: string | null;
  metaAdAccountName: string | null;
  /** s236-2 — raw granted scope list from Meta's /me/permissions.
   *  Lets the dashboard render per-scope detail if needed without
   *  pulling the bundle definitions onto the client. Older
   *  connections that pre-date s236 are backfilled to the full
   *  bundle set in migration 0094. */
  grantedScopes: string[];
  /** s236-2 — derived list of bundles the founder has fully
   *  granted. The dashboard renders per-bundle tick/cross UI off
   *  this. */
  activeBundles: MetaScopeBundle[];
}

/** s192 — one ad account as returned by Graph's /me/adaccounts. */
export interface MetaAdAccountDTO {
  id: string;
  name: string;
  currency: string;
  accountStatus: number;
}

export async function fetchMetaConnection(brandId: string): Promise<BrandMetaConnectionDTO | null> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/meta/connection`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`GET meta/connection ${res.status}`);
  const json = (await res.json()) as { connection: BrandMetaConnectionDTO | null };
  return json.connection;
}

export async function disconnectMeta(
  brandId: string,
  opts: { deleteSlots?: boolean } = {},
): Promise<{ slotsDeleted: number }> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/meta/disconnect`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ deleteSlots: opts.deleteSlots ?? false }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: `disconnect ${res.status}` }));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `disconnect ${res.status}`;
    throw new Error(detail);
  }
  const body = await res.json().catch(() => ({ slotsDeleted: 0 }));
  const slotsDeleted =
    typeof body === 'object' && body && 'slotsDeleted' in body
      ? Number((body as { slotsDeleted: unknown }).slotsDeleted) || 0
      : 0;
  return { slotsDeleted };
}

export interface MetaHistoryLearnResultDTO {
  feedCardId?: string;
  postsAnalyzed: number;
  costUsd: number;
  durationMs: number;
}

/** Trigger the one-shot history-learn pass for a brand. Mag walks the
 *  IG back-catalogue via the Graph API, runs the voice-learn agent,
 *  auto-applies the resulting voice patterns to the brand brain, and
 *  lands a `meta_history_learn` feed card the founder can review on
 *  /home. Surface from the Meta connection card on /settings + as a
 *  "re-learn" button on the feed card itself. */
export async function triggerMetaHistoryLearn(brandId: string): Promise<MetaHistoryLearnResultDTO> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/meta/learn-history`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = body.detail ?? body.error ?? `learn-history ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as MetaHistoryLearnResultDTO;
}

export interface MetaContentPlanRequestDTO {
  /** Plan length in days. Defaults server-side to 30. */
  planDays?: number;
  /** Target posts per week. Defaults server-side to 3. */
  postsPerWeek?: number;
}

export interface MetaContentPlanResultDTO {
  feedCardId?: string;
  postsScheduled: number;
  costUsd: number;
  durationMs: number;
}

/** Trigger a fresh content-plan generation for a brand. Mag drafts
 *  a 15- or 30-day rolling schedule from brain + latest engagement
 *  digest. Lands a meta_content_plan feed card the founder reviews
 *  on /home. */
export async function triggerMetaContentPlan(
  brandId: string,
  body: MetaContentPlanRequestDTO = {},
): Promise<MetaContentPlanResultDTO> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/meta/content-plan`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = errBody.detail ?? errBody.error ?? `content-plan ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as MetaContentPlanResultDTO;
}

export interface MetaPostDraftResultDTO {
  slotId: string;
  costUsd: number;
  durationMs: number;
}

export interface MetaSlotImageResultDTO {
  slotId: string;
  imageUrl: string;
  costUsd: number;
}

/** s233.5 — phase B v2 #1. Returned by triggerMetaSlotVideo when the
 *  pipeline finishes rendering an mp4 for a reel / video slot. The
 *  creativeAssetId is the link Phase B v2 will join on later to
 *  attribute IG engagement to the duration mag chose. */
export interface MetaSlotVideoResultDTO {
  slotId: string;
  videoUrl: string;
  videoDurationSeconds: number;
  creativeAssetId: string;
  costUsd: number;
}

/** [12.2L-169] Per-channel trust gate. Filled by the API on the
 *  publish lane of each channel (the highest-stakes decision in the
 *  bundle). When eligible=false, the autopilot toggle MUST render
 *  disabled with the mag-voice reason as a tooltip. */
export interface AutopilotEligibilityDTO {
  eligible: boolean;
  /** Consecutive approved verdicts on this channel's publish lane,
   *  all-time. Resets to 0 on any dismiss/override. */
  approvedStreak: number;
  /** Threshold required to unlock — currently 3. */
  threshold: number;
  /** Mag-voice copy explaining the current state. Lowercase, no
   *  exclamations. Render verbatim as the toggle's helper text /
   *  tooltip. */
  reason: string;
}

export interface CampaignAutopilotStatusDTO {
  campaignId: string;
  /** Which autopilot channels apply to this campaign's kind.
   *  Empty array = no autopilot lanes (kinds like conversion_path /
   *  analytics_setup). UI hides the toggle when empty. */
  channels: Array<'meta_organic' | 'meta_paid' | 'google_paid'>;
  /** True iff every autopilot decision for the campaign's channels
   *  is currently overridden to its autopilot-on level. */
  on: boolean;
  /** The full overrides record on the campaign row. Surfaced for
   *  future per-decision-override UI; null when no overrides set. */
  overrides: Record<string, 'A0' | 'A1' | 'A2' | 'A3'> | null;
  /** [12.2L-169] Per-channel trust eligibility. Keyed by channel
   *  name. Missing when channels is empty. */
  eligibility?: Record<string, AutopilotEligibilityDTO>;
  /** [spec ea8ed74b] True once the founder has waived the trust gate on
   *  this campaign. The waiver sticks, so this stays true across later
   *  off/on flips and the UI renders the live toggle, not the padlock. */
  trustOverridden?: boolean;
  /** True iff every channel's publish-lane eligibility is met. UI
   *  reads this to decide whether the toggle is interactive. */
  allEligible?: boolean;
}

/** [12.2L-169] Thrown when the autopilot toggle is refused because
 *  mag hasn't earned the founder's trust yet (consecutive-approval
 *  streak < threshold on the relevant decision type). The dashboard
 *  catches this specifically to surface the mag-voice reason inline
 *  instead of a generic toast. */
export class AutopilotTrustGateError extends Error {
  override readonly name = 'AutopilotTrustGateError';
  constructor(
    message: string,
    public readonly channel: string,
    public readonly approvedStreak: number,
    public readonly threshold: number,
    public readonly blocking: Array<{
      channel: string;
      approvedStreak: number;
      reason: string;
    }>,
  ) {
    super(message);
  }
}

/** Read the current autopilot toggle state for a campaign. */
export async function fetchCampaignAutopilot(
  campaignId: string,
): Promise<CampaignAutopilotStatusDTO> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${campaignId}/autopilot`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = body.detail ?? body.error ?? `autopilot status ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as CampaignAutopilotStatusDTO;
}

/** Heartbeat cadence for the autopilot scheduler. Surfaced as a
 *  "next mag check · in N min" footer on autopilot-on campaigns so
 *  the founder knows when to expect activity. nextTickAt is null
 *  until the first heartbeat fires after a server restart — the
 *  dashboard renders "warming up" in that case. */
export interface AutopilotHeartbeatDTO {
  intervalMs: number;
  lastTickAt: string | null;
  nextTickAt: string | null;
}

export async function fetchAutopilotHeartbeat(): Promise<AutopilotHeartbeatDTO> {
  const res = await fetch(`${env.API_URL}/api/autopilot/heartbeat`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET heartbeat ${res.status} ${detail}`);
  }
  return (await res.json()) as AutopilotHeartbeatDTO;
}

/** Thrown when the autopilot toggle is refused because the brand's
 *  meta connection lacks scopes required for the campaign's channel.
 *  s236-3. The dashboard catches this specifically to render the
 *  extend-connection deep-link inline instead of a generic toast.
 *  Other failure modes throw a plain Error. */
export class AutopilotScopeGateError extends Error {
  override readonly name = 'AutopilotScopeGateError';
  constructor(
    message: string,
    /** Specific scopes the connection is missing. */
    public readonly missingScopes: string[],
    /** Pre-built extend URL the api gave us. Hits
     *  /api/brands/:id/meta/connect/extend?bundles=... which 302s
     *  to Meta's consent dialog for just the missing scopes. */
    public readonly extendUrl: string,
  ) {
    super(message);
  }
}

/** [spec 3a365634] The open asks mag has filed about this campaign.
 *
 *  Same predicate as the NEEDS YOU count on the campaigns list, so the
 *  row and the page cannot disagree about how many there are. */
export async function fetchCampaignAsks(campaignId: string): Promise<FeedCard[]> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${campaignId}/asks`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`campaign asks ${res.status}`);
  const body = (await res.json()) as { cards: FeedCard[] };
  return body.cards;
}

/** [spec ba7c4612] Set the campaign's daily budget — the one number the
 *  campaign page shows — by fanning it across launched paid deliverables.
 *
 *  Returns what ACTUALLY happened, which on a partial apply is not what
 *  was asked for: `actualTotalUsd` is re-read from the campaign after
 *  the writes, and `complete` is false when a leg failed and the run
 *  stopped. Callers must render actualTotalUsd, never the input. */
export interface CampaignBudgetResult {
  complete: boolean;
  actualTotalUsd: number;
  requestedTotalUsd: number;
  /** Present only when a leg stopped the run. `kind` decides the
   *  affordance: 'platform' may clear on another attempt, 'scope' will
   *  not until the connection is re-granted — so they must not share a
   *  button. extendUrl is set only for meta, the one channel with a
   *  self-serve re-grant flow. */
  failure?: {
    kind: 'platform' | 'scope';
    channel: 'google_paid' | 'meta_paid';
    detail?: string;
    extendUrl?: string;
  };
  legs: Array<{
    deliverableId: string;
    from: number;
    to: number;
    outcome: string;
    detail?: string;
  }>;
}

export async function setCampaignBudget(
  campaignId: string,
  newDailyBudgetUsd: number,
): Promise<CampaignBudgetResult> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${campaignId}/budget`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ newDailyBudgetUsd }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    throw new Error(body.detail ?? body.error ?? `set budget ${res.status}`);
  }
  return (await res.json()) as CampaignBudgetResult;
}

/** Flip the per-campaign autopilot toggle.
 *
 *  [spec ea8ed74b] `overrideTrustGate` waives the 3-approval gate. It is
 *  a separate argument rather than a field the caller assembles so the
 *  only way to ask for it is to mean it — and so every existing call
 *  site keeps the gated behaviour untouched. */
export async function setCampaignAutopilot(
  campaignId: string,
  on: boolean,
  overrideTrustGate = false,
): Promise<CampaignAutopilotStatusDTO> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${campaignId}/autopilot`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(overrideTrustGate ? { on, overrideTrustGate: true } : { on }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      detail?: string;
      missingScopes?: string[];
      extendUrl?: string;
      channel?: string;
      approvedStreak?: number;
      threshold?: number;
      blocking?: Array<{ channel: string; approvedStreak: number; reason: string }>;
    };
    if (res.status === 403 && body.error === 'trust_threshold_not_met') {
      throw new AutopilotTrustGateError(
        body.detail ?? "i haven't earned this autonomy yet",
        body.channel ?? '',
        body.approvedStreak ?? 0,
        body.threshold ?? 3,
        body.blocking ?? [],
      );
    }
    if (res.status === 409 && body.error === 'missing_scopes') {
      throw new AutopilotScopeGateError(
        body.detail ?? 'your meta connection needs more access to run this',
        body.missingScopes ?? [],
        body.extendUrl ?? '',
      );
    }
    const detail = body.detail ?? body.error ?? `autopilot toggle ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as CampaignAutopilotStatusDTO;
}

/** Build the meta connect-start URL with a specific bundle set.
 *  s236-2: founder picks which channel(s) they want to grant; the
 *  api composes the right scope list. Omitting `bundles` falls
 *  back to the legacy all-scopes path.
 *
 *  `returnTo` is a same-origin path the picker should navigate to
 *  after the founder finalizes. Server validates it before signing
 *  into the state token, so it's safe to pass anything from
 *  router state — invalid values get silently ignored and the
 *  picker uses its default landing. */
export function metaConnectStartUrl(
  brandId: string,
  options: { bundles?: MetaScopeBundle[]; returnTo?: string } = {},
): string {
  const base = `${env.API_URL}/api/brands/${brandId}/meta/connect/start`;
  const params = new URLSearchParams();
  if (options.bundles && options.bundles.length > 0) {
    params.set('bundles', options.bundles.join(','));
  }
  if (options.returnTo) params.set('returnTo', options.returnTo);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

/** Build the google ads connect-start URL. Mirrors metaConnectStartUrl —
 *  `returnTo` carries the dashboard surface the picker should land on
 *  after the founder picks a customer (validated server-side). */
export function googleConnectStartUrl(
  brandId: string,
  options: { returnTo?: string } = {},
): string {
  const base = `${env.API_URL}/api/brands/${brandId}/google/connect/start`;
  if (!options.returnTo) return base;
  return `${base}?returnTo=${encodeURIComponent(options.returnTo)}`;
}

/** Build the meta connect-EXTEND URL. Requires an existing
 *  connection — api 404s otherwise. Meta's consent dialog only
 *  shows scopes the founder hasn't already granted. s236-2. */
export function metaConnectExtendUrl(brandId: string, bundles: MetaScopeBundle[]): string {
  const param = bundles.join(',');
  return `${env.API_URL}/api/brands/${brandId}/meta/connect/extend?bundles=${encodeURIComponent(param)}`;
}

export interface MetaSlotPublishResultDTO {
  slotId: string;
  publishedPostId: string;
  publishedAt: string;
  via: 'mock' | 'meta';
}

/** Revert an autopilot-rewritten slot back to the previous draft.
 *  Phase A2. */
export async function revertSlotRewrite(
  campaignId: string,
  slotId: string,
): Promise<{ slotId: string; reverted: true }> {
  const res = await fetch(
    `${env.API_URL}/api/campaigns/${campaignId}/slots/${encodeURIComponent(slotId)}/revert-rewrite`,
    {
      method: 'POST',
      credentials: 'include',
    },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = body.detail ?? body.error ?? `revert-rewrite ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as { slotId: string; reverted: true };
}

/** Founder-triggered "publish now" on one organic_meta slot. Slot
 *  must have a draft + imageUrl. Phase 5. Optional `via='mock'`
 *  forces the mock client (founder testing without burning a real
 *  IG post). */
export async function publishOrganicMetaSlot(
  campaignId: string,
  slotId: string,
  options: { via?: 'auto' | 'mock' } = {},
): Promise<MetaSlotPublishResultDTO> {
  const qs = options.via === 'mock' ? '?via=mock' : '';
  const res = await fetch(
    `${env.API_URL}/api/campaigns/${campaignId}/slots/${encodeURIComponent(slotId)}/publish${qs}`,
    {
      method: 'POST',
      credentials: 'include',
    },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = body.detail ?? body.error ?? `slot publish ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as MetaSlotPublishResultDTO;
}

export interface MetaPublishModeResultDTO {
  campaignId: string;
  publishMode: 'manual' | 'auto';
}

/** Toggle the plan's publishMode between manual + auto. Auto =
 *  scheduler walks due slots every 5 min and publishes them. */
export async function setMetaPublishMode(
  campaignId: string,
  mode: 'manual' | 'auto',
): Promise<MetaPublishModeResultDTO> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${campaignId}/plan/publish-mode`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mode }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = body.detail ?? body.error ?? `publish-mode ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as MetaPublishModeResultDTO;
}

export interface MetaContentPlanReviseResultDTO {
  campaignId: string;
  /** Mag's one-sentence recap of what she changed + why. Same
   *  string the dashboard surfaces in the revision-history strip. */
  revisionSummary: string;
  slotsBefore: number;
  slotsAfter: number;
  /** How many of the founder's prior per-slot drafts survived the
   *  revision (carried forward by matching slotId). */
  draftsPreserved: number;
  costUsd: number;
  durationMs: number;
}

/** Chat-driven plan revision (phase 3b). Founder types an
 *  instruction; mag rewrites the plan + ports surviving slot
 *  drafts forward. ~$0.10-0.15 per revision. */
export async function triggerMetaContentPlanRevise(
  campaignId: string,
  instruction: string,
): Promise<MetaContentPlanReviseResultDTO> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${campaignId}/plan/revise`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ instruction }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = body.detail ?? body.error ?? `plan revise ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as MetaContentPlanReviseResultDTO;
}

/** Render the image for one organic_meta slot (phase 4b). Sends
 *  slot.draft.imageBrief through fal flux-schnell, re-hosts the
 *  result on R2, stamps slot.draft.imageUrl. Re-runs overwrite the
 *  prior bytes at the same R2 key. ~$0.003/render. */
export async function triggerMetaSlotImage(
  campaignId: string,
  slotId: string,
): Promise<MetaSlotImageResultDTO> {
  const res = await fetch(
    `${env.API_URL}/api/campaigns/${campaignId}/slots/${encodeURIComponent(slotId)}/image`,
    {
      method: 'POST',
      credentials: 'include',
    },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = body.detail ?? body.error ?? `meta slot-image ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as MetaSlotImageResultDTO;
}

/** Render the video for one organic_meta slot (s233.5 — phase B v2
 *  #1). Routes through runCreativePipeline → template-fill →
 *  Remotion, then stamps slot.draft.videoUrl + videoDurationSeconds
 *  + creativeAssetId. Takes ~20-30s end-to-end because video renders
 *  are the long pole. Only valid for reel / video slot formats. */
export async function triggerMetaSlotVideo(
  campaignId: string,
  slotId: string,
): Promise<MetaSlotVideoResultDTO> {
  const res = await fetch(
    `${env.API_URL}/api/campaigns/${campaignId}/slots/${encodeURIComponent(slotId)}/video`,
    {
      method: 'POST',
      credentials: 'include',
    },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = body.detail ?? body.error ?? `meta slot-video ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as MetaSlotVideoResultDTO;
}

/** Draft (or re-draft) the caption + visual brief for one slot inside
 *  an organic_meta campaign's content plan. Founder-triggered for v1
 *  — they click "draft this post" on a specific slot. Phase 5 adds an
 *  autopilot that batches drafts ahead of their scheduled date. */
export async function triggerMetaPostDraft(
  campaignId: string,
  slotId: string,
): Promise<MetaPostDraftResultDTO> {
  const res = await fetch(
    `${env.API_URL}/api/campaigns/${campaignId}/slots/${encodeURIComponent(slotId)}/draft`,
    {
      method: 'POST',
      credentials: 'include',
    },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    const detail = body.detail ?? body.error ?? `meta post-draft ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as MetaPostDraftResultDTO;
}

/** List the meta ad accounts the connected user has access to (s192).
 *  Throws if the brand has no active connection or no user token
 *  stored. */
export async function listMetaAdAccounts(brandId: string): Promise<MetaAdAccountDTO[]> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/meta/ad-accounts`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: `list ad accounts ${res.status}` }));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `list ad accounts ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { adAccounts: MetaAdAccountDTO[] };
  return json.adAccounts;
}

export async function setMetaAdAccount(
  brandId: string,
  adAccountId: string,
): Promise<BrandMetaConnectionDTO> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/meta/ad-account`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ adAccountId }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: `set ad account ${res.status}` }));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `set ad account ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { connection: BrandMetaConnectionDTO };
  return json.connection;
}

export interface MetaConnectHandoffPage {
  id: string;
  name: string;
  instagramBusinessId: string | null;
}
export interface MetaConnectHandoffResponse {
  brandId: string;
  pages: MetaConnectHandoffPage[];
  /** Same-origin path the picker should navigate to after success.
   *  Set when the connect-start route was hit with ?returnTo=. */
  returnTo?: string;
}

export async function readMetaConnectHandoff(handoff: string): Promise<MetaConnectHandoffResponse> {
  const res = await fetch(
    `${env.API_URL}/api/brands/meta/connect/handoff/${encodeURIComponent(handoff)}`,
    { credentials: 'include' },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: `handoff ${res.status}` }));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `handoff ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as MetaConnectHandoffResponse;
}

export async function finalizeMetaConnect(
  brandId: string,
  handoff: string,
  pageId: string,
): Promise<BrandMetaConnectionDTO> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/meta/connect/finalize`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ handoff, pageId }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: `finalize ${res.status}` }));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `finalize ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { connection: BrandMetaConnectionDTO };
  return json.connection;
}

// s236-2: bundle-aware metaConnectStartUrl + metaConnectExtendUrl
// live earlier in this file alongside the AutopilotScopeGateError.
// The plain-string export above predates s236; callers still using
// it work because the api defaults to all-bundles when no param is
// passed.

// ─── Google Ads connect (s199) ──────────────────────────────────

export interface GoogleConnectHandoffCustomer {
  id: string;
  descriptiveName?: string;
  isManager?: boolean;
}
export interface GoogleConnectHandoffResponse {
  brandId: string;
  customers: GoogleConnectHandoffCustomer[];
  /** customer id -> another brand in this workspace already using it. Present
   *  on a REUSE handoff.
   *
   *  [spec `one-ad-account-several-brands`] This was a refusal: picking one
   *  would have MOVED the account off that brand, so the picker disabled it.
   *  Migration 0232 put brand_id in the unique key, so picking it now gives
   *  this brand its own row and the sibling keeps theirs. The field survives
   *  as a LABEL — "also on gestionalo" — because a founder sharing one account
   *  across brands wants to see which others are on it. */
  takenByBrand?: Record<string, string>;
  /** Same-origin path the picker should navigate to after success.
   *  Set when the connect-start route was hit with ?returnTo=. */
  returnTo?: string;
}

export async function readGoogleConnectHandoff(
  handoff: string,
): Promise<GoogleConnectHandoffResponse> {
  const res = await fetch(
    `${env.API_URL}/api/brands/google/connect/handoff/${encodeURIComponent(handoff)}`,
    { credentials: 'include' },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: `handoff ${res.status}` }));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `handoff ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as GoogleConnectHandoffResponse;
}

/** A google grant this workspace already holds, offerable to a brand that has
 *  none (task c6a530c). One entry per GRANT, not per row — two brands sharing
 *  a login are one thing to pick. */
export interface ReusableGoogleConnection {
  connectionId: string;
  brands: string[];
  accountLabel: string;
}

export async function fetchReusableGoogleConnections(
  brandId: string,
): Promise<ReusableGoogleConnection[]> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/google/reusable`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`GET /api/brands/${brandId}/google/reusable ${res.status}`);
  const body = (await res.json()) as { connections: ReusableGoogleConnection[] };
  return body.connections;
}

/** Mint a handoff from an existing grant. Returns the handoff id — the caller
 *  navigates to the SAME picker the OAuth callback lands on, because the
 *  founder still chooses which ad account this brand gets. */
export async function reuseGoogleConnection(
  brandId: string,
  sourceConnectionId: string,
): Promise<string> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/google/connect/reuse`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sourceConnectionId }),
  });
  if (!res.ok) {
    // Typed at the await rather than inferred: res.json() is `any`, and letting
    // that flow into a local is what the no-unsafe-assignment rule catches.
    const body: unknown = await res.json().catch(() => null);
    const detail =
      typeof body === 'object' && body !== null && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `reuse ${res.status}`;
    throw new Error(detail);
  }
  const parsed = (await res.json()) as { handoff: string };
  return parsed.handoff;
}

export async function finalizeGoogleConnect(
  brandId: string,
  handoff: string,
  customerId: string,
): Promise<unknown> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/google/connect/finalize`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ handoff, customerId }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: `finalize ${res.status}` }));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `finalize ${res.status}`;
    throw new Error(detail);
  }
  return await res.json();
}

/** Generate an image for an instagram_post deliverable (s190).
 *  Calls fal.ai under the hood; ~3-8s. Returns the updated campaign
 *  with the deliverable's image_url field populated. The dashboard
 *  shows a spinner during the wait + re-renders the tile against
 *  the fresh campaign on success. */
export async function generateDeliverableImage(
  campaignId: string,
  deliverableId: string,
): Promise<CampaignDetail> {
  const res = await fetch(
    `${env.API_URL}/api/campaigns/${campaignId}/deliverables/${encodeURIComponent(deliverableId)}/generate-image`,
    { method: 'POST', credentials: 'include' },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: `generate-image ${res.status}` }));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `generate-image ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { campaign: CampaignDetail };
  return json.campaign;
}

/** Backfill or edit the campaign's stated goal (s133). Pass null to
 *  clear it back to mag's auto-anchor fallback. Returns the updated
 *  campaign detail (with tracker + insights + cost re-attached on the
 *  api side) so the caller can refresh local state in one round-trip. */
export async function setCampaignGoal(id: string, goal: string | null): Promise<CampaignDetail> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${id}/goal`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ goal }),
  });
  if (!res.ok) {
    const body = await res
      .json()
      .catch(() => ({ error: `PATCH /api/campaigns/${id}/goal ${res.status}` }));
    const message =
      typeof body === 'object' && body && 'error' in body
        ? String((body as { error: unknown }).error)
        : `PATCH /api/campaigns/${id}/goal ${res.status}`;
    throw new Error(message);
  }
  const json = (await res.json()) as { campaign: CampaignDetail };
  return json.campaign;
}

/** Set the proactive-scan cadence on a campaign (s126). Returns the
 *  updated campaign so the caller can refresh its local state without
 *  a follow-up read. The api primes next_scan_at + recomputes the
 *  monthly cost estimate inside this request. */
export async function setCampaignScanCadence(
  id: string,
  cadence: CampaignScanCadence,
): Promise<CampaignDetail> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${id}/scan-config`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cadence }),
  });
  if (!res.ok) {
    const body = await res
      .json()
      .catch(() => ({ error: `PATCH /api/campaigns/${id}/scan-config ${res.status}` }));
    const message =
      typeof body === 'object' && body && 'error' in body
        ? String((body as { error: unknown }).error)
        : `PATCH /api/campaigns/${id}/scan-config ${res.status}`;
    throw new Error(message);
  }
  const json = (await res.json()) as { campaign: CampaignDetail };
  return json.campaign;
}

/** Trigger a proactive scan ("ask mag what's changed") on a launched
 *  analytics_setup campaign. Synchronous on the api side — returns the
 *  freshly-generated insight in the same response. Ineligible states
 *  (wrong kind, not launched, no tracker) come back as 422 with a
 *  human-readable message we surface verbatim. */
export async function scanCampaign(id: string): Promise<CampaignInsight> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${id}/scan`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res
      .json()
      .catch(() => ({ error: `POST /api/campaigns/${id}/scan ${res.status}` }));
    const message =
      typeof body === 'object' && body && 'error' in body
        ? String((body as { error: unknown }).error)
        : `POST /api/campaigns/${id}/scan ${res.status}`;
    throw new Error(message);
  }
  const json = (await res.json()) as { insight: CampaignInsight };
  return json.insight;
}

/** Per-insight chat (M1 s159.7). Founder asks; mag answers with the
 *  insight + campaign + brand context in scope. Returns the updated
 *  insight (with both turns appended to its thread). */
export async function chatOnInsight(
  campaignId: string,
  insightId: string,
  question: string,
): Promise<CampaignInsight> {
  const res = await fetch(
    `${env.API_URL}/api/campaigns/${encodeURIComponent(campaignId)}/insights/${encodeURIComponent(insightId)}/chat`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question }),
    },
  );
  if (!res.ok)
    await throwFromResponse(
      res,
      `POST /api/campaigns/${campaignId}/insights/${insightId}/chat ${res.status}`,
    );
  const json = (await res.json()) as { insight: CampaignInsight };
  return json.insight;
}

/** s175 — live event from the chat-stream endpoint. The drawer wires
 *  a callback that updates a "↘ checking traffic sources…" line as
 *  the loop progresses, then swaps in the final reply when the
 *  'insight' event arrives. */
/** Loop-progress events forwarded from the SSE endpoint to the chat
 *  drawer's live status. Mirrors the ToolLoopEvent + final-payload
 *  shapes the api emits. Final payloads vary by endpoint:
 *    insight-chat stream → 'insight' with { insight: CampaignInsight }
 *    home-chat   stream → 'card'    with { card:    FeedCard }
 *  Errors are normalized to 'error'. */
export type ChatStreamEvent =
  | { kind: 'tool_call_started'; id: string; name: string; arguments: Record<string, unknown> }
  | {
      kind: 'tool_call_finished';
      id: string;
      name: string;
      durationMs: number;
      isError: boolean;
      resultPreview: string;
    }
  | { kind: 'round_complete'; round: number; moreRounds: boolean }
  // s179 — text deltas from the model's current assistant turn. The
  // drawer accumulates these into a live mag bubble that grows as the
  // reply forms, then swaps to the canonical card/insight body on the
  // final 'insight'/'card' event.
  | { kind: 'text_chunk'; text: string; round: number }
  | { kind: 'done'; text: string; hitMaxRounds: boolean; hitTimeout: boolean }
  | { kind: 'insight'; insight: CampaignInsight }
  | { kind: 'card'; card: FeedCard }
  | { kind: 'error'; error: string; detail?: string; pausedAt?: string };

/** Generic SSE driver shared across all chat-stream surfaces (s175 +
 *  s176). POSTs `body` to `url`, parses SSE frames, forwards loop-
 *  progress events to onEvent, and resolves with the final payload
 *  pulled from the `finalEventName` event. Throws RunawayPausedError
 *  on a runaway event so callers can branch the same way the JSON
 *  endpoints do. */
async function runChatStream<TFinal>(
  url: string,
  body: unknown,
  finalEventName: 'insight' | 'card' | 'campaign',
  extractFinal: (payload: unknown) => TFinal,
  onEvent: (event: ChatStreamEvent) => void,
): Promise<TFinal> {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify(body),
  });
  if (!res.ok || !res.body) {
    await throwFromResponse(res, `POST ${url} ${res.status}`);
  }
  // body is non-null per the check above; non-null assertion safe.
  // biome-ignore lint/style/noNonNullAssertion: throwFromResponse guarantees a thrown error when null.
  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalPayload: TFinal | null = null;
  let streamError: {
    error: string;
    detail?: string;
    pausedAt?: string;
    balance?: number;
    topUpHref?: string;
  } | null = null;
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // SSE events are separated by '\n\n'. Parse complete frames out
    // of the buffer; leave any trailing partial frame for the next
    // chunk.
    let frameEnd: number;
    // biome-ignore lint/suspicious/noAssignInExpressions: idiomatic SSE buffer drain
    while ((frameEnd = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, frameEnd);
      buffer = buffer.slice(frameEnd + 2);
      const parsed = parseSSEFrame(frame);
      if (!parsed) continue;
      if (parsed.event === finalEventName) {
        finalPayload = extractFinal(parsed.data);
        // Forward as the matching event kind so the drawer sees it.
        // [s379] 'campaign' (the intake stream) has no drawer event to
        // forward — the caller uses the returned value — so it only
        // captures.
        if (finalEventName === 'insight') {
          onEvent({ kind: 'insight', insight: finalPayload as unknown as CampaignInsight });
        } else if (finalEventName === 'card') {
          onEvent({ kind: 'card', card: finalPayload as unknown as FeedCard });
        }
        continue;
      }
      if (parsed.event === 'error') {
        streamError = parsed.data as {
          error: string;
          detail?: string;
          pausedAt?: string;
          balance?: number;
          topUpHref?: string;
        };
        onEvent({
          kind: 'error',
          error: streamError.error,
          ...(streamError.detail ? { detail: streamError.detail } : {}),
          ...(streamError.pausedAt ? { pausedAt: streamError.pausedAt } : {}),
        });
        continue;
      }
      // ToolLoopEvent shapes mirror the server-side union; the data
      // payload's `kind` matches the SSE event name.
      onEvent(parsed.data as ChatStreamEvent);
    }
  }
  if (streamError) {
    if (streamError.error === 'runaway_paused') {
      throw new RunawayPausedError(streamError.pausedAt ?? '');
    }
    if (streamError.error === 'out_of_tokens') {
      throw new InsufficientTokensError(
        Number(streamError.balance ?? 0),
        String(streamError.topUpHref ?? '/settings/billing'),
      );
    }
    throw new Error(streamError.detail ?? streamError.error);
  }
  if (finalPayload === null) {
    throw new Error(`chat stream closed without a '${finalEventName}' payload`);
  }
  return finalPayload;
}

/** Stream the insight chat turn via SSE. Returns the final
 *  CampaignInsight once the loop completes; calls onEvent for every
 *  intermediate step. Throws RunawayPausedError on a runaway-paused
 *  event so callers can branch on it like the JSON endpoint. */
export async function chatOnInsightStreaming(
  campaignId: string,
  insightId: string,
  question: string,
  onEvent: (event: ChatStreamEvent) => void,
): Promise<CampaignInsight> {
  return runChatStream(
    `${env.API_URL}/api/campaigns/${encodeURIComponent(campaignId)}/insights/${encodeURIComponent(insightId)}/chat/stream`,
    { question },
    'insight',
    (payload) => (payload as { insight: CampaignInsight }).insight,
    onEvent,
  );
}

/** [spec e85da0ee slice 4] What the bubble should open with, asked before the
 *  founder types anything.
 *
 *  `opener` is mag's greeting for this surface, or null when the server has
 *  nothing real to say about it — null means keep today's generic greeting.
 *  `card` is the thread already open for this descriptor, so the drawer can
 *  render the conversation the founder is walking back into instead of an
 *  empty box that fills with weeks of history the moment they send.
 *
 *  Both are resolved server-side from the ADDRESS alone. Side-effect free: it
 *  costs no agent call and creates no card. */
export type ChatContextPreload = {
  opener: string | null;
  card: FeedCard | null;
};

export async function fetchChatContextPreload(
  brandId: string | null | undefined,
  context: ChatContextDescriptor,
): Promise<ChatContextPreload> {
  const params = new URLSearchParams({ kind: context.kind });
  if (context.id) params.set('id', context.id);
  if (brandId) params.set('brand', brandId);
  const res = await fetch(`${env.API_URL}/api/feed/chat/context?${params.toString()}`, {
    credentials: 'include',
  });
  // The bubble must open regardless. A failed preload is the generic greeting
  // and a fresh thread — exactly today's behaviour — never a blocked drawer.
  if (!res.ok) return { opener: null, card: null };
  return (await res.json()) as ChatContextPreload;
}

/** Stream the first home-chat turn via SSE (s176). Returns the
 *  freshly-created FeedCard once the agent loop completes. s215 phase
 *  2b — optional `attachedAssetIds` carries brand_asset uuids the
 *  founder dropped via the composer file picker (already uploaded to
 *  /api/brand-brain/assets). */
export async function askMagChatStreaming(
  question: string,
  brandId: string | null | undefined,
  onEvent: (event: ChatStreamEvent) => void,
  attachedAssetIds?: string[],
  /** spec e85da0ee — what the founder is looking at, as an ADDRESS. Never
   *  facts: the server access-checks the id and reads the facts itself. */
  context?: ChatContextDescriptor,
): Promise<FeedCard> {
  const body: Record<string, unknown> = { question };
  if (attachedAssetIds && attachedAssetIds.length > 0) {
    body['attachedAssetIds'] = attachedAssetIds;
  }
  if (context) {
    body['context'] = context;
  }
  return runChatStream(
    `${env.API_URL}/api/feed/chat/stream${brandQuery(brandId)}`,
    body,
    'card',
    (payload) => (payload as { card: FeedCard }).card,
    onEvent,
  );
}

/** Stream a home-chat follow-up turn via SSE (s176). */
export async function sendMagChatTurnStreaming(
  cardId: string,
  question: string,
  onEvent: (event: ChatStreamEvent) => void,
  attachedAssetIds?: string[],
): Promise<FeedCard> {
  const body: Record<string, unknown> = { question };
  if (attachedAssetIds && attachedAssetIds.length > 0) {
    body['attachedAssetIds'] = attachedAssetIds;
  }
  return runChatStream(
    `${env.API_URL}/api/feed/chat/${encodeURIComponent(cardId)}/messages/stream`,
    body,
    'card',
    (payload) => (payload as { card: FeedCard }).card,
    onEvent,
  );
}

/** Pull (event, data) out of one SSE frame. Handles the multi-line
 *  `data:` prefix; ignores comments + retry/id lines. */
function parseSSEFrame(frame: string): { event: string; data: unknown } | null {
  let eventName = 'message';
  const dataLines: string[] = [];
  for (const line of frame.split('\n')) {
    if (line.startsWith(':')) continue;
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim());
    }
  }
  if (dataLines.length === 0) return null;
  try {
    return { event: eventName, data: JSON.parse(dataLines.join('\n')) };
  } catch {
    return null;
  }
}

// ───── Ad accounts + takeover ─────────────────────────────────────────────
// Phase A of the "take over an existing campaign" flow. Mock-connect today;
// real Google OAuth lands later. Shapes stay stable across the swap.

export interface ConnectedAdAccount {
  id: string;
  provider: AdPlatform;
  providerCustomerId: string;
  accountLabel: string;
  status: AdAccountStatus;
  /** ISO 4217 the account bills in, or null when unknown. Null is not
   *  USD — render the amount without a symbol rather than guessing. */
  currency: string | null;
  connectedAt: string;
  lastUsedAt: string | null;
}

/** What currency this brand's ad money is in, or an explicit refusal.
 *  Mirrors the api's BrandAdCurrency (decision 11456df). Null currency
 *  is NEVER "probably USD" — each reason renders different copy:
 *  'none' = connect an account, 'unknown' = we have one but never read
 *  its currency, 'mixed' = the brand's live accounts disagree. */
export type BrandAdCurrency =
  | { currency: string; reason: 'ok' }
  | { currency: null; reason: 'none' | 'unknown' | 'mixed' };

export interface AdAccountListEnvelope {
  accounts: ConnectedAdAccount[];
  organizationId: string;
  /** Null when the listing spans brands (brand=all), where the question
   *  has no single answer. */
  adCurrency?: BrandAdCurrency | null;
  /** Per-platform answer, keyed by provider. Most money on screen is
   *  platform money, and a brand CAN bill in two currencies at once —
   *  google in USD while meta is in UYU — so the brand-wide answer
   *  refusing does not mean a google figure is unlabellable. Platforms
   *  the brand never connected are absent, not null. */
  adCurrencyByProvider?: Partial<Record<AdPlatform, BrandAdCurrency>>;
}

export async function fetchAdAccounts(
  brandId?: string | null,
): Promise<AdAccountListEnvelope | null> {
  const res = await fetch(`${env.API_URL}/api/ad-accounts${brandQuery(brandId)}`, {
    credentials: 'include',
  });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`GET /api/ad-accounts ${res.status}`);
  return (await res.json()) as AdAccountListEnvelope;
}

export interface ConnectMockInput {
  /** Which ad platform the founder is connecting. Drives the endpoint
   *  path + how the api validates the customer-id format. */
  provider: AdPlatform;
  providerCustomerId: string;
  accountLabel?: string;
}

export async function connectMockAdAccount(
  input: ConnectMockInput,
  brandId?: string | null,
): Promise<ConnectedAdAccount> {
  const res = await fetch(
    `${env.API_URL}/api/ad-accounts/${input.provider}/connect-mock${brandQuery(brandId)}`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        providerCustomerId: input.providerCustomerId,
        ...(input.accountLabel ? { accountLabel: input.accountLabel } : {}),
      }),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /api/ad-accounts/${input.provider}/connect-mock ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { account: ConnectedAdAccount };
  return json.account;
}

export async function disconnectAdAccount(id: string): Promise<ConnectedAdAccount> {
  const res = await fetch(`${env.API_URL}/api/ad-accounts/${id}/disconnect`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /api/ad-accounts/${id}/disconnect ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { account: ConnectedAdAccount };
  return json.account;
}

export async function fetchProviderCampaigns(adAccountId: string): Promise<CampaignListItem[]> {
  const res = await fetch(`${env.API_URL}/api/ad-accounts/${adAccountId}/campaigns`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `GET /api/ad-accounts/${adAccountId}/campaigns ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { campaigns: CampaignListItem[] };
  return json.campaigns;
}

export interface ImportedCampaignDetail {
  id: string;
  organizationId: string;
  /** s200 — brand id, used by the takeover-view to target Phase B
   *  write endpoints (which are brand-scoped). */
  brandId: string;
  adAccountId: string;
  providerCampaignId: string;
  name: string;
  snapshot: CampaignSnapshot;
  magRead: TakeoverRead | null;
  importedAt: string;
  refreshedAt: string | null;
  adAccountLabel: string;
  provider: AdPlatform;
  /** Unification roadmap Phase 4 — the founder's persisted budget
   *  pick from the conversation card (USD cents). Null until the
   *  founder responds to the recommendation. Set even when they
   *  choose to keep the platform's current budget (we record the
   *  decision either way). */
  dailyBudgetUsdCents: number | null;
  /** [spec move-campaign-between-sharing-brands] Other brands in this
   *  workspace holding this campaign's ad account.
   *
   *  EMPTY IS THE SIGNAL TO RENDER NOTHING. Almost every account is held by a
   *  single brand, so a move control on every imported campaign would charge
   *  every founder's attention to serve a handful. Empty means the page is
   *  exactly what it was before this shipped. */
  sharedWith: Array<{ id: string; name: string; slug: string }>;
}

export interface ImportedCampaignListEnvelope {
  campaigns: ImportedCampaignDetail[];
  organizationId: string;
}

export async function fetchImportedCampaigns(): Promise<ImportedCampaignListEnvelope | null> {
  const res = await fetch(`${env.API_URL}/api/imported-campaigns`, { credentials: 'include' });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`GET /api/imported-campaigns ${res.status}`);
  return (await res.json()) as ImportedCampaignListEnvelope;
}

/** Remove an imported campaign from magig. Server-side this drops
 *  the campaigns row + the imported_campaigns mirror + any cascade-
 *  dependent rows (autopilot audit, metrics history). The platform
 *  campaign on Google/Meta stays untouched — the founder manages
 *  that on the provider's own UI. */
/** [spec move-campaign-between-sharing-brands] Move an imported campaign to a
 *  sibling brand that shares its ad account.
 *
 *  Founder-initiated: the server checks editor on BOTH the losing and the
 *  gaining brand, and refuses a target brand that does not hold the account. */
export async function moveImportedCampaignToBrand(
  campaignId: string,
  brandId: string,
): Promise<{ id: string; brandId: string; adAccountId: string }> {
  const res = await fetch(`${env.API_URL}/api/imported-campaigns/${campaignId}/brand`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ brandId }),
  });
  if (!res.ok) {
    // The sibling writes in this file cast only the CATCH branch, which leaves
    // the success branch as `any` — their lines are grandfathered, new ones are
    // not. Cast the whole awaited value so the narrowing below is real.
    const body = (await res.json().catch(() => null)) as {
      detail?: unknown;
      error?: unknown;
    } | null;
    const detail =
      typeof body?.detail === 'string'
        ? body.detail
        : typeof body?.error === 'string'
          ? body.error
          : `POST /api/imported-campaigns/${campaignId}/brand ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as {
    campaign: { id: string; brandId: string; adAccountId: string };
  };
  return json.campaign;
}

export async function deleteImportedCampaign(campaignId: string): Promise<void> {
  const res = await fetch(`${env.API_URL}/api/imported-campaigns/${campaignId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null as { detail?: unknown; error?: unknown } | null);
    const detail =
      typeof body?.detail === 'string'
        ? body.detail
        : typeof body?.error === 'string'
          ? body.error
          : `DELETE /api/imported-campaigns/${campaignId} ${res.status}`;
    throw new Error(detail);
  }
}

/** Unification Phase 4 — persist the founder's pick from the budget
 *  conversation card. Accepts USD cents (100..500000); server
 *  validates the range. Returns the persisted value so the caller
 *  can confirm the round-trip. */
export async function setImportedCampaignBudget(
  campaignId: string,
  dailyBudgetUsdCents: number,
): Promise<number> {
  const res = await fetch(`${env.API_URL}/api/imported-campaigns/${campaignId}/budget`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ dailyBudgetUsdCents }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null as { detail?: unknown; error?: unknown } | null);
    const detail =
      typeof body?.detail === 'string'
        ? body.detail
        : typeof body?.error === 'string'
          ? body.error
          : `POST /api/imported-campaigns/${campaignId}/budget ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { dailyBudgetUsdCents: number };
  return json.dailyBudgetUsdCents;
}

// ─── Google Ads Phase B writes (s200) ──────────────────────────
//
// One helper per write endpoint. Every call is brand-scoped + needs
// a connected google ads account on that brand; the api returns a
// 409 'no_google_connection' if missing. Returned shape is the same
// GoogleAdsMutationResult the api ships, so callers can render a
// "mock" badge when via='mock'.

export interface GoogleAdsMutationResult {
  resourceNames: string[];
  via: 'mock' | 'google';
}

interface GoogleAdsMutationError {
  error: string;
  detail?: string;
  googleErrorCodes?: string[];
}

/** Optional archival context the dashboard attaches to every Google
 *  takeover write so the api can record a `takeover_action_executions`
 *  row alongside the mutation. Mirrors the server-side Zod schema in
 *  @magig/schemas. Pass when the write originated from a Mag
 *  recommendation; omit for direct admin actions (the api just
 *  skips the record). */
export interface TakeoverActionContextPayload {
  importedCampaignId: string;
  title: string;
  rationale: string;
  execution: TakeoverActionExecution;
}

async function postGoogleAdsWrite(
  url: string,
  body: Record<string, unknown>,
  actionContext?: TakeoverActionContextPayload,
): Promise<GoogleAdsMutationResult> {
  const merged = actionContext ? { ...body, actionContext } : body;
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(merged),
  });
  if (!res.ok) {
    const errBody = (await res.json().catch(() => null)) as GoogleAdsMutationError | null;
    const detail = errBody?.detail ?? errBody?.error ?? `google write ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { result: GoogleAdsMutationResult };
  return json.result;
}

export async function setGoogleCampaignStatus(
  brandId: string,
  resourceId: string,
  status: 'ENABLED' | 'PAUSED',
  actionContext?: TakeoverActionContextPayload,
): Promise<GoogleAdsMutationResult> {
  return postGoogleAdsWrite(
    `${env.API_URL}/api/brands/${brandId}/google/campaigns/status`,
    { resourceId, status },
    actionContext,
  );
}

export async function setGoogleAdGroupStatus(
  brandId: string,
  resourceId: string,
  status: 'ENABLED' | 'PAUSED',
  actionContext?: TakeoverActionContextPayload,
): Promise<GoogleAdsMutationResult> {
  return postGoogleAdsWrite(
    `${env.API_URL}/api/brands/${brandId}/google/ad-groups/status`,
    { resourceId, status },
    actionContext,
  );
}

export async function setGoogleAdStatus(
  brandId: string,
  /** providerAdId from the snapshot. */
  resourceId: string,
  /** Parent providerAdGroupId from the snapshot — required because
   *  Google's ad_group_ad resource names are compound. */
  adGroupResourceId: string,
  status: 'ENABLED' | 'PAUSED',
  actionContext?: TakeoverActionContextPayload,
): Promise<GoogleAdsMutationResult> {
  return postGoogleAdsWrite(
    `${env.API_URL}/api/brands/${brandId}/google/ads/status`,
    { resourceId, adGroupResourceId, status },
    actionContext,
  );
}

export async function updateGoogleBudget(
  brandId: string,
  budgetResourceId: string,
  dailyBudgetUsd: number,
  actionContext?: TakeoverActionContextPayload,
): Promise<GoogleAdsMutationResult> {
  return postGoogleAdsWrite(
    `${env.API_URL}/api/brands/${brandId}/google/budget`,
    { budgetResourceId, dailyBudgetUsd },
    actionContext,
  );
}

/** s206 — swap a responsive search ad. Google policy: RSAs can't be
 *  edited in place; the api atomically removes the old ad and
 *  creates a new one via a single mutate call. Caller passes the
 *  full replacement spec. */
export interface GoogleRsaSwapInput {
  oldAdGroupAdResourceId: string;
  adGroupResourceId: string;
  headlines: Array<{ text: string; pinnedField?: 'HEADLINE_1' | 'HEADLINE_2' | 'HEADLINE_3' }>;
  descriptions: Array<{ text: string }>;
  finalUrl: string;
  trackingUrlTemplate?: string;
}

/** Spec 5614cfab slice 2 — ask mag what she would change about a live ad.
 *
 *  READ-SHAPED DESPITE BEING A POST: it fetches the ad from google, runs the
 *  rewrite agent and returns both halves of a diff. It does not touch the ad.
 *  Applying is a separate call the founder makes after reading it, because a
 *  swap removes the live ad and resets its learning.
 *
 *  `target` carries the two ids the apply needs. They are returned rather
 *  than stored for the same reason `current` is: both are only true as of
 *  this read. */
export interface RewriteDraftResponse {
  current: {
    headlines: string[];
    descriptions: string[];
    finalUrl: string | null;
    adStrength: 'poor' | 'average' | 'good' | 'excellent' | null;
  };
  target: { oldAdGroupAdResourceId: string; adGroupResourceId: string };
  proposed: {
    headlines: Array<{ text: string; pinnedField?: 'HEADLINE_1' | 'HEADLINE_2' | 'HEADLINE_3' }>;
    descriptions: Array<{ text: string }>;
    rationale: string;
  };
  costUsd: number;
}

export async function fetchRewriteDraft(
  campaignId: string,
  deliverableId: string,
): Promise<RewriteDraftResponse> {
  const res = await fetch(
    `${env.API_URL}/api/campaigns/${campaignId}/deliverables/${encodeURIComponent(deliverableId)}/rewrite-draft`,
    { method: 'POST', credentials: 'include' },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `rewrite draft failed (${res.status})`);
  }
  return (await res.json()) as RewriteDraftResponse;
}

export async function swapGoogleRsa(
  brandId: string,
  input: GoogleRsaSwapInput,
  actionContext?: TakeoverActionContextPayload,
): Promise<GoogleAdsMutationResult> {
  return postGoogleAdsWrite(
    `${env.API_URL}/api/brands/${brandId}/google/rsa/swap`,
    { ...input },
    actionContext,
  );
}

/** s208 — create a brand-new RSA on an existing ad group. Same
 *  form-shape as swap minus the "old ad" id; mag's UI lets the
 *  founder add a new ad variant alongside whatever's already
 *  running. */
export interface GoogleNewRsaInput {
  adGroupResourceId: string;
  headlines: Array<{ text: string; pinnedField?: 'HEADLINE_1' | 'HEADLINE_2' | 'HEADLINE_3' }>;
  descriptions: Array<{ text: string }>;
  finalUrl: string;
  trackingUrlTemplate?: string;
}

export async function createGoogleRsa(
  brandId: string,
  input: GoogleNewRsaInput,
  actionContext?: TakeoverActionContextPayload,
): Promise<GoogleAdsMutationResult> {
  return postGoogleAdsWrite(
    `${env.API_URL}/api/brands/${brandId}/google/rsa`,
    { ...input },
    actionContext,
  );
}

/** s208 — create a new ad group on an existing campaign. Accepts an
 *  optional initial RSA + an optional initial keyword set so mag can
 *  ship the whole group atomically (vs a bare ad group with nothing
 *  in it). */
export interface GoogleNewAdGroupInput {
  campaignResourceId: string;
  name: string;
  cpcBidUsd: number;
  keywords?: Array<{ text: string; matchType: 'EXACT' | 'PHRASE' | 'BROAD' }>;
  initialAd?: {
    headlines: Array<{ text: string; pinnedField?: 'HEADLINE_1' | 'HEADLINE_2' | 'HEADLINE_3' }>;
    descriptions: Array<{ text: string }>;
    finalUrl: string;
  };
}

export async function createGoogleAdGroup(
  brandId: string,
  input: GoogleNewAdGroupInput,
  actionContext?: TakeoverActionContextPayload,
): Promise<GoogleAdsMutationResult> {
  return postGoogleAdsWrite(
    `${env.API_URL}/api/brands/${brandId}/google/ad-groups`,
    { ...input },
    actionContext,
  );
}

export async function fetchImportedCampaign(id: string): Promise<ImportedCampaignDetail | null> {
  const res = await fetch(`${env.API_URL}/api/imported-campaigns/${id}`, {
    credentials: 'include',
  });
  if (res.status === 401) return null;
  if (res.status === 404) return null;
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/imported-campaigns/${id} ${res.status} ${detail}`);
  }
  const json = (await res.json()) as { campaign: ImportedCampaignDetail };
  return json.campaign;
}

/** Post-write refresh — pull a fresh Google snapshot, optionally
 *  re-running the takeover-read + budget-recommender agents.
 *
 *  `skipMagRead: true` keeps Mag's recommendation list stable across
 *  approvals — used when autopilot is OFF so the founder finishes
 *  their batch on a list that doesn't mutate under their finger.
 *  The snapshot still refreshes, so the campaign-anatomy drawer +
 *  per-action "isActionApplied" self-detection still catch up.
 *
 *  `skipMagRead: false` (or omitted) re-runs the agents so Mag's
 *  read tracks the new state — used when autopilot is ON, so the
 *  next autopilot tick works against the latest signal. */
export async function refreshImportedCampaign(
  id: string,
  options: { skipMagRead?: boolean } = {},
): Promise<ImportedCampaignDetail> {
  const qs = options.skipMagRead ? '?skipMagRead=1' : '';
  const res = await fetch(`${env.API_URL}/api/imported-campaigns/${id}/refresh${qs}`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/imported-campaigns/${id}/refresh ${res.status} ${detail}`);
  }
  const json = (await res.json()) as { campaign: ImportedCampaignDetail };
  return json.campaign;
}

// ─── Takeover-recommendation dismissals ───────────────────────────────
// ─── Takeover-action executions ("how's it doing?" panel) ──────────
// One row per founder-approved Mag action that shipped to Google.
// The dashboard reads these to render an expandable impact panel
// under each ✓ shipped action — timestamp, per-resource metrics
// from the latest snapshot, and a mag-voice voice line.

export interface TakeoverImpactMetricsSlice {
  windowDays: number;
  impressions: number;
  clicks: number;
  conversions: number;
  costUsd: number;
}

export interface TakeoverImpactPanel {
  shippedAt: string;
  shippedAgoMinutes: number;
  resourceKind: 'campaign' | 'ad_group' | 'ad' | 'budget' | 'unknown';
  resourceLabel: string;
  metrics: TakeoverImpactMetricsSlice | null;
  voiceLine: string;
}

export interface TakeoverShippedExecution {
  id: string;
  actionSignature: string;
  actionKind: string;
  titleSnapshot: string;
  rationaleSnapshot: string;
  executionSnapshot: TakeoverActionExecution;
  targetResourceId: string;
  createdResourceName: string | null;
  executedAt: string;
}

export interface TakeoverExecutionItem {
  execution: TakeoverShippedExecution;
  impact: TakeoverImpactPanel | null;
}

export async function listTakeoverExecutions(campaignId: string): Promise<TakeoverExecutionItem[]> {
  const res = await fetch(`${env.API_URL}/api/imported-campaigns/${campaignId}/executions`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET executions ${res.status} ${detail}`);
  }
  const json = (await res.json()) as { executions: TakeoverExecutionItem[] };
  return json.executions;
}

// ─── Takeover-recommendation dismissals ───────────────────────────────
// Founder-curated "not this one" log for mag's per-campaign action
// recommendations. Dismissing an action drops it from the takeover
// view until the founder restores it; the dismissed log on the page
// renders the stored title + rationale for reference.

export interface TakeoverDismissalRecord {
  id: string;
  actionSignature: string;
  titleSnapshot: string;
  rationaleSnapshot: string;
  decisionNote: string | null;
  /** Frozen execution block from the dismissed action. Shape varies
   *  by kind (the discriminated union), so the dashboard treats this
   *  as opaque except when re-surfacing in the disclosure. */
  executionSnapshot: unknown;
  dismissedAt: string;
}

export async function dismissTakeoverRecommendation(
  campaignId: string,
  body: {
    execution: TakeoverActionExecution;
    title: string;
    rationale: string;
    decisionNote?: string;
  },
): Promise<{ dismissal: { id: string; actionSignature: string } }> {
  const res = await fetch(`${env.API_URL}/api/imported-campaigns/${campaignId}/dismissals`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST dismissals ${res.status} ${detail}`);
  }
  return (await res.json()) as { dismissal: { id: string; actionSignature: string } };
}

export async function listTakeoverDismissals(
  campaignId: string,
): Promise<TakeoverDismissalRecord[]> {
  const res = await fetch(`${env.API_URL}/api/imported-campaigns/${campaignId}/dismissals`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET dismissals ${res.status} ${detail}`);
  }
  const json = (await res.json()) as { dismissals: TakeoverDismissalRecord[] };
  return json.dismissals;
}

export async function restoreTakeoverDismissal(
  campaignId: string,
  dismissalId: string,
): Promise<void> {
  const res = await fetch(
    `${env.API_URL}/api/imported-campaigns/${campaignId}/dismissals/${dismissalId}`,
    { method: 'DELETE', credentials: 'include' },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`DELETE dismissal ${res.status} ${detail}`);
  }
}

export async function importProviderCampaign(input: {
  adAccountId: string;
  providerCampaignId: string;
}): Promise<ImportedCampaignDetail> {
  const res = await fetch(`${env.API_URL}/api/imported-campaigns`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /api/imported-campaigns ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { campaign: ImportedCampaignDetail };
  return json.campaign;
}

// ───── Mag analytics ──────────────────────────────────────────────────────
// Project provisioning + recent-events feed + per-path click counts. The
// dashboard polls /api/analytics for the "live" feel; once Mag's agent
// tools land we'll proxy queries through them instead.

export interface AnalyticsSummary {
  windowDays: number;
  pageViews: number;
  clicks: number;
  scrollEvents: number;
  /** Every conversion-type event, whatever it was named — render it with
   *  conversionsByName, never alone. See the note on the server-side
   *  type in packages/clickhouse/src/queries.ts. */
  conversions: number;
  /** OPTIONAL on purpose. The dashboard and the api deploy separately, so
   *  a dashboard build can reach an api that predates this field; typing
   *  it as required would make the analytics route throw on `.length`
   *  instead of degrading the one card. */
  conversionsByName?: { name: string; count: number }[];
  uniqueVisitors: number;
}

export interface AnalyticsEnvelope {
  projects: AnalyticsProject[];
  events: AnalyticsEventRow[];
  summary: AnalyticsSummary | null;
  pathClicks: AnalyticsPathClickCount[];
  /** 14-day daily breakdown of pageviews + unique visitors, oldest-
   *  first. Zero days are filled server-side so the array is always
   *  exactly 14 entries when a tracker exists. */
  visitsByDay: AnalyticsVisitsByDay[];
  /** Top-N rollups for device / browser / referrer over the same
   *  window as `summary`. Null when there's no tracker yet. */
  audience: AnalyticsAudience | null;
  organizationId: string;
  /** True when the ClickHouse-backed reads (events / summary / etc.)
   *  failed transiently and were degraded to empty — the tracker list
   *  (`projects`, from Postgres) is still real. The UI shows a "readings
   *  paused" notice instead of implying there's no data. Absent on
   *  healthy responses. */
  degraded?: boolean;
}

export async function fetchAnalytics(brandId?: string | null): Promise<AnalyticsEnvelope | null> {
  const brand = brandQuery(brandId);
  const tz = tzQuery(brand ? '&' : '?');
  const res = await fetch(`${env.API_URL}/api/analytics${brand}${tz}`, {
    credentials: 'include',
  });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`GET /api/analytics ${res.status}`);
  return (await res.json()) as AnalyticsEnvelope;
}

export interface ActiveAnalyticsEnvelope {
  activeSessions: AnalyticsActiveSession[];
  windowMinutes: number;
  brandId: string;
}

export async function fetchActiveAnalytics(
  brandId: string,
): Promise<ActiveAnalyticsEnvelope | null> {
  const res = await fetch(`${env.API_URL}/api/analytics/active${brandQuery(brandId)}`, {
    credentials: 'include',
  });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`GET /api/analytics/active ${res.status}`);
  return (await res.json()) as ActiveAnalyticsEnvelope;
}

export interface VisitorEventsEnvelope {
  events: AnalyticsEventRow[];
  anonId: string;
  brandId: string;
}

export async function fetchVisitorEvents(
  brandId: string,
  anonId: string,
): Promise<VisitorEventsEnvelope | null> {
  const res = await fetch(
    `${env.API_URL}/api/analytics/visitors/${encodeURIComponent(anonId)}/events${brandQuery(brandId)}`,
    { credentials: 'include' },
  );
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`GET /api/analytics/visitors/${anonId}/events ${res.status}`);
  return (await res.json()) as VisitorEventsEnvelope;
}

export interface CreateAnalyticsProjectInput {
  name: string;
  allowedHosts: string[];
}

// ───── Conversion forwarders ─────────────────────────────────────────────
// Per-project, per-platform toggles that decide which ad-platform CAPI /
// Enhanced Conversion APIs Mag fans conversion events to.
//
// META IS REAL [task 272644f]. Once a pixel id and a CAPI access token are
// saved, meta conversions go out through the batch upload scheduler, not the
// mocked real-time fan-out — so the panel must not describe meta as a dry run
// while money is moving. Google and tiktok are still mocked.

export type ConversionPlatform = 'meta' | 'google' | 'tiktok';

export interface ConversionForwardConfig {
  projectId: string;
  platform: ConversionPlatform;
  enabled: boolean;
  /** Which credential keys the row HOLDS — never their values. The api
   *  answers with key names only, so a token can be reported as present and
   *  can never be rendered, re-sent, or read back out of the dashboard. */
  configKeys: string[];
  updatedAt: string;
}

/** Meta's two credential keys, spelled as the api stores them. */
const CAPI_PIXEL_ID_KEY = 'pixel_id';
const CAPI_ACCESS_TOKEN_KEY = 'access_token_encrypted';

export function hasCapiPixelId(config: ConversionForwardConfig | null): boolean {
  return config?.configKeys.includes(CAPI_PIXEL_ID_KEY) ?? false;
}

export function hasCapiAccessToken(config: ConversionForwardConfig | null): boolean {
  return config?.configKeys.includes(CAPI_ACCESS_TOKEN_KEY) ?? false;
}

/** Both halves present — the only state in which the uploader will send. */
export function capiCredentialsComplete(config: ConversionForwardConfig | null): boolean {
  return hasCapiPixelId(config) && hasCapiAccessToken(config);
}

export interface ConversionForwardLogEntry {
  id: string;
  platform: ConversionPlatform;
  conversionName: string;
  status: 'sent' | 'failed' | 'mocked';
  attemptedAt: string;
}

export interface ConversionForwardsEnvelope {
  platforms: ConversionPlatform[];
  forwards: ConversionForwardConfig[];
  recentLogs: ConversionForwardLogEntry[];
}

export async function fetchConversionForwards(
  projectId: string,
): Promise<ConversionForwardsEnvelope> {
  const params = new URLSearchParams({ projectId });
  const res = await fetch(`${env.API_URL}/api/analytics/conversion-forwards?${params.toString()}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/analytics/conversion-forwards ${res.status} ${detail}`);
  }
  return (await res.json()) as ConversionForwardsEnvelope;
}

export interface MetaAdPixelOption {
  id: string;
  name: string;
}

/** Why no pixel list could be produced. Each one is a different founder
 *  action, which is the reason they are not collapsed into one failure. */
export type MetaPixelRefusal =
  | 'no_meta_connection'
  | 'connection_missing_scope'
  | 'connection_token_expired'
  | 'no_ad_account'
  | 'missing_access_token'
  | 'undecryptable_access_token'
  | 'encryption_unavailable'
  | 'graph_error'
  | 'unreachable'
  | 'no_config'
  | 'missing_pixel_id';

export interface MetaPixelsEnvelope {
  /** null means NO LIST COULD BE PRODUCED — see `refusal`. An empty array
   *  means the ad account genuinely has no pixels, which is a different
   *  sentence and must not be rendered as the same one. */
  pixels: MetaAdPixelOption[] | null;
  refusal: MetaPixelRefusal | null;
  /** Meta's own message, when it gave one. */
  detail: string | null;
}

/** List the pixels on the brand's meta ad account, so a founder can pick one
 *  instead of reading an id out of Events Manager.
 *
 *  A refusal comes back as a 200 with `pixels: null` — the request was fine,
 *  the answer is "not yet", and the panel renders which. Only a genuine
 *  transport or auth failure throws. */
export async function fetchMetaPixels(projectId: string): Promise<MetaPixelsEnvelope> {
  const params = new URLSearchParams({ projectId });
  const res = await fetch(
    `${env.API_URL}/api/analytics/conversion-forwards/meta-pixels?${params.toString()}`,
    { credentials: 'include' },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/analytics/conversion-forwards/meta-pixels ${res.status} ${detail}`);
  }
  return (await res.json()) as MetaPixelsEnvelope;
}

export async function setConversionForward(input: {
  projectId: string;
  platform: ConversionPlatform;
  /** Omit to leave the toggle exactly as it is — saving a credential should
   *  not have to restate a toggle decision. */
  enabled?: boolean;
  /** Meta-only. The numeric pixel id; the api refuses anything else. */
  pixelId?: string;
  /** Meta-only, PLAINTEXT on the wire and encrypted server-side. Write-only:
   *  it is never returned by any read. */
  accessToken?: string;
  /** Explicit, because an empty string would be indistinguishable from a
   *  field the founder simply left blank. */
  clearCredentials?: boolean;
}): Promise<ConversionForwardConfig> {
  const res = await fetch(`${env.API_URL}/api/analytics/conversion-forwards`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /api/analytics/conversion-forwards ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { forward: ConversionForwardConfig };
  return json.forward;
}

/** Trigger Mag to write a fresh analytics finding for a project. Lands
 *  as a feed card in /home plus the /analytics findings list. */
export async function createAnalyticsFinding(input: {
  projectId: string;
  path?: string;
}): Promise<FeedCard> {
  const res = await fetch(`${env.API_URL}/api/analytics/findings`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /api/analytics/findings ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { card: FeedCard };
  return json.card;
}

export async function fetchAnalyticsHeatmap(input: {
  projectId: string;
  path: string;
  days?: number;
}): Promise<AnalyticsPathHeatmap | null> {
  const params = new URLSearchParams({
    projectId: input.projectId,
    path: input.path,
    ...(input.days ? { days: String(input.days) } : {}),
  });
  const res = await fetch(`${env.API_URL}/api/analytics/heatmap?${params.toString()}`, {
    credentials: 'include',
  });
  if (res.status === 401) return null;
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/analytics/heatmap ${res.status} ${detail}`);
  }
  const json = (await res.json()) as { heatmap: AnalyticsPathHeatmap };
  return json.heatmap;
}

export async function createAnalyticsProject(
  input: CreateAnalyticsProjectInput,
  brandId?: string | null,
): Promise<AnalyticsProject> {
  const res = await fetch(`${env.API_URL}/api/analytics/projects${brandQuery(brandId)}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /api/analytics/projects ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { project: AnalyticsProject };
  return json.project;
}

export interface UpdateAnalyticsProjectInput {
  name?: string;
  allowedHosts?: string[];
}

export async function updateAnalyticsProject(
  projectId: string,
  input: UpdateAnalyticsProjectInput,
): Promise<AnalyticsProject> {
  const res = await fetch(`${env.API_URL}/api/analytics/projects/${projectId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `PATCH /api/analytics/projects/${projectId} ${res.status}`;
    throw new Error(detail);
  }
  const json = (await res.json()) as { project: AnalyticsProject };
  return json.project;
}

/** Generate or rotate the server-side write key for a tracker. The
 *  returned plaintext is the only copy that exists in plaintext anywhere
 *  — the api stored only sha256(plaintext). Caller MUST surface it to
 *  the founder in the same render cycle (modal with copy + warning) and
 *  drop it from memory afterwards. Calling this on a tracker that
 *  already has a key is a rotation: the old key stops working
 *  immediately. */
export async function setAnalyticsProjectWriteKey(
  projectId: string,
): Promise<{ writeKey: string; createdAt: string }> {
  const res = await fetch(`${env.API_URL}/api/analytics/projects/${projectId}/write-key`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /api/analytics/projects/${projectId}/write-key ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as { writeKey: string; createdAt: string };
}

/** Reveal the tracker's current server write key. Resolves to null when
 *  the key predates recoverable storage — that's a real state ("exists,
 *  can't be shown"), not an error, so the panel renders it rather than
 *  surfacing a failure. */
export async function revealAnalyticsProjectWriteKey(projectId: string): Promise<string | null> {
  const res = await fetch(`${env.API_URL}/api/analytics/projects/${projectId}/write-key`, {
    credentials: 'include',
  });
  if (!res.ok) {
    // Annotated unknown rather than the inferred any of res.json() — the
    // narrowing below already does the work, and the annotation is what
    // keeps this off the no-unsafe-assignment rule.
    const body: unknown = await res.json().catch(() => ({}));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `GET /api/analytics/projects/${projectId}/write-key ${res.status}`;
    throw new Error(detail);
  }
  return ((await res.json()) as { writeKey: string | null }).writeKey;
}

export interface AnalyticsServerStatus {
  count: number;
  /** ISO timestamp, or '' when nothing has ever arrived. */
  lastAt: string;
  recentNames: string[];
}

/** Whether anything has ever come in through this tracker's write key.
 *  A silently-misconfigured webhook is otherwise invisible — the panel
 *  used to describe what a write key is and never say whether yours had
 *  received a single event. */
export async function getAnalyticsServerStatus(projectId: string): Promise<AnalyticsServerStatus> {
  const res = await fetch(`${env.API_URL}/api/analytics/projects/${projectId}/server-status`, {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`GET /api/analytics/projects/${projectId}/server-status ${res.status}`);
  }
  return (await res.json()) as AnalyticsServerStatus;
}

export async function ackFeedCard(id: string): Promise<FeedCard> {
  const res = await fetch(`${env.API_URL}/api/feed/${id}/ack`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/feed/${id}/ack ${res.status} ${detail}`);
  }
  const json = (await res.json()) as { card: FeedCard };
  return json.card;
}

/** s216-54h.53 — analytics_finding only. Founder shipped the
 *  recommended change. Flips the card to acked + records 'approved'
 *  in the trust ledger with decisionNote='implemented' so brand-coach
 *  sees the positive verdict (vs a dismiss). On the open card only —
 *  409 on an already-acked or already-dismissed card. */
export async function markFindingImplemented(id: string): Promise<FeedCard> {
  const res = await fetch(`${env.API_URL}/api/feed/${id}/mark-finding-implemented`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/feed/${id}/mark-finding-implemented ${res.status} ${detail}`);
  }
  const json = (await res.json()) as { card: FeedCard };
  return json.card;
}

/** s216-54h.51 — flip a card from unread → read. Fired by the
 *  FeedCardDrawer on mount; idempotent server-side, so calling
 *  twice for the same card is a no-op (read_at stamps once and
 *  stays). Returns the fresh card so the caller can update its
 *  local cache without a full feed refetch. */
export async function markFeedCardRead(id: string): Promise<FeedCard> {
  const res = await fetch(`${env.API_URL}/api/feed/${id}/mark-read`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/feed/${id}/mark-read ${res.status} ${detail}`);
  }
  const json = (await res.json()) as { card: FeedCard };
  return json.card;
}

/** Accept a mag_campaign_proposal card. Spawns the real campaign
 *  via the intake pipeline + flips the card to 'acked'. Returns
 *  campaignId so the UI can route to the new chat thread. */
/** [spec be4289a9, slice 1] The founder vouching for a domain.
 *
 *  409 evidence_gone is its own case rather than a generic failure: the
 *  card was true when it was written and the tag has since come off the
 *  site, which the founder should be told plainly instead of being shown
 *  a request-failed message for a request that was fine. */
export interface DnsChallenge {
  domain: string;
  recordName: string;
  recordType: 'TXT';
  recordValue: string;
}

/** [spec be4289a9, slice 2] Ask for the TXT record this brand must publish.
 *
 *  Safe to call repeatedly: the server returns the SAME challenge rather than
 *  minting a new one, so a founder who reloads is not told to replace a
 *  record they already added. */
export async function mintDomainDnsChallenge(
  brandId: string,
  domain: string,
): Promise<DnsChallenge> {
  const res = await fetch(
    `${env.API_URL}/api/brands/${brandId}/domain-verifications/dns-challenge`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ domain }),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      `POST /api/brands/:id/domain-verifications/dns-challenge ${res.status} ${detail}`,
    );
  }
  return (await res.json()) as DnsChallenge;
}

/** Check the record. 409 is the ordinary case, not a failure: DNS does not
 *  say what we need YET. The founder-facing sentence distinguishes "nothing
 *  there" from "something there that is not ours", because those ask for
 *  different next moves — add the record, versus look at what you pasted. */
export async function checkDomainDnsVerification(
  brandId: string,
  domain: string,
): Promise<{ domain: string; outcome: string }> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/domain-verifications/dns-check`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ domain }),
  });
  if (res.status === 409) {
    const payload = (await res.json().catch(() => ({}))) as { error?: string };
    if (payload.error === 'mismatch') {
      throw new Error("i found a record there, but it isn't the one i issued — check the value");
    }
    if (payload.error === 'no_challenge') {
      throw new Error('no record was issued for this domain yet');
    }
    throw new Error("i can't see that record yet — dns can take a while to spread");
  }
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/brands/:id/domain-verifications/dns-check ${res.status} ${detail}`);
  }
  return (await res.json()) as { domain: string; outcome: string };
}

export async function confirmDomainVerification(
  brandId: string,
  domain: string,
): Promise<{ domain: string; outcome: string }> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/domain-verifications`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ domain }),
  });
  if (res.status === 409) {
    throw new Error("that tag isn't reporting from this domain any more — i can't vouch for it");
  }
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/brands/:id/domain-verifications ${res.status} ${detail}`);
  }
  return (await res.json()) as { domain: string; outcome: string };
}

export async function acceptCampaignProposal(
  cardId: string,
): Promise<{ campaignId: string; proposalName: string }> {
  const res = await fetch(`${env.API_URL}/api/feed/mag-campaign-proposal/${cardId}/accept`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/feed/mag-campaign-proposal/:cardId/accept ${res.status} ${detail}`);
  }
  return (await res.json()) as { campaignId: string; proposalName: string };
}

/** [12.2L-124] AI strategist heartbeats — next brand-coach tick + current
 *  strategist cadence decision. Renders in brand settings as a "next pass"
 *  hint + a "run now" CTA when the strategist is skipping. */
export interface StrategistRunDecision {
  decision: 'run';
  reason: string;
  effectiveWindowDays: number;
}
export interface StrategistSkipDecision {
  decision: 'skip';
  reason: string;
  detail: string | null;
}
/** What mag may spend on her own, RESOLVED by the api.
 *
 *  Do NOT read `brand.monthlyAutonomousSpendBudgetCents` for this. That column
 *  is the founder's explicit override and is null for every brand whose cap
 *  derives from observed ad spend, so reading it renders "unset" over a real
 *  ceiling.
 *
 *  `source` is not decoration: 'none' means no ceiling could be determined and
 *  the gate refuses any autonomous spend, which is a different claim from a cap
 *  of zero. Never collapse them into one number.
 *
 *  `currency` is 'USD' for a derived cap (the derivation refuses anything that
 *  is not single-currency USD) and null for a founder-set one, which is
 *  denominated in the connected ad account's currency — label that from
 *  `fetchAdAccounts`, as the cap editor already does. */
export interface AutonomySpendCap {
  capCents: number | null;
  source: 'founder' | 'derived' | 'none';
  /** Trailing-window spend the cap was derived from, USD. Null unless derived. */
  observedSpendUsd: number | null;
  currency: 'USD' | null;
}
export interface BrandHeartbeats {
  brandCoach: { nextTickAt: string | null };
  autonomySpendCap: AutonomySpendCap;
  strategist: StrategistRunDecision | StrategistSkipDecision;
}

export async function fetchBrandHeartbeats(brandId: string): Promise<BrandHeartbeats> {
  const res = await fetch(`${env.API_URL}/api/brands/${encodeURIComponent(brandId)}/heartbeats`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/brands/:brandId/heartbeats ${res.status} ${detail}`);
  }
  return (await res.json()) as BrandHeartbeats;
}

/** [12.2L-124] Founder-triggered strategist run. Bypasses the 14d/30d/60d
 *  cadence + cool-down windows but still respects marketing URL, budget
 *  headroom, connected platforms, and the org's daily LLM budget. */
export type RunStrategistNowResponse =
  | {
      kind: 'ran';
      /** [task 30a36f5] The shared union from @magig/schemas, not
       *  `string`. While this was a string, adding an outcome on the api
       *  side degraded silently to the generic toast line with no type
       *  error here — which happened twice before anyone noticed. */
      outcome: RunStrategistOutcome;
      costUsd: number;
      proposalId?: string;
      reason?: string;
    }
  | {
      kind: 'refused';
      refusal:
        | { reason: 'no_marketing_url' }
        | { reason: 'no_budget_headroom' }
        | { reason: 'no_platforms' }
        | { reason: 'org_budget_exhausted'; spentUsd: number; capUsd: number }
        | { reason: 'no_target_market'; detail: string };
    };

export async function runStrategistNow(brandId: string): Promise<RunStrategistNowResponse> {
  const res = await fetch(
    `${env.API_URL}/api/brands/${encodeURIComponent(brandId)}/strategist/run-now`,
    {
      method: 'POST',
      credentials: 'include',
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/brands/:brandId/strategist/run-now ${res.status} ${detail}`);
  }
  return (await res.json()) as RunStrategistNowResponse;
}

/** [12.2L-131] Launch an ai_strategist-origin campaign on Google
 *  Ads. Runs the translator + the real client's createCampaign
 *  chain. Returns the providerCampaignId so the UI can deep-link
 *  to the campaign in the customer's Google Ads UI. */
export type LaunchFromStrategyResponse =
  | { outcome: 'launched'; providerCampaignId: string; via: 'mock' | 'google' }
  | { error: string; detail?: string };

/** [12.2L-131] Launch every campaign spawned by an accepted strategy
 *  proposal in one click. Per-campaign outcomes surface so the UI can
 *  render partial-success states.
 *  [12.2L-165] M6 — outcome union grew Meta variants; `provider` tag
 *  added so the renderer can disambiguate which platform produced
 *  each outcome without overlap inference on kind. */
export interface PerCampaignLaunchResultDTO {
  campaignId: string;
  campaignName: string | null;
  provider: 'google' | 'meta' | string;
  outcome:
    | { kind: 'launched'; providerCampaignId?: string; via: 'mock' | 'real' | 'google' }
    | { kind: 'campaign_not_found' }
    | { kind: 'campaign_wrong_origin'; origin: string }
    | { kind: 'campaign_wrong_status'; status: string }
    | { kind: 'campaign_wrong_provider'; provider: string }
    | { kind: 'no_strategy_proposal' }
    | { kind: 'no_matching_channel' }
    | { kind: 'no_google_account' }
    | { kind: 'no_meta_connection' }
    | { kind: 'meta_missing_scope'; missing: string[] }
    | { kind: 'meta_no_ad_account' }
    | { kind: 'promote_post_missing_post_id' }
    | { kind: 'google_rejected'; detail: string }
    | { kind: 'meta_rejected'; detail: string }
    | { kind: 'unsupported'; detail: string };
}

export interface LaunchAllFromProposalResponse {
  proposalId: string;
  results: PerCampaignLaunchResultDTO[];
  summary: { total: number; launched: number; failed: number; skipped: number };
}

export async function launchAllFromProposal(
  proposalId: string,
): Promise<LaunchAllFromProposalResponse> {
  const res = await fetch(
    `${env.API_URL}/api/strategist/proposals/${encodeURIComponent(proposalId)}/launch-all`,
    { method: 'POST', credentials: 'include' },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/strategist/proposals/:id/launch-all ${res.status} ${detail}`);
  }
  return (await res.json()) as LaunchAllFromProposalResponse;
}

export async function launchAiStrategistCampaign(
  campaignId: string,
): Promise<LaunchFromStrategyResponse> {
  const res = await fetch(
    `${env.API_URL}/api/campaigns/${encodeURIComponent(campaignId)}/launch-from-strategy`,
    { method: 'POST', credentials: 'include' },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    return {
      error: body.error ?? `launch_failed_${res.status}`,
      ...(body.detail ? { detail: body.detail } : {}),
    };
  }
  return (await res.json()) as LaunchFromStrategyResponse;
}

/** [12.2L-121] Accept an AI strategist proposal. Spawns the proposal's
 *  per-channel components as `campaigns` rows with origin='ai_strategist'
 *  status='ready'. Returns the new campaign ids so the UI can route to
 *  the campaigns list. */
export async function acceptStrategyProposal(
  proposalId: string,
  /** [spec b59688f0] The founder's chosen DAILY budget per channel, keyed by
   *  campaignName — the key the launch matches on. Omit to take mag's
   *  recommendation. Only channels the founder actually changed need appear;
   *  absent keys keep the proposal's own figure. */
  chosenDailyBudgetUsdByChannel?: Record<string, number>,
): Promise<{ spawnedCampaignIds: string[]; proposalName: string }> {
  const res = await fetch(
    `${env.API_URL}/api/strategist/proposals/${encodeURIComponent(proposalId)}/accept`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(chosenDailyBudgetUsdByChannel ? { chosenDailyBudgetUsdByChannel } : {}),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/strategist/proposals/:id/accept ${res.status} ${detail}`);
  }
  return (await res.json()) as { spawnedCampaignIds: string[]; proposalName: string };
}

/** [12.2L-121] Dismiss an AI strategist proposal. Optional founder note
 *  lands on the card's decision_note + drives the 30d cluster cool-down. */
export async function dismissStrategyProposal(proposalId: string, reason?: string): Promise<void> {
  const res = await fetch(
    `${env.API_URL}/api/strategist/proposals/${encodeURIComponent(proposalId)}/dismiss`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(reason ? { reason } : {}),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/strategist/proposals/:id/dismiss ${res.status} ${detail}`);
  }
}

/** Accept a mag_channel_proposal card. Upserts brand_channels.{kind}
 *  with operatedByMag=true; status stays pending_connection until
 *  the founder runs OAuth. Card flips to 'acked'. */
export async function acceptChannelProposal(
  cardId: string,
): Promise<{ brandChannelId: string; channelKind: string }> {
  const res = await fetch(`${env.API_URL}/api/feed/mag-channel-proposal/${cardId}/accept`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/feed/mag-channel-proposal/:cardId/accept ${res.status} ${detail}`);
  }
  return (await res.json()) as { brandChannelId: string; channelKind: string };
}

/** Apply a brand_evolution card's diffs — bumps the brand brain from
 *  v_n to v_{n+1} and marks the card acked. Returns the new brain
 *  version so the UI can echo "brain now v3" without a re-fetch.
 *
 *  s216-54h.46 — optional `selectedIndexes` picks a subset of the
 *  card's proposed diffs (per-diff checkbox UI). Omit to apply all
 *  (back-compat). Indexes are 0-based into payload.diffs. */
export async function applyBrandEvolution(
  id: string,
  selectedIndexes?: readonly number[],
): Promise<{ newBrainVersion: number }> {
  const init: RequestInit = {
    method: 'POST',
    credentials: 'include',
  };
  if (selectedIndexes !== undefined) {
    init.headers = { 'Content-Type': 'application/json' };
    init.body = JSON.stringify({ selectedIndexes });
  }
  const res = await fetch(`${env.API_URL}/api/feed/${id}/apply-brand-evolution`, init);
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/feed/${id}/apply-brand-evolution ${res.status} ${detail}`);
  }
  return (await res.json()) as { newBrainVersion: number };
}

/** s215 phase 3a — read the brand's full authority map: per-
 *  decision-type {defaultLevel, overrideLevel, currentLevel,
 *  dropped}. Renderable as a settings table without any joining;
 *  the server resolves the drop history into `currentLevel` and
 *  flags `dropped` when it's stricter than the baseline. */
export type AuthorityLevel = 'A0' | 'A1' | 'A2' | 'A3';

export interface AuthorityItem {
  decisionType: string;
  defaultLevel: AuthorityLevel;
  overrideLevel: AuthorityLevel | null;
  currentLevel: AuthorityLevel;
  dropped: boolean;
}

export async function fetchBrandAuthority(brandId: string): Promise<{ items: AuthorityItem[] }> {
  const res = await fetch(`${env.API_URL}/api/brands/${encodeURIComponent(brandId)}/authority`, {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`GET /api/brands/${brandId}/authority ${res.status}`);
  }
  return (await res.json()) as { items: AuthorityItem[] };
}

/** Set an explicit override on the brand's authority map for one
 *  decision type. Manager-only on the server side. Returns the
 *  resolved currentLevel after the merge — drops still apply on
 *  top so the saved value may differ from `level` when there's an
 *  outstanding stricter drop. */
export async function setBrandAuthority(
  brandId: string,
  decisionType: string,
  level: AuthorityLevel,
): Promise<{
  ok: true;
  decisionType: string;
  level: AuthorityLevel;
  currentLevel: AuthorityLevel;
}> {
  const res = await fetch(`${env.API_URL}/api/brands/${encodeURIComponent(brandId)}/authority`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ decisionType, level }),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/brands/${brandId}/authority ${res.status} ${detail}`);
  }
  return (await res.json()) as {
    ok: true;
    decisionType: string;
    level: AuthorityLevel;
    currentLevel: AuthorityLevel;
  };
}

/** Apply a trust_migration_proposal card — mutates the brand's
 *  authority_map to grant Mag the proposed level on that decision
 *  type. The card flips to acked atomically; founder dismisses via
 *  the regular skip path instead. */
export async function applyTrustMigration(
  id: string,
): Promise<{ newAuthority: 'A0' | 'A1' | 'A2' | 'A3'; decisionType: string }> {
  const res = await fetch(`${env.API_URL}/api/feed/${id}/apply-trust-migration`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/feed/${id}/apply-trust-migration ${res.status} ${detail}`);
  }
  return (await res.json()) as { newAuthority: 'A0' | 'A1' | 'A2' | 'A3'; decisionType: string };
}

/** Submit founder text on a monthly_plan card. Server runs the
 *  synthesis agent and transitions the card payload from
 *  awaiting_input to synthesized; caller refreshes feed queries
 *  to see the updated card. */
export async function submitMonthlyPlan(
  id: string,
  founderInput: string,
): Promise<{ costUsd: number }> {
  const res = await fetch(`${env.API_URL}/api/feed/monthly-plan/${id}/submit`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ founderInput }),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/feed/monthly-plan/${id}/submit ${res.status} ${detail}`);
  }
  return (await res.json()) as { costUsd: number };
}

/** Record an 'approved' verdict on one concept inside a
 *  creative_drop card (s216). Founder needs creative_direction
 *  authority to be relevant; the server enforces that 'approved'
 *  only fires on needs_approval concepts. Refresh ['feed'] after
 *  to pick up the action chip. */
export async function approveDropConcept(
  cardId: string,
  conceptId: string,
): Promise<{ action: { conceptId: string; action: 'approved' | 'objected'; actedAt: string } }> {
  const res = await fetch(
    `${env.API_URL}/api/feed/creative-drop/${cardId}/concepts/${conceptId}/approve`,
    { method: 'POST', credentials: 'include' },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      `POST /api/feed/creative-drop/${cardId}/concepts/${conceptId}/approve ${res.status} ${detail}`,
    );
  }
  return (await res.json()) as {
    action: { conceptId: string; action: 'approved' | 'objected'; actedAt: string };
  };
}

/** Rename a campaign (s233.13). Inline edit on the detail page.
 *  Pass null to clear the name (UI falls back to the kind-derived
 *  default). Server trims + caps at 80 chars. */
export async function renameCampaign(
  campaignId: string,
  name: string | null,
): Promise<{ campaign: { id: string; name: string | null } }> {
  const res = await fetch(`${env.API_URL}/api/campaigns/${campaignId}/name`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`PATCH /api/campaigns/${campaignId}/name ${res.status} ${detail}`);
  }
  return (await res.json()) as { campaign: { id: string; name: string | null } };
}

/** Regenerate the fal-generated background on an image-surface
 *  creative asset (s233.12). Keeps the hook / body / cta / brief
 *  untouched — only the photographic background gets a new pass.
 *  Re-enqueues the render after. Caller refreshes the asset query
 *  to pick up the new mp4/png URL. */
export async function regenerateCreativeAssetBackground(assetId: string): Promise<{
  creativeAssetId: string;
  backgroundImageUrl: string;
  costUsd: number;
  queued: boolean;
}> {
  const res = await fetch(`${env.API_URL}/api/creative-assets/${assetId}/regenerate-background`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      `POST /api/creative-assets/${assetId}/regenerate-background ${res.status} ${detail}`,
    );
  }
  return (await res.json()) as {
    creativeAssetId: string;
    backgroundImageUrl: string;
    costUsd: number;
    queued: boolean;
  };
}

/** Asset-scoped duration override (s233.4). Bumps a video creative
 *  asset's videoDurationOverrideSeconds + re-renders. Foundation for
 *  per-slide carousel overrides in the drawer; the drop-concept
 *  wrapper below delegates to this same path server-side. Caller
 *  refreshes the asset query after to pick up the new url. */
export async function overrideCreativeAssetDuration(
  assetId: string,
  durationSeconds: number,
): Promise<{ creativeAssetId: string; durationSeconds: number; queued: boolean }> {
  const res = await fetch(`${env.API_URL}/api/creative-assets/${assetId}/override-duration`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ durationSeconds }),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      `POST /api/creative-assets/${assetId}/override-duration ${res.status} ${detail}`,
    );
  }
  return (await res.json()) as {
    creativeAssetId: string;
    durationSeconds: number;
    queued: boolean;
  };
}

/** Founder duration override (s233.3). Bumps the underlying
 *  creative_asset's videoDurationOverrideSeconds + re-renders. The
 *  drop concept's durationSeconds field stays as Mag's original
 *  recommendation (audit trail). Caller polls the asset until status
 *  flips back to 'ready'. */
export async function overrideDropConceptDuration(
  cardId: string,
  conceptId: string,
  durationSeconds: number,
): Promise<{ creativeAssetId: string; durationSeconds: number; queued: boolean }> {
  const res = await fetch(
    `${env.API_URL}/api/feed/creative-drop/${cardId}/concepts/${conceptId}/override-duration`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ durationSeconds }),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      `POST /api/feed/creative-drop/${cardId}/concepts/${conceptId}/override-duration ${res.status} ${detail}`,
    );
  }
  return (await res.json()) as {
    creativeAssetId: string;
    durationSeconds: number;
    queued: boolean;
  };
}

/** Record an 'objected' verdict on one concept (s216). Valid on
 *  any proposedDecision kind — founder can veto an auto_launch
 *  concept before mag actually fires. */
export async function objectDropConcept(
  cardId: string,
  conceptId: string,
): Promise<{ action: { conceptId: string; action: 'approved' | 'objected'; actedAt: string } }> {
  const res = await fetch(
    `${env.API_URL}/api/feed/creative-drop/${cardId}/concepts/${conceptId}/object`,
    { method: 'POST', credentials: 'include' },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      `POST /api/feed/creative-drop/${cardId}/concepts/${conceptId}/object ${res.status} ${detail}`,
    );
  }
  return (await res.json()) as {
    action: { conceptId: string; action: 'approved' | 'objected'; actedAt: string };
  };
}

/** Undo a previously-recorded approve/object verdict on one drop
 *  concept. Deletes the creative_drop_concept_actions row so the
 *  founder can re-decide. The trust-ledger entry from the original
 *  action stays in place (append-only audit). Returns the action
 *  that was removed for tooltip/toast surfaces. */
export async function undoDropConcept(
  cardId: string,
  conceptId: string,
): Promise<{
  undone: { conceptId: string; action: 'approved' | 'objected'; actedAt: string } | null;
}> {
  const res = await fetch(
    `${env.API_URL}/api/feed/creative-drop/${cardId}/concepts/${conceptId}/undo`,
    { method: 'POST', credentials: 'include' },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      `POST /api/feed/creative-drop/${cardId}/concepts/${conceptId}/undo ${res.status} ${detail}`,
    );
  }
  return (await res.json()) as {
    undone: { conceptId: string; action: 'approved' | 'objected'; actedAt: string } | null;
  };
}

// ───── Notifications (s178) ────────────────────────────────────────────────
// Inbox + bell badge + SSE stream. The stream itself uses EventSource, not
// fetch — see lib/notifications-stream.ts for the subscription helper.

export type NotificationChannelDelivery = {
  channel: 'in_app' | 'email' | 'slack' | 'web_push';
  status: 'queued' | 'sending' | 'sent' | 'delivered' | 'failed' | 'skipped';
  reason?: string;
};

export interface NotificationItem {
  id: string;
  organizationId: string;
  brandId: string | null;
  kind: string;
  title: string;
  body: string;
  cta: { label: string; href: string } | null;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  payload: unknown;
  feedCardId: string | null;
  status: 'pending' | 'dispatched' | 'partial' | 'failed';
  readAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  deliveries: NotificationChannelDelivery[];
}

export interface NotificationListEnvelope {
  notifications: NotificationItem[];
  nextCursor: string | null;
}

export interface NotificationListParams {
  brand?: string | 'all';
  filter?: 'all' | 'unread' | 'archived';
  cursor?: string | null;
  limit?: number;
  kinds?: string[];
}

export async function fetchNotifications(
  params: NotificationListParams = {},
): Promise<NotificationListEnvelope> {
  const qs = new URLSearchParams();
  if (params.brand) qs.set('brand', params.brand);
  if (params.filter) qs.set('filter', params.filter);
  if (params.cursor) qs.set('cursor', params.cursor);
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.kinds && params.kinds.length > 0) qs.set('kinds', params.kinds.join(','));
  const url = `${env.API_URL}/api/notifications${qs.toString() ? `?${qs.toString()}` : ''}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error(`GET /api/notifications ${res.status}`);
  return (await res.json()) as NotificationListEnvelope;
}

export async function fetchNotificationUnreadCount(brand?: string | 'all'): Promise<number> {
  const qs = new URLSearchParams();
  if (brand) qs.set('brand', brand);
  const url = `${env.API_URL}/api/notifications/unread-count${
    qs.toString() ? `?${qs.toString()}` : ''
  }`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error(`GET /api/notifications/unread-count ${res.status}`);
  const body = (await res.json()) as { count: number };
  return body.count;
}

export async function markNotificationRead(id: string, read = true): Promise<void> {
  const res = await fetch(`${env.API_URL}/api/notifications/${id}/read`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ read }),
  });
  if (!res.ok) throw new Error(`PATCH /api/notifications/${id}/read ${res.status}`);
}

export async function archiveNotification(id: string, archived = true): Promise<void> {
  const res = await fetch(`${env.API_URL}/api/notifications/${id}/archive`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ archived }),
  });
  if (!res.ok) throw new Error(`PATCH /api/notifications/${id}/archive ${res.status}`);
}

export async function markAllNotificationsRead(brand?: string | 'all'): Promise<number> {
  const res = await fetch(`${env.API_URL}/api/notifications/read-all`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(brand ? { brand } : {}),
  });
  if (!res.ok) throw new Error(`POST /api/notifications/read-all ${res.status}`);
  const body = (await res.json()) as { updated: number };
  return body.updated;
}

// s212 — per (user, brand) preferred channel for outbound pings.
// The single setting the home page widget surfaces. Email is the
// schema default + the fallback when no row exists.

export type PreferredNotificationChannel = 'in_app' | 'email' | 'slack' | 'web_push' | 'telegram';

export interface RecipientSettingsResponse {
  brandId: string;
  /** Legacy single-channel field. The api returns it alongside
   *  preferredChannels for backward compat with older readers; new
   *  code reads the array. */
  preferredChannel: PreferredNotificationChannel;
  /** s212-multichannel — the full set of channels Mag dispatches to
   *  for this (user, brand). Every entry receives every non-required
   *  notification (per-kind opt-outs still apply). At least one
   *  channel must be present; the api refuses an empty array. */
  preferredChannels: PreferredNotificationChannel[];
  /** s212-escalation toggle. When false, Mag won't escalate unread
   *  asks past the SLA over louder channels — she just lets them
   *  sit in the founder's preferred channels + the inbox. Default true. */
  escalationEnabled: boolean;
  /** False when the value is the default (email + escalation on)
   *  because the founder hasn't explicitly picked yet — drives the
   *  "confirm your channels" prompt on the home widget. */
  explicit: boolean;
}

export async function getRecipientSettings(brandId: string): Promise<RecipientSettingsResponse> {
  const res = await fetch(
    `${env.API_URL}/api/notifications/recipient-settings?brand=${encodeURIComponent(brandId)}`,
    { credentials: 'include' },
  );
  if (!res.ok) throw new Error(`GET /api/notifications/recipient-settings ${res.status}`);
  return (await res.json()) as RecipientSettingsResponse;
}

/** Partial update — any subset of fields. Empty body returns 400.
 *  When the caller passes `preferredChannels`, the api mirrors the
 *  first element into the legacy singular column so back-compat
 *  readers stay sane. Empty array is rejected (need at least one
 *  channel — Mag has to reach you somehow). */
export async function setRecipientSettings(
  brandId: string,
  changes: {
    preferredChannel?: PreferredNotificationChannel;
    preferredChannels?: PreferredNotificationChannel[];
    escalationEnabled?: boolean;
  },
): Promise<RecipientSettingsResponse> {
  const res = await fetch(`${env.API_URL}/api/notifications/recipient-settings`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ brandId, ...changes }),
  });
  if (!res.ok) throw new Error(`PUT /api/notifications/recipient-settings ${res.status}`);
  return (await res.json()) as RecipientSettingsResponse;
}

// Preferences — user × brand × kind × channel overrides. The catalog
// defaults live in @magig/schemas; the api only stores explicit
// overrides + quiet hours.

export interface NotificationPreferenceRow {
  id: string;
  brandId: string | null;
  /** Catalog kind id or '*' meaning all kinds. */
  kind: string;
  channel: NotificationChannel;
  enabled: boolean;
  quietHoursStart: string | null;
  quietHoursEnd: string | null;
  timezone: string | null;
  updatedAt: string;
}

export async function fetchNotificationPreferences(): Promise<NotificationPreferenceRow[]> {
  const res = await fetch(`${env.API_URL}/api/notifications/preferences`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`GET /api/notifications/preferences ${res.status}`);
  const body = (await res.json()) as { preferences: NotificationPreferenceRow[] };
  return body.preferences;
}

export interface UpsertPreferenceArgs {
  brandId: string | null;
  kind: string;
  channel: NotificationChannel;
  enabled: boolean;
  quietHoursStart?: string | null;
  quietHoursEnd?: string | null;
  timezone?: string | null;
}

export async function upsertNotificationPreference(args: UpsertPreferenceArgs): Promise<void> {
  const res = await fetch(`${env.API_URL}/api/notifications/preferences`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`PUT /api/notifications/preferences ${res.status} ${detail}`);
  }
}

export interface RemovePreferenceArgs {
  brandId: string | null;
  kind: string;
  channel: NotificationChannel;
}

export async function removeNotificationPreference(args: RemovePreferenceArgs): Promise<void> {
  const res = await fetch(`${env.API_URL}/api/notifications/preferences`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
  });
  if (!res.ok) throw new Error(`DELETE /api/notifications/preferences ${res.status}`);
}

// Slack install status (s178.7h) — what the prefs UI reads to render
// "✦ connected to <workspace>" vs the install button.

export type SlackInstallStatus =
  | { connected: false }
  | {
      connected: true;
      teamId: string;
      teamName: string;
      installedAt: string;
      /** Null = mag DMs each recipient, which is the default. */
      targetChannelId: string | null;
      targetChannelName: string | null;
      /** Which catalog categories route to the channel. Empty means a
       *  channel is chosen but nothing goes there yet. */
      channelCategories: string[];
    };

export async function fetchSlackInstallStatus(brandId: string): Promise<SlackInstallStatus> {
  const res = await fetch(`${env.API_URL}/api/slack/install?brandId=${brandId}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`GET /api/slack/install ${res.status}`);
  return (await res.json()) as SlackInstallStatus;
}

// ───── Slack channel target (spec 315365b1) ───────────────────────────

export interface SlackChannelOption {
  id: string;
  name: string;
  /** Whether mag is already a member. Surfaced BEFORE the founder picks,
   *  so "invite @mag first" is a prompt rather than a silent skip on
   *  every send afterwards. Only meaningful together with isPrivate —
   *  see needsInvite() in slack-channel-picker.tsx. */
  isMember: boolean;
  /** Private channels are the ONLY ones where membership is required:
   *  chat:write.public covers public channels mag has never joined. */
  isPrivate: boolean;
}

export interface SlackChannelList {
  connected: boolean;
  /** True when the install predates channels:read — it can still DM, but
   *  cannot list channels until the founder re-runs the connect. */
  needsReconnect?: boolean;
  channels: SlackChannelOption[];
}

export async function fetchSlackChannels(brandId: string): Promise<SlackChannelList> {
  const res = await fetch(`${env.API_URL}/api/slack/channels?brandId=${brandId}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`GET /api/slack/channels ${res.status}`);
  return (await res.json()) as SlackChannelList;
}

export type SlackRoutedCategory = 'cadence' | 'alert' | 'ask' | 'win' | 'system';

/** channelId null clears the target and returns the brand to DMs. */
export async function setSlackChannelTarget(args: {
  brandId: string;
  channelId: string | null;
  channelName: string | null;
  categories: SlackRoutedCategory[];
}): Promise<void> {
  const res = await fetch(`${env.API_URL}/api/slack/channel-target`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
  });
  if (!res.ok) throw new Error(`PATCH /api/slack/channel-target ${res.status}`);
}

export async function disconnectSlackInstall(brandId: string): Promise<void> {
  const res = await fetch(`${env.API_URL}/api/slack/install`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ brandId }),
  });
  if (!res.ok) throw new Error(`DELETE /api/slack/install ${res.status}`);
}

/** Stream event shape (subset of NotificationItem) emitted by
 *  `/api/notifications/stream`. The server omits status/readAt/archivedAt
 *  on push since they're always defaults at emit time. */
export interface NotificationStreamEvent {
  id: string;
  brandId: string | null;
  kind: string;
  title: string;
  body: string;
  cta: { label: string; href: string } | null;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  payload: unknown;
  feedCardId: string | null;
  /** The brand's mark, for the toast's brand badge. Null when the
   *  notification is not brand-scoped, or when the brand has no favicon yet —
   *  the badge then falls back to a plum dot, the same fallback BrandChip
   *  uses. */
  brandFaviconUrl?: string | null;
  /** The brand this is about, for the toast's brand badge. Null when the
   *  notification is org-scoped. Unlike slack and telegram, the in-app title
   *  is NOT brand-prefixed — the badge carries both mark and name, so a
   *  prefix would say the brand twice and eat the headline's width. */
  brandName?: string | null;
  createdAt: string;
}

// ─── Billing (s120) ────────────────────────────────────────────────────

export type SubscriptionStatus =
  | 'none'
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'incomplete'
  | 'incomplete_expired'
  | 'paused';

export interface BillingPlanDisplay {
  key: PlanKey;
  label: string;
  basePriceUsd: number;
  maxBrands: number;
  /** Monthly analytics events included on this plan. Read into the
   *  plan-card feature bullets + the current-plan recap. Cap not yet
   *  enforced (placeholder per the billing analytics events doc (CommitMind)). */
  monthlyAnalyticsEvents: number;
  /** [s487] Mag tokens per cycle — the billing unit. Read by the plan
   *  cards so the number cannot drift from PLANS. */
  includedTokensPerCycle: number;
  maxSeats: number;
  platforms: readonly ('google' | 'meta' | 'tiktok')[];
}

export interface BillingTopupPackDisplay {
  key: 'small' | 'medium' | 'large';
  tokens: number;
  priceUsd: number;
  label: string;
}

export interface BillingState {
  plan: PlanKey;
  subscriptionStatus: SubscriptionStatus;
  /** ISO timestamp of the next renewal / period end; null when no
   *  active subscription. */
  currentPeriodEnd: string | null;
  /** True once the founder has gone through Checkout at least once. */
  hasStripeCustomer: boolean;
  /** False in dev when Stripe env vars aren't set — the billing UI
   *  shows a setup-hint state instead of action buttons. */
  stripeConfigured: boolean;
  /** All publicly-purchasable plans, in display order. */
  plans: BillingPlanDisplay[];
  /** [s264] Mag-tokens billing unit. balance = current org balance
   *  (denormalized from the ledger for fast read); packs = static
   *  top-up offerings; autobuy = the founder's auto-buy config (off
   *  by default). See the mag-tokens doc (CommitMind). */
  magTokens: {
    balance: number;
    /** [task 6b9cf44] The balance at which the server sends the low-balance
     *  email. The chip goes amber at the same point by reading this rather
     *  than holding its own copy — the two used to agree by coincidence, and
     *  when the token unit moved only one of them was updated.
     *
     *  OPTIONAL because the dashboard and the api deploy independently: a
     *  payload without it means an api older than this change. */
    lowBalanceThresholdTokens?: number;
    packs: BillingTopupPackDisplay[];
    autobuy: {
      enabled: boolean;
      thresholdTokens: number | null;
      packTokens: number | null;
    };
    /** [s264] Cycle-to-date consumption. Powers the chip burn-down
     *  hint ("~12 days at this rate"). startsAt = Stripe period
     *  start when known, calendar-month UTC fallback otherwise. */
    cycle: {
      startsAt: string;
      consumedTokens: number;
    };
  };
}

export async function fetchBillingState(): Promise<BillingState> {
  const res = await fetch(`${env.API_URL}/api/billing/me`, { credentials: 'include' });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/billing/me ${res.status} ${detail}`);
  }
  return (await res.json()) as BillingState;
}

/** Start a Stripe Checkout session for the given plan. Returns the
 *  Stripe-hosted URL the dashboard should redirect to.
 *
 *  `returnTo` overrides where Stripe sends the founder after
 *  success/cancel — must be a leading-slash path the api will prepend
 *  with DASHBOARD_URL. Useful when starting Checkout from an
 *  onboarding flow (returnTo='/brand/<slug>/home', so the founder lands
 *  in their just-onboarded brand) vs the default upgrade flow from
 *  /settings/billing. */
export async function createCheckoutSessionApi(
  plan: PlanKey,
  options: { returnTo?: string } = {},
): Promise<{ url: string }> {
  const body: { plan: PlanKey; returnTo?: string } = { plan };
  if (options.returnTo) body.returnTo = options.returnTo;
  const res = await fetch(`${env.API_URL}/api/billing/checkout`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const responseBody = (await res.json().catch(() => ({}))) as {
      error?: string;
      detail?: string;
    };
    throw new Error(
      responseBody.detail ?? responseBody.error ?? `POST /api/billing/checkout ${res.status}`,
    );
  }
  return (await res.json()) as { url: string };
}

/** [s264] Start a one-time mag-tokens top-up Checkout. Same shape as
 *  createCheckoutSessionApi: the api resolves the active price by
 *  lookup_key, opens a Stripe `mode: 'payment'` Checkout, and the
 *  webhook grants tokens on success. */
export async function createTopupCheckoutSessionApi(
  pack: 'small' | 'medium' | 'large',
  options: { returnTo?: string } = {},
): Promise<{ url: string }> {
  const body: { pack: string; returnTo?: string } = { pack };
  if (options.returnTo) body.returnTo = options.returnTo;
  const res = await fetch(`${env.API_URL}/api/billing/topup`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const responseBody = (await res.json().catch(() => ({}))) as {
      error?: string;
      detail?: string;
    };
    throw new Error(
      responseBody.detail ?? responseBody.error ?? `POST /api/billing/topup ${res.status}`,
    );
  }
  return (await res.json()) as { url: string };
}

/** [s264] One row in the mag-token ledger. The api projects the
 *  append-only mag_token_transactions table; rows are returned most-
 *  recent-first, capped at 30. */
export interface MagTokenTransactionRow {
  id: string;
  /** Positive = grant (subscription/topup/autobuy/admin); negative
   *  = consumption (llm_decrement). */
  deltaTokens: number;
  kind:
    | 'subscription_grant'
    | 'topup_purchase'
    | 'llm_decrement'
    | 'autobuy'
    | 'admin_adjust'
    | 'refund';
  /** Balance AFTER this row landed — the meter's atomic UPDATE …
   *  RETURNING captures this so the UI can show a running total
   *  without summing the full history client-side. */
  balanceAfterTokens: number;
  /** Freeform context — agent_name for decrements, pack key for
   *  grants, etc. Shape varies by kind. */
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export async function fetchMagTokenTransactions(): Promise<MagTokenTransactionRow[]> {
  const res = await fetch(`${env.API_URL}/api/billing/mag-token-transactions`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/billing/mag-token-transactions ${res.status} ${detail}`);
  }
  const body = (await res.json()) as { transactions: MagTokenTransactionRow[] };
  return body.transactions;
}

/** [s264] Update auto-buy config. Pass enabled=false to disable;
 *  enabled=true requires thresholdTokens + packTokens. */
export async function updateAutobuyConfigApi(input: {
  enabled: boolean;
  thresholdTokens?: number;
  packTokens?: 1000 | 5000 | 10000;
}): Promise<{ ok: true }> {
  const body: {
    enabled: boolean;
    thresholdTokens?: number;
    packTokens?: number;
  } = { enabled: input.enabled };
  if (input.thresholdTokens != null) body.thresholdTokens = input.thresholdTokens;
  if (input.packTokens != null) body.packTokens = input.packTokens;
  const res = await fetch(`${env.API_URL}/api/billing/autobuy`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const responseBody = (await res.json().catch(() => ({}))) as {
      error?: string;
      detail?: string;
    };
    throw new Error(
      responseBody.detail ?? responseBody.error ?? `PATCH /api/billing/autobuy ${res.status}`,
    );
  }
  return (await res.json()) as { ok: true };
}

/** Open the Stripe-hosted Billing Portal. Returns the URL to redirect
 *  the founder to. */
export async function createPortalSessionApi(): Promise<{ url: string }> {
  const res = await fetch(`${env.API_URL}/api/billing/portal`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      detail?: string;
    };
    throw new Error(body.detail ?? body.error ?? `POST /api/billing/portal ${res.status}`);
  }
  return (await res.json()) as { url: string };
}

/** Open the Stripe-hosted Billing Portal scoped directly to the
 *  payment-method-update flow. One click for the customer; no portal
 *  landing page in between. */
export async function createPaymentMethodPortalApi(): Promise<{ url: string }> {
  const res = await fetch(`${env.API_URL}/api/billing/portal-flow/payment-method`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      detail?: string;
    };
    throw new Error(
      body.detail ?? body.error ?? `POST /api/billing/portal-flow/payment-method ${res.status}`,
    );
  }
  return (await res.json()) as { url: string };
}

// ─── Customer details (address, tax ids, default payment method) ──────

export interface BillingAddress {
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
}

export interface BillingTaxId {
  id: string;
  type: string;
  value: string;
  verification: { status: string | null } | null;
}

export interface BillingPaymentMethod {
  id: string;
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  funding: string | null;
}

export interface BillingCustomerDetails {
  email: string | null;
  name: string | null;
  address: BillingAddress;
  taxIds: BillingTaxId[];
  defaultPaymentMethod: BillingPaymentMethod | null;
}

export async function fetchBillingCustomer(): Promise<BillingCustomerDetails | null> {
  const res = await fetch(`${env.API_URL}/api/billing/customer`, { credentials: 'include' });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/billing/customer ${res.status} ${detail}`);
  }
  const json = (await res.json()) as { customer: BillingCustomerDetails | null };
  return json.customer;
}

/** PATCH the billing address. Each field accepts `null` to explicitly
 *  clear; omit a key entirely to leave it unchanged. */
export async function updateBillingAddressApi(
  patch: Partial<BillingAddress>,
): Promise<BillingAddress> {
  const res = await fetch(`${env.API_URL}/api/billing/address`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      detail?: string;
    };
    throw new Error(body.detail ?? body.error ?? `PATCH /api/billing/address ${res.status}`);
  }
  const json = (await res.json()) as { address: BillingAddress };
  return json.address;
}

export async function addBillingTaxIdApi(input: {
  type: string;
  value: string;
}): Promise<BillingTaxId> {
  const res = await fetch(`${env.API_URL}/api/billing/tax-ids`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      detail?: string;
    };
    throw new Error(body.detail ?? body.error ?? `POST /api/billing/tax-ids ${res.status}`);
  }
  const json = (await res.json()) as { taxId: BillingTaxId };
  return json.taxId;
}

export async function removeBillingTaxIdApi(id: string): Promise<void> {
  const res = await fetch(`${env.API_URL}/api/billing/tax-ids/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      detail?: string;
    };
    throw new Error(body.detail ?? body.error ?? `DELETE /api/billing/tax-ids/${id} ${res.status}`);
  }
}

// ─── Invoices ──────────────────────────────────────────────────────────

export interface BillingInvoice {
  id: string;
  number: string | null;
  status: string | null;
  /** Smallest currency unit (cents for USD). */
  total: number;
  currency: string;
  /** Unix seconds. */
  created: number;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
}

export async function fetchBillingInvoices(): Promise<BillingInvoice[]> {
  const res = await fetch(`${env.API_URL}/api/billing/invoices`, { credentials: 'include' });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/billing/invoices ${res.status} ${detail}`);
  }
  const json = (await res.json()) as { invoices: BillingInvoice[] };
  return json.invoices;
}

export interface BillingUpcomingInvoice {
  total: number;
  currency: string;
  periodStart: number;
  periodEnd: number;
  lineItems: Array<{
    description: string | null;
    amount: number;
  }>;
}

export async function fetchBillingUpcoming(): Promise<BillingUpcomingInvoice | null> {
  const res = await fetch(`${env.API_URL}/api/billing/upcoming`, { credentials: 'include' });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/billing/upcoming ${res.status} ${detail}`);
  }
  const json = (await res.json()) as { upcoming: BillingUpcomingInvoice | null };
  return json.upcoming;
}

// ─── Analytics-event usage (s210 cap) ─────────────────────────────────

export interface AnalyticsUsage {
  /** Events written to ClickHouse this cycle. */
  eventsUsed: number;
  /** Events refused over-cap this cycle. */
  eventsDropped: number;
  /** PLANS[plan].monthlyAnalyticsEvents — what the cap is for this org. */
  cap: number;
  /** YYYY-MM-DD, inclusive. */
  cycleStart: string;
  /** YYYY-MM-DD, exclusive (next cycle starts here). */
  cycleEnd: string;
  /** ISO timestamp of the most recent accepted event. Null when no
   *  events ingested yet this cycle. */
  lastEventAt: string | null;
  /** ISO timestamp of the FIRST drop this cycle (banner trigger).
   *  Null when the cap hasn't been hit. */
  lastDroppedAt: string | null;
}

export async function fetchAnalyticsUsage(): Promise<AnalyticsUsage> {
  const res = await fetch(`${env.API_URL}/api/billing/analytics-usage`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/billing/analytics-usage ${res.status} ${detail}`);
  }
  return (await res.json()) as AnalyticsUsage;
}

// ─── Telegram channel (AIaS-2) ────────────────────────────────────

export interface TelegramBotIdentity {
  username: string;
  firstName: string;
}

export interface TelegramChannelStatus {
  connected: boolean;
  /** When the founder first tapped the start link. Null when not
   *  connected. */
  linkedAt?: string | null;
  /** Most recent inbound message from this founder on this brand.
   *  Null when connected but the founder hasn't DM'd mag yet. */
  lastInboundAt?: string | null;
}

/** Bot identity. 503 if TELEGRAM_BOT_TOKEN isn't configured on the api
 *  service — the UI degrades to "Telegram not configured yet". */
export async function fetchTelegramBot(): Promise<TelegramBotIdentity | null> {
  const res = await fetch(`${env.API_URL}/api/telegram/me`, {
    credentials: 'include',
  });
  if (res.status === 503) return null;
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/telegram/me ${res.status} ${detail}`);
  }
  return (await res.json()) as TelegramBotIdentity;
}

/** Per-(user × brand) connection status. UI uses this to decide
 *  between the connect button and the linked-status block. */
export async function fetchTelegramChannelStatus(brandId: string): Promise<TelegramChannelStatus> {
  const res = await fetch(
    `${env.API_URL}/api/telegram/channels?brandId=${encodeURIComponent(brandId)}`,
    { credentials: 'include' },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/telegram/channels ${res.status} ${detail}`);
  }
  return (await res.json()) as TelegramChannelStatus;
}

// ─── Brand inputs (spec §0a.9) ─────────────────────────────────────
//
// Founder→Mag composer on /home: events, asset_drops, pinned_briefs,
// asides. Each input routes downstream; mag confirms receipt.

import type {
  BrandInputDTO,
  BrandInputListResponse,
  BrandMemoDTO,
  ChannelTimelineResponse,
  NewBrandInput,
} from '@magig/schemas';

export type {
  BrandInputDTO,
  BrandMemoDTO,
  ChannelTimelineResponse,
  NewBrandInput,
} from '@magig/schemas';

export async function fetchLatestBrandMemo(brandId: string): Promise<BrandMemoDTO | null> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/memos/latest`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/brands/:id/memos/latest ${res.status} ${detail}`);
  }
  const body = (await res.json()) as { memo: BrandMemoDTO | null };
  return body.memo;
}

export interface BrandMemoListEntry {
  id: string;
  periodStart: string;
  periodKind: 'weekly' | 'daily';
  headline: string;
  generatedAt: string;
}

export async function fetchBrandMemoList(brandId: string): Promise<BrandMemoListEntry[]> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/memos`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/brands/:id/memos ${res.status} ${detail}`);
  }
  const body = (await res.json()) as { memos: BrandMemoListEntry[] };
  return body.memos;
}

export async function fetchBrandMemoById(
  brandId: string,
  memoId: string,
): Promise<BrandMemoDTO | null> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/memos/${memoId}`, {
    credentials: 'include',
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/brands/:id/memos/:memoId ${res.status} ${detail}`);
  }
  const body = (await res.json()) as { memo: BrandMemoDTO };
  return body.memo;
}

// ─── Brand channels (spec §0b) ─────────────────────────────────────

import type {
  BrandChannelDTO,
  BrandChannelKind,
  BrandContentPlanDTO,
  BrandContentSlotDTO,
} from '@magig/schemas';

export type {
  BrandChannelDTO,
  BrandChannelKind,
  BrandContentPlanDTO,
  BrandContentSlotDTO,
} from '@magig/schemas';

export async function fetchBrandChannels(brandId: string): Promise<BrandChannelDTO[]> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/channels`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/brands/:id/channels ${res.status} ${detail}`);
  }
  const body = (await res.json()) as { channels: BrandChannelDTO[] };
  return body.channels;
}

export interface BrandChannelDetail {
  channel: BrandChannelDTO | null;
  plan: BrandContentPlanDTO | null;
  slots: BrandContentSlotDTO[];
  /** [12.2L-173] Latest takeover proposal envelope when one exists.
   *  Null on channels that have never had a proposal requested. */
  takeoverProposal: import('@magig/schemas').ChannelTakeoverProposalEnvelope | null;
}

export async function fetchBrandChannelDetail(
  brandId: string,
  kind: BrandChannelKind,
): Promise<BrandChannelDetail> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/channels/${kind}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/brands/:id/channels/:kind ${res.status} ${detail}`);
  }
  return (await res.json()) as BrandChannelDetail;
}

// ─── Channel intake (chat-based onboarding) ─────────────────────────────────
//
// Four endpoints back the chat-with-mag drawer on /channels/<kind>:
// GET intake (load), POST intake/start, POST intake/respond, POST
// intake/finalize. The view shape is the same on all four so the
// drawer can patch its local cache uniformly.

export interface ChannelIntakeView {
  state: import('@magig/schemas').ChannelIntakeState | null;
  awaitingFounder: boolean;
  awaitingFinalize: boolean;
  completed: boolean;
  /** Present on finalize response only — true when runChannelContentPlan
   *  succeeded inline. False when the orchestrator skipped (channel
   *  kind not supported yet) or threw (caught + logged server-side).
   *  Drives the toast copy on commit. */
  planFired?: boolean;
}

export async function fetchChannelIntake(
  brandId: string,
  kind: BrandChannelKind,
): Promise<ChannelIntakeView> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/channels/${kind}/intake`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/brands/:id/channels/:kind/intake ${res.status} ${detail}`);
  }
  return (await res.json()) as ChannelIntakeView;
}

export async function startChannelIntakeApi(
  brandId: string,
  kind: BrandChannelKind,
): Promise<ChannelIntakeView> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/channels/${kind}/intake/start`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /channels/${kind}/intake/start ${res.status}`;
    throw new Error(msg);
  }
  return (await res.json()) as ChannelIntakeView;
}

export async function respondChannelIntakeApi(input: {
  brandId: string;
  kind: BrandChannelKind;
  reply: string;
}): Promise<ChannelIntakeView> {
  const res = await fetch(
    `${env.API_URL}/api/brands/${input.brandId}/channels/${input.kind}/intake/respond`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reply: input.reply }),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /channels/${input.kind}/intake/respond ${res.status}`;
    throw new Error(msg);
  }
  return (await res.json()) as ChannelIntakeView;
}

export async function finalizeChannelIntakeApi(
  brandId: string,
  kind: BrandChannelKind,
): Promise<ChannelIntakeView> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/channels/${kind}/intake/finalize`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /channels/${kind}/intake/finalize ${res.status}`;
    throw new Error(msg);
  }
  return (await res.json()) as ChannelIntakeView;
}

// ─── Funnel intake (chat-based funnel learning) ─────────────────────────────
//
// Four endpoints back the chat-with-mag funnel-intake drawer, opened from
// the funnel_intake_pending feed card: GET intake (load), POST
// intake/start, POST intake/respond, POST intake/finalize. Keyed on the
// brand alone (one funnel per brand). Same view shape across all four so
// the drawer patches its local cache uniformly.

export interface FunnelIntakeView {
  state: import('@magig/schemas').FunnelIntakeState | null;
  awaitingFounder: boolean;
  awaitingFinalize: boolean;
  completed: boolean;
}

export async function fetchFunnelIntake(brandId: string): Promise<FunnelIntakeView> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/funnel/intake`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/brands/:id/funnel/intake ${res.status} ${detail}`);
  }
  return (await res.json()) as FunnelIntakeView;
}

export async function startFunnelIntakeApi(brandId: string): Promise<FunnelIntakeView> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/funnel/intake/start`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /funnel/intake/start ${res.status}`;
    throw new Error(msg);
  }
  return (await res.json()) as FunnelIntakeView;
}

export async function respondFunnelIntakeApi(input: {
  brandId: string;
  reply: string;
}): Promise<FunnelIntakeView> {
  const res = await fetch(`${env.API_URL}/api/brands/${input.brandId}/funnel/intake/respond`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reply: input.reply }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /funnel/intake/respond ${res.status}`;
    throw new Error(msg);
  }
  return (await res.json()) as FunnelIntakeView;
}

export async function finalizeFunnelIntakeApi(brandId: string): Promise<FunnelIntakeView> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/funnel/intake/finalize`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /funnel/intake/finalize ${res.status}`;
    throw new Error(msg);
  }
  return (await res.json()) as FunnelIntakeView;
}

/** Draft caption + image_brief for one brand_content_slots row.
 *  Founder-triggered per slot from the channel detail page. */
/** Fetch one slot's full state for the approval drawer — the complete
 *  caption, image brief, and rendered media, not the truncated feed-
 *  card preview. */
export async function fetchBrandContentSlot(slotId: string): Promise<BrandContentSlotDTO> {
  const res = await fetch(`${env.API_URL}/api/channels/slots/${slotId}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/channels/slots/:slotId ${res.status} ${detail}`);
  }
  const body = (await res.json()) as { slot: BrandContentSlotDTO };
  return body.slot;
}

export async function draftBrandContentSlot(
  slotId: string,
): Promise<{ slotId: string; costUsd: number; durationMs: number }> {
  const res = await fetch(`${env.API_URL}/api/channels/slots/${slotId}/draft`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/channels/slots/:slotId/draft ${res.status} ${detail}`);
  }
  return (await res.json()) as { slotId: string; costUsd: number; durationMs: number };
}

/** Render the image for one brand_content_slots row. Stamps draft.imageUrl
 *  + draft.creativeAssetId on success. */
export async function generateBrandContentSlotImage(
  slotId: string,
): Promise<{ slotId: string; imageUrl: string; creativeAssetId: string; costUsd: number }> {
  const res = await fetch(`${env.API_URL}/api/channels/slots/${slotId}/image`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/channels/slots/:slotId/image ${res.status} ${detail}`);
  }
  return (await res.json()) as {
    slotId: string;
    imageUrl: string;
    creativeAssetId: string;
    costUsd: number;
  };
}

/** Render the video for one brand_content_slots row (reel / video
 *  format). Stamps draft.videoUrl + draft.videoDurationSeconds +
 *  draft.creativeAssetId on success. */
export async function generateBrandContentSlotVideo(slotId: string): Promise<{
  slotId: string;
  videoUrl: string;
  videoDurationSeconds: number;
  creativeAssetId: string;
  costUsd: number;
}> {
  const res = await fetch(`${env.API_URL}/api/channels/slots/${slotId}/video`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/channels/slots/:slotId/video ${res.status} ${detail}`);
  }
  return (await res.json()) as {
    slotId: string;
    videoUrl: string;
    videoDurationSeconds: number;
    creativeAssetId: string;
    costUsd: number;
  };
}

/** Manual publish trigger for one brand_content_slots row. Stamps
 *  publishedAt + publishedPostId + publishedVia. */
export async function publishBrandContentSlot(slotId: string): Promise<
  | {
      outcome: 'published';
      slotId: string;
      publishedPostId: string;
      via: 'mock' | 'meta';
      publishedAt: string;
    }
  | { outcome: 'already_published'; postId: string }
> {
  const res = await fetch(`${env.API_URL}/api/channels/slots/${slotId}/publish`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/channels/slots/:slotId/publish ${res.status} ${detail}`);
  }
  return (await res.json()) as
    | {
        outcome: 'published';
        slotId: string;
        publishedPostId: string;
        via: 'mock' | 'meta';
        publishedAt: string;
      }
    | { outcome: 'already_published'; postId: string };
}

/** 48/N — resolve the founder's call on a slot_approval_pending card.
 *  Each action transitions the slot's approval_status server-side
 *  and dismisses the matching open feed card. */
export async function approveBrandContentSlot(slotId: string): Promise<void> {
  const res = await fetch(`${env.API_URL}/api/channels/slots/${slotId}/approve`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/channels/slots/:slotId/approve ${res.status} ${detail}`);
  }
}

export async function rejectBrandContentSlot(slotId: string): Promise<void> {
  const res = await fetch(`${env.API_URL}/api/channels/slots/${slotId}/reject`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/channels/slots/:slotId/reject ${res.status} ${detail}`);
  }
}

export async function requestChangesBrandContentSlot(slotId: string, note: string): Promise<void> {
  const res = await fetch(`${env.API_URL}/api/channels/slots/${slotId}/request-changes`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ note }),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/channels/slots/:slotId/request-changes ${res.status} ${detail}`);
  }
}

export interface SlotChatHistoryTurn {
  role: 'founder' | 'mag';
  body: string;
}

export type SlotChatResponse =
  | {
      outcome: 'discussed';
      slotId: string;
      reply: string;
    }
  | {
      outcome: 'redrafted';
      slotId: string;
      reply: string;
      caption: string;
      imageBrief: string;
    };

/** s216-54h.67 — one chat turn against a slot. The server's chat
 *  agent reads the full history and decides per turn whether to
 *  discuss (just reply) or redraft (reply + commit a new caption).
 *  Caller passes prior turns so the agent has full context. */
export async function redraftSlotChat(
  slotId: string,
  message: string,
  history: SlotChatHistoryTurn[],
): Promise<SlotChatResponse> {
  const res = await fetch(`${env.API_URL}/api/channels/slots/${slotId}/redraft-chat`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ message, history }),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/channels/slots/:slotId/redraft-chat ${res.status} ${detail}`);
  }
  return (await res.json()) as SlotChatResponse;
}

/** Drop a single unpublished slot. Published slots return 409. */
export async function deleteBrandContentSlot(slotId: string): Promise<void> {
  const res = await fetch(`${env.API_URL}/api/channels/slots/${slotId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`DELETE /api/channels/slots/:slotId ${res.status} ${detail}`);
  }
}

export async function pauseBrandChannel(
  brandId: string,
  kind: BrandChannelKind,
  days: number,
): Promise<BrandChannelDTO> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/channels/${kind}/pause`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ days }),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/brands/:id/channels/:kind/pause ${res.status} ${detail}`);
  }
  const body = (await res.json()) as { channel: BrandChannelDTO };
  return body.channel;
}

export async function unpauseBrandChannel(
  brandId: string,
  kind: BrandChannelKind,
): Promise<BrandChannelDTO> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/channels/${kind}/unpause`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/brands/:id/channels/:kind/unpause ${res.status} ${detail}`);
  }
  const body = (await res.json()) as { channel: BrandChannelDTO };
  return body.channel;
}

export async function setBrandChannelOperatedByMag(
  brandId: string,
  kind: BrandChannelKind,
  on: boolean,
): Promise<BrandChannelDTO> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/channels/${kind}/operated-by-mag`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ on }),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/brands/:id/channels/:kind/operated-by-mag ${res.status} ${detail}`);
  }
  const body = (await res.json()) as { channel: BrandChannelDTO };
  return body.channel;
}

/** [12.2L-173] Explicit channel hand-over. Flips operatedByMag=true
 *  AND fires the channel-onboarding orchestrator (history-learn +
 *  content-plan). Use this when the founder says "yes, mag, run this
 *  channel" — distinct from setBrandChannelOperatedByMag(true) which
 *  is just the flag flip without running the work. */
export async function handOverBrandChannel(
  brandId: string,
  kind: BrandChannelKind,
): Promise<BrandChannelDTO> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/channels/${kind}/hand-over`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/brands/:id/channels/:kind/hand-over ${res.status} ${detail}`);
  }
  const body = (await res.json()) as { channel: BrandChannelDTO };
  return body.channel;
}

/** 49/N + 50/N — patch per-channel autopilot, approval lead-time,
 *  creative-mode default, and designer email. All fields optional;
 *  the api requires at least one. */
export async function patchBrandChannelSettings(
  brandId: string,
  kind: BrandChannelKind,
  patch: {
    autopilot?: boolean;
    approvalLeadTimeHours?: number;
    creativeMode?: 'mag' | 'designer';
    designerEmail?: string | null;
    designerUserId?: string | null;
  },
): Promise<{
  autopilot: boolean;
  approvalLeadTimeHours: number;
  creativeMode: 'mag' | 'designer';
  designerEmail: string | null;
  designerUserId: string | null;
}> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/channels/${kind}/settings`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`PATCH /api/brands/:id/channels/:kind/settings ${res.status} ${detail}`);
  }
  return (await res.json()) as {
    autopilot: boolean;
    approvalLeadTimeHours: number;
    creativeMode: 'mag' | 'designer';
    designerEmail: string | null;
    designerUserId: string | null;
  };
}

// ─── Designer self (session-authed slot list + upload) ─────────────────────

export interface DesignerAssignedSlot {
  slotId: string;
  brandId: string;
  brandSlug: string;
  brandName: string;
  channelKind: string;
  format: string;
  pillar: string;
  concept: string;
  imageBrief: string;
  captionPreview: string;
  scheduledForLabel: string;
  scheduledForISO: string;
}

export async function fetchDesignerAssignedSlots(): Promise<DesignerAssignedSlot[]> {
  const res = await fetch(`${env.API_URL}/api/designer/me/slots`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/designer/me/slots ${res.status} ${detail}`);
  }
  const body = (await res.json()) as { slots: DesignerAssignedSlot[] };
  return body.slots;
}

export async function uploadDesignerAssetForSlot(input: {
  slotId: string;
  file: File;
}): Promise<{ slotId: string; mediaKind: 'image' | 'video'; mediaUrl: string }> {
  const fd = new FormData();
  fd.append('file', input.file);
  const res = await fetch(`${env.API_URL}/api/designer/me/slots/${input.slotId}/upload`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `POST /api/designer/me/slots/${input.slotId}/upload ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as {
    slotId: string;
    mediaKind: 'image' | 'video';
    mediaUrl: string;
  };
}

// ─── Designer picker + invite ───────────────────────────────────────────────

export interface DesignerOption {
  userId: string;
  email: string;
  name: string | null;
}

export async function fetchBrandDesigners(brandId: string): Promise<DesignerOption[]> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/designers`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/brands/:id/designers ${res.status} ${detail}`);
  }
  const body = (await res.json()) as { designers: DesignerOption[] };
  return body.designers;
}

/** 50/N — flip a single slot's creative-mode override. null clears it
 *  so the channel default applies again. */
export async function patchBrandContentSlotSettings(
  slotId: string,
  patch: { creativeModeOverride: 'mag' | 'designer' | null },
): Promise<{ creativeModeOverride: 'mag' | 'designer' | null }> {
  const res = await fetch(`${env.API_URL}/api/channels/slots/${slotId}/settings`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`PATCH /api/channels/slots/:slotId/settings ${res.status} ${detail}`);
  }
  return (await res.json()) as { creativeModeOverride: 'mag' | 'designer' | null };
}

/** Wipe unpublished slots for the channel + re-run the onboarding
 *  orchestrator. Published history is preserved; the new plan agent
 *  drafts a fresh cadence + pillars, and the auto-prep flow drafts
 *  + renders every slot before the founder sees them. */
export async function rebuildBrandChannelPlan(
  brandId: string,
  kind: BrandChannelKind,
): Promise<{
  brandChannelId: string;
  deletedSlots: number;
  contentPlanId: string | null;
  slotsCreated: number;
  durationMs: number;
}> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/channels/${kind}/plan/rebuild`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/brands/:id/channels/:kind/plan/rebuild ${res.status} ${detail}`);
  }
  return (await res.json()) as {
    brandChannelId: string;
    deletedSlots: number;
    contentPlanId: string | null;
    slotsCreated: number;
    durationMs: number;
  };
}

/** [12.2L-173] Generate a fresh takeover proposal. Runs the
 *  channel-proposal agent server-side; the response carries the
 *  envelope so the dashboard can render it without a follow-up GET. */
export async function proposeChannelTakeover(
  brandId: string,
  kind: BrandChannelKind,
): Promise<import('@magig/schemas').ChannelTakeoverProposalEnvelope> {
  const res = await fetch(
    `${env.API_URL}/api/brands/${brandId}/channels/${kind}/propose-takeover`,
    { method: 'POST', credentials: 'include' },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/brands/:id/channels/:kind/propose-takeover ${res.status} ${detail}`);
  }
  const body = (await res.json()) as {
    envelope: import('@magig/schemas').ChannelTakeoverProposalEnvelope;
  };
  return body.envelope;
}

/** Approve the stored proposal. Same downstream effects as
 *  handOverBrandChannel (operatedByMag=true + orchestrator); this
 *  is the proposal-aware path the founder takes after reading
 *  what Mag's about to do. */
export async function approveChannelTakeoverProposal(
  brandId: string,
  kind: BrandChannelKind,
): Promise<{
  envelope: import('@magig/schemas').ChannelTakeoverProposalEnvelope;
  channel: BrandChannelDTO;
}> {
  const res = await fetch(
    `${env.API_URL}/api/brands/${brandId}/channels/${kind}/proposal/approve`,
    { method: 'POST', credentials: 'include' },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/brands/:id/channels/:kind/proposal/approve ${res.status} ${detail}`);
  }
  return (await res.json()) as {
    envelope: import('@magig/schemas').ChannelTakeoverProposalEnvelope;
    channel: BrandChannelDTO;
  };
}

/** Discard the stored proposal. operatedByMag stays unchanged;
 *  founder can request a new proposal anytime. */
export async function discardChannelTakeoverProposal(
  brandId: string,
  kind: BrandChannelKind,
): Promise<import('@magig/schemas').ChannelTakeoverProposalEnvelope> {
  const res = await fetch(
    `${env.API_URL}/api/brands/${brandId}/channels/${kind}/proposal/discard`,
    { method: 'POST', credentials: 'include' },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/brands/:id/channels/:kind/proposal/discard ${res.status} ${detail}`);
  }
  const body = (await res.json()) as {
    envelope: import('@magig/schemas').ChannelTakeoverProposalEnvelope;
  };
  return body.envelope;
}

export async function flagMemoReceiptMistake(
  brandId: string,
  memoId: string,
  receiptIdx: number,
): Promise<{ decisionType: string; newLevel: string }> {
  const res = await fetch(
    `${env.API_URL}/api/brands/${brandId}/memos/${memoId}/receipts/${receiptIdx}/flag-mistake`,
    { method: 'POST', credentials: 'include' },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST flag-mistake ${res.status} ${detail}`);
  }
  const body = (await res.json()) as {
    ok: boolean;
    decisionType: string;
    newLevel: string;
  };
  return { decisionType: body.decisionType, newLevel: body.newLevel };
}

export async function createBrandInput(
  brandId: string,
  input: NewBrandInput,
): Promise<BrandInputDTO> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/inputs`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/brands/:id/inputs ${res.status} ${detail}`);
  }
  const body = (await res.json()) as { input: BrandInputDTO };
  return body.input;
}

export async function listBrandInputs(
  brandId: string,
  opts: { status?: string; kind?: string; limit?: number } = {},
): Promise<BrandInputDTO[]> {
  const params = new URLSearchParams();
  if (opts.status) params.set('status', opts.status);
  if (opts.kind) params.set('kind', opts.kind);
  if (opts.limit) params.set('limit', String(opts.limit));
  const url = `${env.API_URL}/api/brands/${brandId}/inputs${params.size > 0 ? `?${params}` : ''}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/brands/:id/inputs ${res.status} ${detail}`);
  }
  const body = (await res.json()) as BrandInputListResponse;
  return body.inputs;
}

export async function fetchChannelTimeline(
  brandId: string,
  opts: { lookbackDays?: number; lookaheadDays?: number } = {},
): Promise<ChannelTimelineResponse> {
  const params = new URLSearchParams();
  if (opts.lookbackDays) params.set('lookbackDays', String(opts.lookbackDays));
  if (opts.lookaheadDays) params.set('lookaheadDays', String(opts.lookaheadDays));
  const url = `${env.API_URL}/api/brands/${brandId}/channel-timeline${
    params.size > 0 ? `?${params}` : ''
  }`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`GET /api/brands/:id/channel-timeline ${res.status} ${detail}`);
  }
  return (await res.json()) as ChannelTimelineResponse;
}

/** Mints a one-shot start-token (30 min TTL) for the signed-in user
 *  × the given brand. The dashboard composes the t.me deep-link
 *  from this + the bot username. */
export async function mintTelegramStartToken(brandId: string): Promise<{ token: string }> {
  const res = await fetch(`${env.API_URL}/api/telegram/link-tokens`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ brandId }),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`POST /api/telegram/link-tokens ${res.status} ${detail}`);
  }
  return (await res.json()) as { token: string };
}
